import { useState, useEffect, useRef, Fragment } from "react";

const API = "https://totsufe.biz/safety/api";

// ログインの有効期限切れ・無効（401）のときは、エラーを出さずにログイン画面へ戻す
(function () {
  if (window.__tfeFetchPatched) return;
  window.__tfeFetchPatched = true;
  const orig = window.fetch;
  window.fetch = function (...args) {
    return orig.apply(this, args).then((res) => {
      try {
        const url = String((args[0] && args[0].url) || args[0]);
        if (res.status === 401 && url.indexOf(API) === 0 && url.indexOf('/login.php') < 0 && sessionStorage.getItem('tfe_token')) {
          sessionStorage.removeItem('tfe_token');
          sessionStorage.removeItem('tfe_user');
          window.location.reload();
        }
      } catch (e) {}
      return res;
    });
  };
})();

// ================================
// レスポンシブ判定フック
// ================================
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ================================
// カラー定義
// ================================
const C = {
  navy:    '#1a2a4a',
  navyL:   '#2a4a7a',
  blue:    '#185FA5',
  green:   '#1D9E75',
  amber:   '#BA7517',
  red:     '#E24B4A',
  bg:      '#F0F2F5',
  white:   '#ffffff',
  gray1:   '#f8f8f8',
  gray2:   '#eeeeee',
  gray3:   '#999999',
  text:    '#1a1a1a',
  textSub: '#666666',
};

// ================================
// ロール設定
// ================================
const ROLE_CONFIG = {
  safety_admin: {
    label: "安全管理室",
    color: C.blue,
    bg: "#E6F1FB",
    navItems: ["dashboard","report","list","trouble","trouble_list","ky","safety_cycle","accident_report","patrol","education","improvement","incident","schedule","settings"],
  },
  manager: {
    label: "管理職",
    color: C.blue,
    bg: "#E6F1FB",
    navItems: ["dashboard","report","list","trouble","trouble_list","ky","safety_cycle","accident_report","patrol","education","improvement","incident","schedule","settings"],
  },
  employee: {
    label: "社員",
    color: "#2a5a3a",
    bg: "#EAF3DE",
    navItems: ["dashboard","report","list","trouble","trouble_list","ky","safety_cycle","accident_report","education","improvement","incident","schedule","settings"],
  },
  partner: {
    label: "パートナー",
    color: "#5a3a1a",
    bg: "#FAEEDA",
    navItems: ["dashboard","report","list","trouble","trouble_list","ky","accident_report","improvement","incident","schedule","settings"],
  },
};

const NAV_LABELS = {
  dashboard:     { label: "ホーム",         icon: "🏠" },
  report:        { label: "ヒヤリハット報告", icon: "🙈" },
  list:          { label: "ヒヤリハット一覧", icon: "📊" },
  trouble:       { label: "トラブル報告",    icon: "⚠️"  },
  trouble_list:  { label: "トラブル一覧",    icon: "📋"  },
  ky:            { label: "KY報告",         icon: "📷"  },
  education:     { label: "安全教育資料",    icon: "📚"  },
  safety_cycle:    { label: "新規入場者教育報告", icon: "🎓" },
  accident_report: { label: "速報メール",     icon: "🚨" },
  patrol:          { label: "安パト",          icon: "🛡️" },
  improvement:     { label: "改善メモ",        icon: "💡"  },
  incident:        { label: "インセンティブ＆\u200Bペナルティー",        icon: "🏅"  },
  safety_mail:   { label: "安全メール",       icon: "📧"  },
  schedule:      { label: "予定",           icon: "📅"  },
  settings:      { label: "設定",           icon: "⚙️"  },
};

// ================================
// App本体
// ================================
function App() {
  const isMobile = useIsMobile();
  const [token, setToken] = useState(sessionStorage.getItem('tfe_token') || null);
  const [user, setUser] = useState(() => {
    try {
      const s = sessionStorage.getItem('tfe_user');
      return s ? JSON.parse(s) : null;
    } catch {
      sessionStorage.removeItem('tfe_user');
      sessionStorage.removeItem('tfe_token');
      return null;
    }
  });
  const [page, setPage] = useState(() => sessionStorage.getItem('tfe_page') || "dashboard");

  const changePage = (p) => { sessionStorage.setItem('tfe_page', p); setPage(p); };
  const [loginError, setLoginError] = useState("");
  const [permissions, setPermissions] = useState({});
  const [moreOpen, setMoreOpen] = useState(false);
  const [focusTroubleId, setFocusTroubleId] = useState(null); // 賞罰制度から開くトラブル報告のID

  useEffect(() => {
    if (!token || !user) return;
    fetch(`${API}/permissions.php?path=my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => setPermissions(data)).catch(() => {});
  }, [token, user]);

  const handleLogin = async (email, password) => {
    setLoginError("");
    try {
      const res = await fetch(`${API}/login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.error || "ログイン失敗"); return; }
      sessionStorage.setItem('tfe_token', data.token);
      sessionStorage.setItem('tfe_user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      changePage("dashboard");
    } catch { setLoginError("サーバーに接続できません"); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('tfe_token');
    sessionStorage.removeItem('tfe_user');
    setToken(null); setUser(null); setPermissions({}); changePage("dashboard");
  };

  if (!token || !user) return <LoginPage onLogin={handleLogin} error={loginError} />;

  const roleCfg = ROLE_CONFIG[user.role] || ROLE_CONFIG.partner;
  const visibleNav = roleCfg.navItems.filter(id => {
    if (user.role === "safety_admin") return true;
    const permKey = { list:"reports", trouble:"trouble_reports", trouble_list:"trouble_reports", ky:"ky", schedule:"schedule" }[id];
    if (!permKey) return true;
    return permissions[permKey] !== false;
  });

  const renderPage = () => {
    if (!visibleNav.includes(page)) return <PermDenied />;
    switch (page) {
      case "dashboard": return <Dashboard user={user} token={token} isMobile={isMobile} onNavigate={changePage} navItems={visibleNav} />;
      case "report":    return <ReportForm token={token} user={user} />;
      case "list":      return <ReportList token={token} user={user} isMobile={isMobile} />;
      case "trouble":      return <TroublePage token={token} user={user} />;
      case "trouble_list": return <TroubleListPage token={token} user={user} focusId={focusTroubleId} onFocusDone={() => setFocusTroubleId(null)} />;
      case "ky":           return <KYPage token={token} user={user} />;
      case "education":    return <EducationPage token={token} user={user} />;
      case "safety_cycle":    return <NewEntryEducationPage token={token} user={user} />;
      case "accident_report": return <AccidentReportPage token={token} user={user} />;
      case "patrol":          return <PatrolPage token={token} user={user} />;
      case "improvement":  return <ImprovementPage token={token} user={user} />;
      case "incident":     return <IncidentPage token={token} user={user} onOpenTrouble={(id) => { setFocusTroubleId(id); changePage("trouble_list"); }} />;
      case "safety_mail":  return <SafetyMailPage token={token} user={user} />;
      case "schedule":  return <SchedulePage user={user} token={token} />;
      case "settings":  return <Settings token={token} user={user} />;
      default:          return <PermDenied />;
    }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100dvh", background:"linear-gradient(180deg,#e6eefb 0%,#F0F2F5 45%)", fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      <style>{`
        @keyframes tfePageIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
        .tfe-page { animation: tfePageIn .35s ease; }
        button { transition: filter .15s, transform .1s; }
        button:not(:disabled):hover { filter: brightness(1.07); }
        button:not(:disabled):active { transform: scale(.97); }
        .tfe-lift:hover { transform: translateY(-3px); box-shadow: 0 8px 18px rgba(24,95,165,0.18) !important; }
        .tfe-lift { transition: transform .18s, box-shadow .18s; }
      `}</style>

      {/* ヘッダー */}
      <div style={{
        background: "linear-gradient(90deg,#12203d 0%,#1d3a6b 55%,#185FA5 100%)",
        borderBottom: "3px solid transparent",
        borderImage: "linear-gradient(90deg,#F59E0B,#1D9E75,#2f8be6) 1",
        padding: isMobile ? "0 14px" : "0 20px",
        height: isMobile ? "56px" : "62px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        flexShrink:0, boxShadow:"0 2px 8px rgba(0,0,0,0.2)",
        position:"sticky", top:0, zIndex:100,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
          <img src="./t-reon1.png" alt="T-Reon1"
            style={{ height: isMobile ? "46px" : "54px", objectFit:"contain", marginRight:"2px" }}
            onError={e => e.target.style.display="none"} />
          <img src="./TFE3mojiロゴ.png" alt="TFE"
            style={{ height: isMobile ? "26px" : "32px", objectFit:"contain", filter:"brightness(0) invert(1)" }}
            onError={e => e.target.style.display="none"} />
          <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", lineHeight:1.2 }}>
            <span style={{ color:"white", fontSize: isMobile ? "15px" : "20px", fontWeight:"800", letterSpacing:"0.01em" }}>
              Safety<span style={{ fontWeight:"800" }}>Board</span>
            </span>
            {!isMobile && <span style={{ color:"rgba(255,255,255,0.5)", fontSize:"9px", letterSpacing:"0.12em" }}>SAFETY MANAGEMENT SYSTEM</span>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:"rgba(255,255,255,0.9)", fontSize: isMobile?"11px":"12px", fontWeight:"500" }}>{user.name}</div>
            <div style={{
              fontSize:"9px", padding:"1px 6px", borderRadius:"20px",
              background: roleCfg.color + "33", color:"rgba(255,255,255,0.7)",
              border:`0.5px solid ${roleCfg.color}55`, display:"inline-block"
            }}>{roleCfg.label}</div>
          </div>
          <button onClick={handleLogout} style={{
            background:"rgba(255,255,255,0.1)", border:"0.5px solid rgba(255,255,255,0.2)",
            color:"rgba(255,255,255,0.7)", padding: isMobile?"5px 8px":"5px 12px",
            borderRadius:"8px", cursor:"pointer", fontSize:"11px", whiteSpace:"nowrap"
          }}>ログアウト</button>
        </div>
      </div>

      <div style={{ display:"flex", flex:1, overflow:"hidden", position:"relative" }}>

        {/* PC用サイドバー */}
        {!isMobile && (
          <div style={{
            width:"160px", background: C.white,
            borderRight:`1px solid ${C.gray2}`,
            padding:"16px 8px", flexShrink:0,
            display:"flex", flexDirection:"column", gap:"2px",
            overflowY:"auto",
          }}>
            {visibleNav.map(id => {
              const active = page === id;
              return (
                <div key={id} onClick={() => changePage(id)} style={{
                  display:"flex", alignItems:"center", gap:"8px",
                  padding:"9px 12px", borderRadius:"10px", cursor:"pointer",
                  fontSize:"12px", fontWeight: active?"600":"400",
                  background: active ? roleCfg.bg : "transparent",
                  boxShadow: active ? `inset 3px 0 0 ${roleCfg.color}, 0 2px 8px ${roleCfg.color}22` : "none",
                  color: active ? roleCfg.color : C.textSub,
                  transition:"all .15s",
                }}>
                  <span style={{ fontSize:"14px" }}>{NAV_LABELS[id].icon}</span>
                  <span>{NAV_LABELS[id].label.replace("ダッシュボード","ホーム")}</span>
                </div>
              );
            })}
            <div style={{ marginTop:"auto", padding:"10px 12px", borderTop:`1px solid ${C.gray2}` }}>
              <div style={{
                fontSize:"10px", padding:"5px 8px", borderRadius:"8px",
                background: roleCfg.bg, color: roleCfg.color,
                textAlign:"center", fontWeight:"600", letterSpacing:"0.05em"
              }}>{roleCfg.label}</div>
            </div>
          </div>
        )}

        {/* メインコンテンツ */}
        <div style={{
          flex:1, padding: isMobile?"14px 12px":"20px",
          overflowY:"auto",
          paddingBottom: isMobile ? "80px" : "20px",
        }}>
          <div key={page} className="tfe-page">{renderPage()}</div>
        </div>
      </div>

      {/* スマホ用ボトムタブバー */}
      {isMobile && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:200,
          background: C.white, borderTop:`1px solid ${C.gray2}`,
          boxShadow:"0 -2px 12px rgba(0,0,0,0.08)",
          paddingBottom:"env(safe-area-inset-bottom, 0px)",
        }}>
          {/* じゃばらメニュー */}
          {moreOpen && visibleNav.length > 5 && (
            <div style={{
              display:"grid", gridTemplateColumns:"repeat(4, 1fr)",
              borderBottom:`1px solid ${C.gray2}`,
              background: C.gray1,
            }}>
              {visibleNav.slice(5).map(id => {
                const active = page === id;
                return (
                  <div key={id} onClick={() => { changePage(id); setMoreOpen(false); }} style={{
                    display:"flex", flexDirection:"column", alignItems:"center",
                    padding:"10px 0 8px", cursor:"pointer", gap:"3px",
                    color: active ? roleCfg.color : C.gray3,
                    borderBottom: active ? `2px solid ${roleCfg.color}` : "2px solid transparent",
                  }}>
                    <span style={{ fontSize:"20px", lineHeight:1 }}>{NAV_LABELS[id].icon}</span>
                    <span style={{ fontSize:"9px", fontWeight: active?"600":"400", textAlign:"center" }}>{NAV_LABELS[id].label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* ボトムタブ */}
          <div style={{ display:"flex", alignItems:"center" }}>
            {visibleNav.slice(0, 5).map(id => {
              const active = page === id;
              return (
                <div key={id} onClick={() => { changePage(id); setMoreOpen(false); }} style={{
                  flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                  padding:"8px 0 6px", cursor:"pointer", gap:"2px",
                  color: active ? roleCfg.color : C.gray3,
                  borderTop: active ? `2px solid ${roleCfg.color}` : "2px solid transparent",
                  transition:"all .15s",
                }}>
                  <span style={{ fontSize:"20px", lineHeight:1 }}>{NAV_LABELS[id].icon}</span>
                  <span style={{ fontSize:"9px", fontWeight: active?"600":"400" }}>{NAV_LABELS[id].label}</span>
                </div>
              );
            })}
            {visibleNav.length > 5 && (
              <div onClick={() => setMoreOpen(o => !o)} style={{
                flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                padding:"8px 0 6px", cursor:"pointer", gap:"2px",
                color: (moreOpen || visibleNav.slice(5).includes(page)) ? roleCfg.color : C.gray3,
                borderTop: (moreOpen || visibleNav.slice(5).includes(page)) ? `2px solid ${roleCfg.color}` : "2px solid transparent",
                transition:"all .15s",
              }}>
                <span style={{ fontSize:"20px", lineHeight:1 }}>{moreOpen ? "✕" : "☰"}</span>
                <span style={{ fontSize:"9px", fontWeight: moreOpen?"600":"400" }}>もっと</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// ログイン画面
// ================================
function LoginPage({ onLogin, error }) {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    await onLogin(email, password); setLoading(false);
  };

  return (
    <div style={{
      minHeight:"100dvh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background:`linear-gradient(160deg, ${C.navy} 0%, ${C.navyL} 60%, #1D4E89 100%)`,
      padding:"20px",
    }}>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideFromLeft {
          from { opacity: 0; transform: translateX(-32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideFromRight {
          from { opacity: 0; transform: translateX(32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
      `}</style>

      <div style={{ textAlign:"center", marginBottom:"32px" }}>
        {/* T-Reon1 バウンスアニメーション */}
        <img src="./t-reon1.png" alt="T-Reon1"
          style={{ height: isMobile?"80px":"100px", objectFit:"contain", marginBottom:"10px",
            filter:"drop-shadow(0 6px 18px rgba(0,0,0,0.45))",
            animation:"bounce 2.4s ease-in-out infinite",
          }}
          onError={e => e.target.style.display="none"} />

        {/* TFEロゴ → Safety → Board の順で左から流れる */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", marginBottom:"8px" }}>
          <img src="./TFE3mojiロゴ.png" alt="TFE"
            style={{ height: isMobile?"38px":"46px", objectFit:"contain",
              filter:"brightness(0) invert(1)",
              animation:"slideFromLeft 0.6s ease-out 0.2s both",
            }}
            onError={e => e.target.style.display="none"} />
          <span style={{ color:"white", fontSize: isMobile?"38px":"46px", fontWeight:"800", letterSpacing:"0.01em",
            lineHeight:"1",
            animation:"slideFromLeft 0.6s ease-out 0.45s both",
            display:"inline-block",
          }}>
            Safety
          </span>
          <span style={{ color:"white", fontSize: isMobile?"38px":"46px", fontWeight:"800", letterSpacing:"0.01em",
            lineHeight:"1",
            animation:"slideFromRight 0.6s ease-out 0.65s both",
            display:"inline-block",
          }}>
            Board
          </span>
        </div>
        <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"11px", letterSpacing:"0.2em", textTransform:"uppercase",
          animation:"fadeIn 0.8s ease-out 1s both",
        }}>
          Safety Management System
        </div>
      </div>

      <div style={{
        background: C.white, borderRadius:"20px",
        padding: isMobile ? "28px 24px" : "36px 40px",
        width:"100%", maxWidth:"380px",
        boxShadow:"0 24px 60px rgba(0,0,0,0.35)",
      }}>
        <div style={{ fontSize:"20px", fontWeight:"700", marginBottom:"24px", color: C.navy, letterSpacing:"-0.02em" }}>
          ログイン
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:"16px" }}>
            <label style={{ display:"block", fontSize:"11px", color: C.textSub, marginBottom:"6px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.05em" }}>
              メールアドレス
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              required placeholder="example@tfe.co.jp"
              style={{ width:"100%", padding:"12px 14px", fontSize:"14px",
                border:`1.5px solid ${C.gray2}`, borderRadius:"10px",
                outline:"none", boxSizing:"border-box", background: C.gray1,
                transition:"border-color .15s",
              }}
              onFocus={e => e.target.style.borderColor = C.blue}
              onBlur={e => e.target.style.borderColor = C.gray2}
            />
          </div>
          <div style={{ marginBottom:"24px" }}>
            <label style={{ display:"block", fontSize:"11px", color: C.textSub, marginBottom:"6px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.05em" }}>
              パスワード
            </label>
            <div style={{ position:"relative" }}>
              <input type={showPw?"text":"password"} value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="••••••••"
                style={{ width:"100%", padding:"12px 44px 12px 14px", fontSize:"14px",
                  border:`1.5px solid ${C.gray2}`, borderRadius:"10px",
                  outline:"none", boxSizing:"border-box", background: C.gray1,
                  transition:"border-color .15s",
                }}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{
                position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)",
                background:"none", border:"none", cursor:"pointer", color: C.gray3, fontSize:"16px", padding:"4px",
              }}>{showPw?"🙈":"👁"}</button>
            </div>
          </div>

          {error && (
            <div style={{
              background:"#FFF0F0", border:`1px solid #FFCCCC`,
              borderRadius:"10px", padding:"10px 14px",
              fontSize:"12px", color: C.red, marginBottom:"16px",
              display:"flex", alignItems:"center", gap:"6px",
            }}>
              <span>⚠️</span> {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            width:"100%", padding:"13px",
            background: loading ? C.gray3 : C.navy,
            color: C.white, border:"none", borderRadius:"12px",
            fontSize:"14px", fontWeight:"700", cursor: loading?"not-allowed":"pointer",
            transition:"background .2s", letterSpacing:"0.02em",
            boxShadow: loading ? "none" : "0 4px 16px rgba(26,42,74,0.35)",
          }}>
            {loading ? "ログイン中..." : "ログイン"}
          </button>
        </form>
      </div>

      <div style={{ color:"rgba(255,255,255,0.25)", fontSize:"10px", marginTop:"28px", letterSpacing:"0.05em",
        animation:"fadeIn 1s ease-out 1.2s both",
      }}>
        © 2026 TFE SafetyBoard
      </div>
    </div>
  );
}

// ================================
// ダッシュボード
// ================================
// 数字がふわっと増えるアニメーション
function CountUp({ value, duration = 700 }) {
  const target = Number(value) || 0;
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (t) => {
      if (start === undefined) start = t;
      const k = Math.min(1, (t - start) / duration);
      setN(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{n.toLocaleString()}</>;
}

// 写真ポップアップ（複数枚のときは ←→ キー・ボタン・スワイプで前後の写真へ）
function PhotoLightbox({ photo, setPhoto }) {
  const list = Array.isArray(photo?.list) && photo.list.length > 0 ? photo.list : null;
  const idx  = list ? Math.min(photo.idx || 0, list.length - 1) : 0;
  const cur  = list ? list[idx] : photo;
  const many = !!list && list.length > 1;
  const touchX = useRef(null);

  const go = (d) => {
    if (!many) return;
    const n = (idx + d + list.length) % list.length;
    setPhoto({ ...list[n], list, idx: n });
  };
  const close = () => setPhoto(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft")  go(-1);
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const navBtn = (side, label, d) => (
    <button type="button" aria-label={label} onClick={e => { e.stopPropagation(); go(d); }}
      style={{ position:"absolute", top:"50%", [side]:"12px", transform:"translateY(-50%)", width:"44px", height:"44px", borderRadius:"50%",
        border:"1px solid rgba(255,255,255,0.35)", background:"rgba(0,0,0,0.45)", color:"#fff", fontSize:"20px", cursor:"pointer", zIndex:2 }}>
      {d < 0 ? "‹" : "›"}
    </button>
  );

  return (
    <div onClick={close}
      onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1);
      }}
      style={{ position:"fixed", inset:0, zIndex:900, background:"rgba(0,0,0,0.85)",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
      {many && navBtn("left", "前の写真", -1)}
      {many && navBtn("right", "次の写真", 1)}
      <img src={cur.url} alt={cur.name || ""} onClick={e => e.stopPropagation()}
        style={{ maxWidth:"100%", maxHeight:"75dvh", borderRadius:"12px", objectFit:"contain" }} />
      {many && <div style={{ marginTop:"10px", color:"rgba(255,255,255,0.8)", fontSize:"12px", fontWeight:"700" }}>{idx + 1} / {list.length}</div>}
      <div style={{ display:"flex", gap:"12px", marginTop:"14px" }} onClick={e => e.stopPropagation()}>
        <button onClick={() => { const a=document.createElement('a'); a.href=cur.url; a.download=cur.name||'photo'; a.target='_blank'; a.click(); }}
          style={{ padding:"10px 24px", background:C.blue, color:C.white, border:"none", borderRadius:"12px", fontSize:"14px", fontWeight:"700", cursor:"pointer" }}>💾 保存</button>
        <button onClick={close}
          style={{ padding:"10px 24px", background:"rgba(255,255,255,0.15)", color:C.white, border:"1px solid rgba(255,255,255,0.3)", borderRadius:"12px", fontSize:"14px", fontWeight:"700", cursor:"pointer" }}>✕ 閉じる</button>
      </div>
    </div>
  );
}

function Dashboard({ user, token, isMobile, onNavigate, navItems = [] }) {
  const [safe, setSafe] = useState(null); // {label, days, lastAccidentDate}
  const [reports, setReports] = useState([]);
  const [theme, setTheme]           = useState(null);
  const [editingTheme, setEditingTheme] = useState(false);
  const [accidentNews, setAccidentNews] = useState([]);
  const [troubleCount, setTroubleCount] = useState(0);
  const [troubleList, setTroubleList]   = useState([]);
  const [hiddenTroubles, setHiddenTroubles] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`tfe_hidden_trouble_${user.userId}`) || '[]'); } catch { return []; }
  });
  const [kyCount, setKyCount]           = useState(0);
  const [improvePending, setImprovePending] = useState(0);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    const thisMonth = new Date().toISOString().slice(0,7); // "2026-10"

    // ヒヤリハット
    // 安管: 全件 / 社員: 全件 / パートナー: APIが自社分のみ返す
    fetch(`${API}/reports.php`, { headers: h })
      .then(r => r.json()).then(data => Array.isArray(data) && setReports(data)).catch(() => {});

    // 無事故日数（自分の課。パートナーは自社）
    fetch(`${API}/incident.php`, { headers: h })
      .then(r => r.json()).then(data => {
        if (!data || !Array.isArray(data.accidentFree)) return;
        const me = data.me || {};
        const partner = user.role === 'partner';
        const deptFree = Array.isArray(data.deptFree) ? data.deptFree : [];
        const myFree = data.accidentFree.find(a => a.organizationId === me.organizationId);
        const myDept = partner ? null : deptFree.find(d => d.organizationId === me.organizationId && (me.departments || []).includes(d.department));
        const af = myDept || myFree;
        if (!af || af.days === null || af.days === undefined) return;
        setSafe({
          label: partner ? (me.organizationName || '') : ((me.departments || []).join(' / ') || me.organizationName || ''),
          days: af.days, lastAccidentDate: af.lastAccidentDate,
        });
      }).catch(() => {});

    // 安全テーマ
    fetch(`${API}/safety_themes.php?path=current`, { headers: h })
      .then(r => r.json()).then(data => data && setTheme(data)).catch(() => {});

    // 事故速報（ニュース表示用）
    fetch(`${API}/accident_reports.php?type=list&limit=5`, { headers: h })
      .then(r => r.json()).then(data => Array.isArray(data.list) && setAccidentNews(data.list)).catch(() => {});

    // 今月のトラブル件数
    // 安管: 全件 / 社員: 全件 / パートナー: APIが自社分のみ返す
    fetch(`${API}/trouble_reports.php`, { headers: h })
      .then(r => r.json()).then(data => {
        if (!Array.isArray(data)) return;
        setTroubleList(data);
        const count = data.filter(t => (t.occurred_at || t.created_at || '').slice(0,7) === thisMonth).length;
        setTroubleCount(count);
      }).catch(() => {});

    // 今月のKY件数
    // 安管: 全件 / 社員: 自分のみ / パートナー: 自分のみ
    fetch(`${API}/ky_reports.php?type=reports`, { headers: h })
      .then(r => r.json()).then(data => {
        if (!Array.isArray(data)) return;
        const isAdmin = user.role === 'safety_admin' || user.role === 'manager';
        const count = data.filter(r => {
          const dateStr = r.report_date || r.reported_at || r.created_at || '';
          const inMonth = dateStr.slice(0,7) === thisMonth;
          if (!inMonth) return false;
          if (isAdmin) return true; // 全件
          // 自分のみ: uploader_id / user_id / submitted_by いずれかで照合
          const rid = String(r.uploader_id || r.user_id || r.submitted_by || '');
          return rid === String(user.userId);
        }).length;
        setKyCount(count);
      }).catch(() => {});

    // 改善メモ未対応件数
    // 安管: 全件pending / 社員: 自分のみ / パートナー: 自分のみ
    fetch(`${API}/improvement.php`, { headers: h })
      .then(r => r.json()).then(data => {
        if (!Array.isArray(data)) return;
        const isAdmin = user.role === 'safety_admin' || user.role === 'manager';
        const count = data.filter(i => {
          if (i.status !== 'pending') return false;
          if (isAdmin) return true;
          return String(i.submitted_by) === String(user.userId);
        }).length;
        setImprovePending(count);
      }).catch(() => {});
  }, [token]);

  const roleCfg = ROLE_CONFIG[user.role] || ROLE_CONFIG.partner;

  const handleItemCheck = async (itemId, isDone) => {
    if (user.role !== 'safety_admin' && user.role !== 'manager') return;
    await fetch(`${API}/safety_themes.php?itemId=${itemId}`, {
      method:'PATCH', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
      body: JSON.stringify({ isDone }),
    });
    setTheme(prev => ({ ...prev, items: prev.items.map(it => it.id === itemId ? { ...it, isDone } : it) }));
  };

  const isAdmin   = user.role === 'safety_admin' || user.role === 'manager';
  const isPartner = user.role === 'partner' || user.role === 'employee_dispatch';
  const hideTrouble = (id) => {
    const next = [...hiddenTroubles, id];
    setHiddenTroubles(next);
    try { localStorage.setItem(`tfe_hidden_trouble_${user.userId}`, JSON.stringify(next.slice(-200))); } catch {}
  };
  // トラブル速報：パートナー以外／直近7日／自分が非表示にしていないもの
  const troubleNews = isPartner ? [] : troubleList.filter(t => {
    if (hiddenTroubles.includes(t.id)) return false;
    const ts = new Date(String(t.created_at || '').replace(' ', 'T')).getTime();
    return !isNaN(ts) && Date.now() - ts < 7 * 24 * 3600 * 1000;
  }).slice(0, 3);
  const kpiData = [
    {
      label: isPartner ? "自社トラブル(今月)" : "今月のトラブル",
      val:   troubleCount.toString(),
      color: C.red,   icon:"⚠️",
    },
    {
      label: isPartner ? "自社ヒヤリハット" : "ヒヤリハット",
      val:   reports.length.toString(),
      color: C.amber, icon:"🔔",
    },
    {
      label: isAdmin ? "KY(今月・全員)" : "自分のKY(今月)",
      val:   kyCount.toString(),
      color: C.green, icon:"✅",
    },
    {
      label: isAdmin ? "改善メモ未対応(全体)" : "自分の改善メモ未対応",
      val:   improvePending.toString(),
      color: C.navy,  icon:"💡",
    },
  ];

  return (
    <div>

      {/* ロールバナー */}
      <div style={{
        background:"linear-gradient(135deg,#12203d 0%,#185FA5 62%,#1D9E75 100%)",
        borderRadius:"18px", padding:"16px 18px", marginBottom:"14px",
        display:"flex", alignItems:"center", gap:"14px",
        boxShadow:"0 6px 20px rgba(24,95,165,0.3)", position:"relative", overflow:"hidden",
      }}>
        <div style={{ position:"absolute", right:"-30px", top:"-40px", width:"140px", height:"140px", borderRadius:"50%", background:"rgba(255,255,255,0.08)" }} />
        <div style={{ position:"absolute", right:"40px", bottom:"-50px", width:"90px", height:"90px", borderRadius:"50%", background:"rgba(255,255,255,0.06)" }} />
        <span style={{ fontSize:"32px", position:"relative" }}>
          {user.role==='safety_admin' ? '🛡️' : user.role==='employee' ? '👷' : '🤝'}
        </span>
        <div>
          <div style={{ color:C.white, fontWeight:"700", fontSize:"13px", marginBottom:"3px" }}>
            {user.name}さん、{(() => { const h = new Date().getHours(); return h < 12 ? 'おはようございます' : h < 18 ? 'こんにちは' : 'お疲れ様です'; })()}
          </div>
          <div style={{ color:"rgba(255,255,255,0.65)", fontSize:"11px" }}>
            {roleCfg.label} ·  {user.role==='safety_admin' ? '全機能にアクセスできます' : user.role==='employee' ? '全社報告を閲覧できます' : '担当工程のKYを確認できます'}
          </div>
        </div>
      </div>

      {/* トラブル速報（パートナー以外） */}
      {troubleNews.length > 0 && (
        <div style={{ background: C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 4px 14px rgba(226,75,74,0.18)", marginBottom:"14px", border:`1.5px solid ${C.red}55` }}>
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.gray2}`, display:"flex", alignItems:"center", gap:"8px", background:"#FCEBEB" }}>
            <span style={{ fontSize:"14px" }}>⚠️</span>
            <span style={{ fontWeight:"800", fontSize:"13px", color: C.red }}>トラブル速報</span>
          </div>
          {troubleNews.map(t => (
            <div key={t.id} style={{ padding:"10px 12px 10px 16px", borderBottom:`1px solid ${C.gray2}`, display:"flex", alignItems:"center", gap:"10px" }}>
              <div style={{ flex:1, minWidth:0, cursor: onNavigate ? "pointer" : "default" }}
                onClick={() => onNavigate && navItems.includes('trouble_list') && onNavigate('trouble_list')}>
                <div style={{ fontSize:"12px", fontWeight:"700", color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {t.category ? `【${t.category}】` : ''}{t.title}
                </div>
                <div style={{ fontSize:"10px", color:C.textSub, marginTop:"2px" }}>{new Date(String(t.created_at).replace(' ', 'T')).toLocaleString("ja-JP")}</div>
              </div>
              <button type="button" onClick={() => hideTrouble(t.id)} title="非表示にする"
                style={{ border:"none", background:C.gray1, color:C.textSub, borderRadius:"8px", fontSize:"11px", padding:"6px 10px", cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>非表示</button>
            </div>
          ))}
        </div>
      )}

      {/* 無事故日数 */}
      {safe && (
        <div style={{
          background:"linear-gradient(135deg,#065f46 0%,#059669 55%,#34d399 100%)", color:"#fff",
          borderRadius:"18px", padding:"16px 20px", marginBottom:"14px",
          display:"flex", justifyContent:"space-between", alignItems:"center", gap:"12px", flexWrap:"wrap",
          boxShadow:"0 6px 20px rgba(5,150,105,0.3)", position:"relative", overflow:"hidden",
        }}>
          <div style={{ position:"absolute", left:"-20px", bottom:"-40px", width:"120px", height:"120px", borderRadius:"50%", background:"rgba(255,255,255,0.1)" }} />
          <div style={{ position:"relative", minWidth:0 }}>
            <div style={{ fontSize:"11px", opacity:0.85, fontWeight:"700" }}>🛡️ 無事故継続中</div>
            <div style={{ fontSize:"16px", fontWeight:"800", marginTop:"2px", wordBreak:"break-all" }}>{safe.label}</div>
            {safe.lastAccidentDate && <div style={{ fontSize:"11px", opacity:0.85, marginTop:"2px" }}>{fmtStartDate(safe.lastAccidentDate)}～</div>}
          </div>
          <div style={{ position:"relative", textAlign:"right", whiteSpace:"nowrap" }}>
            <span style={{ fontSize:"44px", fontWeight:"900", lineHeight:1, fontVariantNumeric:"tabular-nums" }}><CountUp value={safe.days} /></span>
            <span style={{ fontSize:"15px", fontWeight:"700", marginLeft:"4px" }}>日</span>
          </div>
        </div>
      )}

      {/* クイックアクセス */}
      {onNavigate && (() => {
        const quick = [
          ['report','🙈','ヒヤリ報告'], ['trouble','⚠️','トラブル報告'], ['ky','📷','KY報告'], ['improvement','💡','改善メモ'],
        ].filter(([id]) => navItems.includes(id));
        if (quick.length === 0) return null;
        return (
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${quick.length},1fr)`, gap:"8px", marginBottom:"14px" }}>
            {quick.map(([id, icon, label]) => (
              <button key={id} type="button" onClick={() => onNavigate(id)} className="tfe-lift"
                style={{ background:C.white, border:`1px solid ${C.gray2}`, borderRadius:"14px", padding:"10px 4px", cursor:"pointer",
                  display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", fontFamily:"inherit",
                  boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
                <span style={{ fontSize:"22px" }}>{icon}</span>
                <span style={{ fontSize:"10px", fontWeight:"700", color:C.navy }}>{label}</span>
              </button>
            ))}
          </div>
        );
      })()}

      {/* 事故速報ニュース */}
      {accidentNews.length > 0 && (
        <div style={{ background: C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"14px", border:`1.5px solid ${C.red}22` }}>
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.gray2}`, display:"flex", alignItems:"center", gap:"8px" }}>
            <span style={{ fontSize:"14px" }}>🚨</span>
            <span style={{ fontWeight:"700", fontSize:"13px", color: C.red }}>速報</span>
          </div>
          {accidentNews.map(a => (
            <div key={a.id} style={{ padding:"10px 16px", borderBottom:`1px solid ${C.gray2}`, display:"flex", alignItems:"center", gap:"10px" }}>
              {!a.is_read && <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:C.red, flexShrink:0, display:"inline-block" }} />}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:"12px", fontWeight:"600", color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.title}</div>
                <div style={{ fontSize:"10px", color:C.textSub, marginTop:"2px" }}>{a.occurred_at?.slice(0,10)} {a.location}</div>
              </div>
              {!a.is_confirmed && <span style={{ fontSize:"10px", padding:"2px 8px", borderRadius:"20px", background:"#FCEBEB", color:C.red, fontWeight:"600", flexShrink:0 }}>未確認</span>}
            </div>
          ))}
        </div>
      )}

      {/* KPIカード */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:"10px", marginBottom:"14px" }}>
        {kpiData.map((k,i) => (
          <div key={i} style={{
            background:`linear-gradient(135deg, ${C.white} 55%, ${k.color}14)`,
            borderRadius:"14px", padding:"14px", borderLeft:`4px solid ${k.color}`,
            boxShadow:`0 3px 12px ${k.color}1f`, display:"flex", alignItems:"center", gap:"12px",
          }}>
            <div style={{
              width:"44px", height:"44px", borderRadius:"14px", flexShrink:0,
              background:`linear-gradient(135deg, ${k.color}33, ${k.color}14)`,
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:"21px",
            }}>{k.icon}</div>
            <div>
              <div style={{ fontSize:"10px", color: C.textSub, marginBottom:"2px" }}>{k.label}</div>
              <div style={{ fontSize:"26px", fontWeight:"800", color: k.color, lineHeight:1 }}><CountUp value={k.val} /></div>
            </div>
          </div>
        ))}
      </div>

      {/* 安全テーマ */}
      {theme ? (
        editingTheme ? (
          <ThemeEditForm theme={theme} token={token}
            onSaved={u => { setTheme(u); setEditingTheme(false); }}
            onCancel={() => setEditingTheme(false)} />
        ) : (
          <div style={{
            background:`linear-gradient(135deg, #E6F1FB, #EAF3DE)`,
            border:`1px solid #B5D4F4`, borderRadius:"16px",
            padding:"16px", marginBottom:"14px", position:"relative",
          }}>
            <div style={{ fontSize:"10px", color:"#0C447C", marginBottom:"4px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.08em" }}>
              {theme.month} · 今月の安全テーマ
            </div>
            <div style={{ fontSize:"16px", fontWeight:"700", color:"#0C447C", marginBottom:"12px" }}>
              {theme.title}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              {theme.items?.map(it => (
                <div key={it.id} onClick={() => handleItemCheck(it.id, !it.isDone)}
                  style={{ display:"flex", alignItems:"center", gap:"10px", fontSize:"13px", color:"#185FA5",
                    cursor: user.role==='safety_admin'?'pointer':'default' }}>
                  <div style={{
                    width:"20px", height:"20px", borderRadius:"6px", flexShrink:0,
                    border: `2px solid ${it.isDone ? C.green : '#85B7EB'}`,
                    background: it.isDone ? C.green : "rgba(255,255,255,0.8)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"all .2s",
                  }}>
                    {it.isDone && <span style={{ color:C.white, fontSize:"11px", fontWeight:"700" }}>✓</span>}
                  </div>
                  <span style={{ textDecoration: it.isDone?'line-through':'none', opacity: it.isDone?0.5:1, lineHeight:1.4 }}>
                    {it.body}
                  </span>
                </div>
              ))}
            </div>
            {(user.role==='safety_admin'||user.role==='manager') && (
              <button onClick={() => setEditingTheme(true)} style={{
                position:"absolute", top:"12px", right:"12px",
                padding:"4px 12px", background:"rgba(255,255,255,0.8)", border:`1px solid #B5D4F4`,
                borderRadius:"8px", cursor:"pointer", fontSize:"11px", color:"#185FA5", fontWeight:"600",
              }}>編集</button>
            )}
          </div>
        )
      ) : (user.role === 'safety_admin' || user.role === 'manager') ? (
        <ThemeCreateForm token={token} onSaved={c => setTheme(c)} />
      ) : (
        <div style={{
          background: C.gray1, border:`1.5px dashed ${C.gray2}`, borderRadius:"14px",
          padding:"16px", marginBottom:"14px", fontSize:"12px", color: C.gray3, textAlign:"center",
        }}>今月の安全テーマはまだ設定されていません</div>
      )}

      {/* 最近の報告（ヒヤリ「あり」のみ。なければ非表示） */}
      {reports.filter(r => r.hiya1 === 'あり').length > 0 && (
      <div style={{ background: C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"14px" }}>
        <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.gray2}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:"700", fontSize:"13px", color: C.text }}>最近の報告</span>
          <span style={{ fontSize:"10px", color: C.textSub, background: C.gray1, padding:"3px 8px", borderRadius:"20px" }}>
            {user.role==='safety_admin'?'全社':user.role==='employee'?'全社':'自社'}
          </span>
        </div>
        {reports.filter(r => r.hiya1 === 'あり').map(r => (
          <div key={r.id} style={{ padding:"10px 16px", borderBottom:`1px solid ${C.gray2}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"4px" }}>
              <span style={{
                padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"600",
                background: r.hiya1==='あり'?'#FAEEDA':'#f0f0f0',
                color: r.hiya1==='あり'?C.amber:C.textSub,
              }}>{r.hiya1}</span>
              <span style={{ fontSize:"13px", fontWeight:"600", color: C.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {r.placeName}
              </span>
            </div>
            {r.when && <div style={{ fontSize:"11px", color: C.textSub }}>📍 {r.when}</div>}
            <div style={{ fontSize:"10px", color: C.gray3, marginTop:"3px" }}>
              {r.name} · {r.date} · {r.group}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* KY重点項目 */}
      <div style={{ background: C.white, borderRadius:"16px", padding:"16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontWeight:"700", marginBottom:"12px", fontSize:"13px", color: C.text }}>本日のKY重点項目</div>
        {[
          ['高所作業時の安全帯未着用による墜落', '安全帯装着確認・二丁掛けを徹底'],
          ['資材の玉掛け不良による落下',         '有資格者確認・合図後に吊り上げ'],
          ['足場の固定ピン未施錠による崩壊',     '作業前に目視点検・疑わしければ即報告'],
        ].map(([hazard, counter], i) => (
          <div key={i} style={{
            display:"flex", gap:"12px", padding:"10px 0",
            borderBottom: i < 2 ? `1px solid ${C.gray2}` : "none",
          }}>
            <div style={{
              width:"26px", height:"26px", borderRadius:"8px", background: C.navy,
              color: C.white, display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"11px", fontWeight:"700", flexShrink:0,
            }}>{i+1}</div>
            <div>
              <div style={{ fontSize:"12px", fontWeight:"600", color: C.text, marginBottom:"3px" }}>{hazard}</div>
              <div style={{ fontSize:"11px", color: C.textSub }}>→ {counter}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 安全テーマのメール文（サーバーと同じ定型文。年月はテーマの月から自動）
function buildThemeMailDraft(month, title, items) {
  const [y, m] = String(month || '').split('-').map(Number);
  const ym = `${y || ''}年${m || ''}月度`;
  const lines = [`${ym} 安全目標`, '', title || ''];
  (items || []).map(i => i.trim()).filter(Boolean).forEach(i => lines.push(`・${i}`));
  return lines.join('\n');
}
function buildThemeMailPreview(month, title, lead, items) {
  const [y, m] = String(month || '').split('-').map(Number);
  const ym = `${y || ''}年${m || ''}月度`;
  const line = '-'.repeat(50);
  const middle = (lead && lead.trim()) ? lead.trim() : buildThemeMailDraft(month, title, items);
  const changing = [middle];
  const body = [
    '安全担当各位', '', 'お疲れ様です。', '事務所・各現場へ掲示、協力会社様への展開をお願いします。',
    line, ...changing, line,
    '現場作業・移動における安全は、私たちの最優先事項です。', '●安全は全員で守るもの',
    '一人ひとりの意識と行動が、無事故・無災害の職場環境を作ります。',
    '知識があっても意識がなければ事故につながります。',
    '安全取り組みへのご協力よろしくお願いいたします。', '', '以上、ご安全に！',
  ].join('\n');
  return { subject: `【安管メール】${ym}TFE安全目標`, body };
}

// 保存後のメール結果をまとめた文
function themeMailResultText(r) {
  if (!r) return '';
  if (r.total === 0) return r.test ? 'テスト送信先のメールアドレスが正しくありません。' : '送信先（権限が「管理職」でメールアドレスのあるユーザー）がいないため、メールは送られませんでした。';
  const head = r.test ? `テスト送信：${r.sent}件送りました。` : `各課長に送信：${r.total}人中 ${r.sent}人に送りました。`;
  return r.failed && r.failed.length ? `${head}\n送れなかった宛先：${r.failed.join(', ')}` : head;
}

// 安全テーマ メール欄（本文1か所・プレビュー・送信設定）
function ThemeMailFields({ month, title, items, lead, setLead, sendMail, setSendMail, testEmail, setTestEmail, idSuffix }) {
  const [showPreview, setShowPreview] = useState(false);
  const pv = buildThemeMailPreview(month, title, lead, items);
  return (
    <>
      <Row label="メール用：本文（----の間に入る部分・ホームには出ません）">
        <textarea value={lead} onChange={e => setLead(e.target.value)} rows={8}
          placeholder={"例：\n2026年10月度 安全目標\n\n墜落・転落災害ゼロ\n建設業で最も多い重大災害は墜落・転落です。\n・高所作業前の点検\n・フルハーネスの確実な使用\n慣れた作業ほど基本を徹底してください。"}
          style={{ ...inputStyle, resize:"vertical", lineHeight:1.6 }} />
        <button type="button" onClick={() => setLead(buildThemeMailDraft(month, title, items))} style={{ ...addBtnStyle, marginTop:"6px" }}>✏️ テーマと取組から下書きを入れる</button>
      </Row>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px" }}>
        <input type="checkbox" id={`themeSendMail${idSuffix}`} checked={sendMail} onChange={e=>setSendMail(e.target.checked)} />
        <label htmlFor={`themeSendMail${idSuffix}`} style={{ fontSize:"13px", color:C.text, cursor:"pointer", fontWeight:"600" }}>📨 各課長にメールで送る（権限「管理職」の方）</label>
      </div>
      {sendMail && (
        <Row label="テスト送信先メール">
          <input value={testEmail} onChange={e=>setTestEmail(e.target.value)}
            placeholder="空欄なら各課長に送信。入力するとこのアドレスだけに【テスト送信】" type="email" style={inputStyle} />
        </Row>
      )}
      <div style={{ marginBottom:"12px" }}>
        <button type="button" onClick={() => setShowPreview(v => !v)} style={{ ...addBtnStyle }}>{showPreview ? 'プレビューを閉じる' : '📄 メール文をプレビュー'}</button>
        {showPreview && (
          <div style={{ marginTop:"8px", background:C.gray1, borderRadius:"10px", padding:"12px 14px" }}>
            <div style={{ fontSize:"12px", fontWeight:"700", color:C.navy, marginBottom:"6px" }}>件名：{pv.subject}</div>
            <pre style={{ margin:0, fontSize:"12px", color:C.text, whiteSpace:"pre-wrap", fontFamily:"inherit", lineHeight:1.6 }}>{pv.body}</pre>
          </div>
        )}
      </div>
    </>
  );
}

// ================================
// 安全テーマ 編集フォーム
// ================================
function ThemeEditForm({ theme, token, onSaved, onCancel }) {
  const [title, setTitle]       = useState(theme.title);
  const [items, setItems]       = useState(theme.items?.map(it => it.body) || []);
  const [lead, setLead]         = useState(theme.lead_text || '');
  const [sendMail, setSendMail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sendMail && !testEmail.trim() && !window.confirm('各課長（権限「管理職」の方）にメールを送信します。よろしいですか？\n※ 先に「テスト送信先メール」で自分宛てに確認するのがおすすめです。')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/safety_themes.php?id=${theme.id}`, {
        method:'PATCH', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ title, lead_text: lead, items: items.filter(i => i.trim()), send_mail: sendMail, test_email: testEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.mail_result) window.alert(themeMailResultText(data.mail_result));
        onSaved(data);
      } else {
        window.alert(data.error || '保存に失敗しました');
      }
    } catch { window.alert('保存に失敗しました'); }
    setSaving(false);
  };

  return (
    <div style={{ background: C.white, borderRadius:"16px", padding:"18px", marginBottom:"14px", border:`1.5px solid #B5D4F4`, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:"14px", fontWeight:"700", marginBottom:"16px", color: C.navy }}>安全テーマを編集</div>
      <form onSubmit={handleSubmit}>
        <Row label="テーマタイトル *">
          <input value={title} onChange={e => setTitle(e.target.value)} required style={inputStyle} />
        </Row>
        <div style={{ marginBottom:"12px" }}>
          <label style={labelStyle}>重点取組</label>
          {items.map((item,i) => (
            <div key={i} style={{ display:"flex", gap:"6px", marginBottom:"6px" }}>
              <input value={item} onChange={e => setItems(prev => prev.map((v,idx) => idx===i?e.target.value:v))}
                placeholder={`取組 ${i+1}`} style={{ ...inputStyle, flex:1 }} />
              <button type="button" onClick={() => setItems(prev => prev.filter((_,idx) => idx!==i))}
                style={deleteBtnStyle}>×</button>
            </div>
          ))}
          <button type="button" onClick={() => setItems(prev => [...prev,''])} style={addBtnStyle}>＋ 追加</button>
        </div>
        <ThemeMailFields month={theme.month} title={title} items={items} lead={lead} setLead={setLead}
          sendMail={sendMail} setSendMail={setSendMail} testEmail={testEmail} setTestEmail={setTestEmail} idSuffix="Edit" />
        <div style={{ display:"flex", gap:"8px" }}>
          <button type="submit" disabled={saving} style={primaryBtnStyle(saving)}>{saving?'保存中...':(sendMail ? '保存してメール送信' : '保存')}</button>
          <button type="button" onClick={onCancel} style={cancelBtnStyle}>キャンセル</button>
        </div>
      </form>
    </div>
  );
}

// ================================
// 安全テーマ 新規作成フォーム
// ================================
function ThemeCreateForm({ token, onSaved }) {
  const [show, setShow]         = useState(false);
  const [title, setTitle]       = useState('');
  const [items, setItems]       = useState(['']);
  const [lead, setLead]         = useState('');
  const [sendMail, setSendMail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving]     = useState(false);
  // 年月は端末の日本時間で自動（UTCだと月初の朝に前月になるため）
  const month = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sendMail && !testEmail.trim() && !window.confirm('各課長（権限「管理職」の方）にメールを送信します。よろしいですか？\n※ 先に「テスト送信先メール」で自分宛てに確認するのがおすすめです。')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/safety_themes.php`, {
        method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ month, title, lead_text: lead, items: items.filter(i => i.trim()), send_mail: sendMail, test_email: testEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.mail_result) window.alert(themeMailResultText(data.mail_result));
        onSaved(data);
      } else {
        window.alert(data.error || '保存に失敗しました');
      }
    } catch { window.alert('保存に失敗しました'); }
    setSaving(false);
  };

  if (!show) return (
    <div style={{
      background: C.gray1, border:`1.5px dashed ${C.gray2}`, borderRadius:"14px",
      padding:"14px 16px", marginBottom:"14px", display:"flex", justifyContent:"space-between", alignItems:"center",
    }}>
      <span style={{ fontSize:"12px", color: C.gray3 }}>今月の安全テーマが未設定</span>
      <button onClick={() => setShow(true)} style={{ padding:"6px 14px", background: C.navy, color: C.white, border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>
        ＋ 設定する
      </button>
    </div>
  );

  return (
    <div style={{ background: C.white, borderRadius:"16px", padding:"18px", marginBottom:"14px", border:`1.5px solid #B5D4F4`, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:"14px", fontWeight:"700", marginBottom:"16px", color: C.navy }}>
        今月の安全テーマを設定（{month}）
      </div>
      <form onSubmit={handleSubmit}>
        <Row label="テーマタイトル *">
          <input value={title} onChange={e => setTitle(e.target.value)} required
            placeholder="例：高所作業・足場の安全を最優先に" style={inputStyle} />
        </Row>
        <div style={{ marginBottom:"12px" }}>
          <label style={labelStyle}>重点取組</label>
          {items.map((item,i) => (
            <div key={i} style={{ display:"flex", gap:"6px", marginBottom:"6px" }}>
              <input value={item} onChange={e => setItems(prev => prev.map((v,idx) => idx===i?e.target.value:v))}
                placeholder={`取組 ${i+1}`} style={{ ...inputStyle, flex:1 }} />
              <button type="button" onClick={() => setItems(prev => prev.filter((_,idx) => idx!==i))} style={deleteBtnStyle}>×</button>
            </div>
          ))}
          <button type="button" onClick={() => setItems(prev => [...prev,''])} style={addBtnStyle}>＋ 追加</button>
        </div>
        <ThemeMailFields month={month} title={title} items={items} lead={lead} setLead={setLead}
          sendMail={sendMail} setSendMail={setSendMail} testEmail={testEmail} setTestEmail={setTestEmail} idSuffix="Create" />
        <div style={{ display:"flex", gap:"8px" }}>
          <button type="submit" disabled={saving} style={primaryBtnStyle(saving)}>{saving?'保存中...':(sendMail ? '設定してメール送信' : '設定する')}</button>
          <button type="button" onClick={() => setShow(false)} style={cancelBtnStyle}>キャンセル</button>
        </div>
      </form>
    </div>
  );
}

// ================================
// ヒヤリハット報告フォーム
// ================================
function ReportForm({ token, user }) {
  const isPartnerUser = user.role === "partner" || user.role === "employee_dispatch";
  const [form, setForm] = useState({
    group: (() => {
      if (isPartnerUser) return "";
      const raw = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
      return DEPARTMENTS.find(d =>
        d === raw ||
        d.replace(/課$/, "") === raw.replace(/課$/, "") ||
        d.includes(raw.replace(/課$/, "")) ||
        raw.includes(d.replace(/課$/, ""))
      ) || DEPARTMENTS[0];
    })(),
    name:  user.name  || "",
    email: user.email || "",
    hiya1:"あり", date:new Date().toISOString().slice(0,10),
    placeType:"", placeName:"", when:"",
    result:"", reason:"", action:"", comment:"", photo:"なし",
  });
  const [sent, setSent]   = useState(false);
  const [error, setError] = useState("");

  const set = (k,v) => setForm(prev => ({ ...prev, [k]:v }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      const res = await fetch(`${API}/reports.php`, {
        method:"POST",
        headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setSent(true);
    } catch { setError("送信に失敗しました"); }
  };

  if (sent) return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"48px 24px", textAlign:"center",
    }}>
      <div style={{ fontSize:"56px", marginBottom:"16px" }}>✅</div>
      <div style={{ fontSize:"18px", fontWeight:"700", marginBottom:"8px", color: C.navy }}>報告を送信しました</div>
      <div style={{ fontSize:"12px", color: C.gray3, marginBottom:"24px" }}>ご報告ありがとうございます</div>
      <button onClick={() => setSent(false)} style={{ ...primaryBtnStyle(false), width:"auto", padding:"10px 28px" }}>
        続けて報告する
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth:"560px" }}>
      <div style={{ background: C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:"16px", fontWeight:"700", marginBottom:"20px", color: C.navy }}>
          ヒヤリハット報告
        </div>
        <form onSubmit={handleSubmit}>
          <Row label="グループ *">
            {isPartnerUser && <div style={{ fontSize:"11px", color: C.textSub, marginBottom:"6px" }}>担当しているTFEの課を選択してください</div>}
            <select value={form.group} onChange={e => set("group", e.target.value)} required style={inputStyle}>
              {isPartnerUser && <option value="">-- 課を選択 --</option>}
              {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
            </select>
          </Row>
          <Row label="報告者 *">
            <input value={form.name} onChange={e => set("name", e.target.value)} required style={inputStyle} />
          </Row>
          <Row label="メールアドレス *">
            <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required style={inputStyle} />
          </Row>
          <Row label="ヒヤリハット *">
            <div style={{ display:"flex", gap:"16px" }}>
              {["あり","なし"].map(v => (
                <label key={v} style={{ display:"flex", alignItems:"center", gap:"6px", fontSize:"14px", cursor:"pointer" }}>
                  <input type="radio" name="hiya1" value={v}
                    checked={form.hiya1===v} onChange={e => set("hiya1", e.target.value)} />
                  {v}
                </label>
              ))}
            </div>
          </Row>
          <Row label="いつ *">
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} required style={inputStyle} />
          </Row>
          <Row label="局舎">
            <input value={form.placeType} onChange={e => set("placeType", e.target.value)} placeholder="ctc、NTT等" style={inputStyle} />
          </Row>
          <Row label="局舎名 *">
            <input value={form.placeName} onChange={e => set("placeName", e.target.value)} required style={inputStyle} />
          </Row>
          <Row label="どんな時に">
            <input value={form.when} onChange={e => set("when", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="どうなった">
            <input value={form.result} onChange={e => set("result", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="なぜそうなった">
            <input value={form.reason} onChange={e => set("reason", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="今後どうする">
            <input value={form.action} onChange={e => set("action", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="自由コメント">
            <textarea value={form.comment} onChange={e => set("comment", e.target.value)}
              rows={4} style={{ ...inputStyle, resize:"vertical" }} />
          </Row>
          <Row label="画像">
            <div style={{ display:"flex", gap:"16px" }}>
              {["あり","なし"].map(v => (
                <label key={v} style={{ display:"flex", alignItems:"center", gap:"6px", fontSize:"14px", cursor:"pointer" }}>
                  <input type="radio" name="photo" value={v}
                    checked={form.photo===v} onChange={e => set("photo", e.target.value)} />
                  {v}
                </label>
              ))}
            </div>
          </Row>

          {error && <div style={{ color: C.red, fontSize:"12px", marginBottom:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}

          <div style={{ display:"flex", gap:"10px", marginTop:"20px" }}>
            <button type="submit" style={{ ...primaryBtnStyle(false), flex:1 }}>送信</button>
            <button type="reset" style={{ ...cancelBtnStyle, flex:1 }}>リセット</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ================================
// 報告一覧
// ================================
function ReportList({ token, user, isMobile }) {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const rawDept = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
  const matchedDept = DEPARTMENTS.find(d =>
    d === rawDept ||
    d.replace(/課$/, "") === rawDept.replace(/課$/, "") ||
    d.includes(rawDept.replace(/課$/, "")) ||
    rawDept.includes(d.replace(/課$/, ""))
  ) || "all";
  const [filterDept, setFilterDept] = useState(matchedDept);
  const [filterDateIdx, setFilterDateIdx] = useState(0); // 日付インデックス（0=最新）
  const [filterAri, setFilterAri] = useState(false); // ヒヤリハットありのみ絞り込み

  const fetchReports = () => {
    setLoading(true);
    fetch(`${API}/reports.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { Array.isArray(data) && setReports(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchReports(); }, []);


  const thisMonth = new Date().toISOString().slice(0, 7);
  const deptTabs = ["all", ...Array.from(new Set(reports.map(r => r.group).filter(Boolean))).sort((a,b) => DEPARTMENTS.indexOf(a)-DEPARTMENTS.indexOf(b))];
  const deptFiltered = filterDept === "all" ? reports : reports.filter(r => r.group === filterDept);
  const monthlyCount = deptFiltered.filter(r => (r.date || "").slice(0, 7) === thisMonth).length;
  const ariCount = deptFiltered.filter(r => r.hiya1 === "あり").length;
  const ariFiltered = filterAri ? deptFiltered.filter(r => r.hiya1 === "あり") : deptFiltered;
  // 日付ナビ
  const hiyaDates = [...new Set(ariFiltered.map(r => r.date).filter(Boolean))].sort((a,b) => b.localeCompare(a));
  const safeHiyaIdx = Math.min(filterDateIdx, Math.max(0, hiyaDates.length - 1));
  const currentHiyaDate = hiyaDates[safeHiyaIdx] || null;
  const dateRecords = currentHiyaDate ? ariFiltered.filter(r => r.date === currentHiyaDate) : [];
  const hiyaAriCount = dateRecords.filter(r => r.hiya1 === "あり").length;
  const [hm, hd] = currentHiyaDate ? currentHiyaDate.slice(5).split("-").map(Number) : [null, null];

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"48px", color: C.gray3 }}>
      読み込み中...
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap" }}>
          <span style={{ fontWeight:"700", fontSize:"15px", color: C.text }}>ヒヤリハット一覧</span>
          <span style={{ fontSize:"11px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background: C.amber + "22", color: C.amber }}>今月 {monthlyCount}件</span>
          <span onClick={() => { setFilterAri(f => !f); setFilterDateIdx(0); }} style={{ fontSize:"11px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", cursor:"pointer", transition:"all .15s", background: filterAri ? C.amber : "#FAEEDA", color: filterAri ? C.white : C.amber }}>⚠️ あり {ariCount}件{filterAri ? " ✕" : ""}</span>
        </div>
        <button onClick={fetchReports} style={{ padding:"5px 12px", background: C.gray1, border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"11px", color: C.textSub }}>更新</button>
      </div>

      {/* 課タブ */}
      <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"12px" }}>
        {deptTabs.map(d => {
          const cnt = d === "all" ? reports.length : reports.filter(r => r.group === d).length;
          const ariCnt = d === "all" ? reports.filter(r=>r.hiya1==="あり").length : reports.filter(r => r.group === d && r.hiya1 === "あり").length;
          const active = filterDept === d;
          return (
            <button key={d} onClick={() => { setFilterDept(d); setFilterDateIdx(0); }} style={{
              padding:"5px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:"600",
              cursor:"pointer", border:"none", transition:"all .15s",
              background: active ? C.navy : C.white,
              color:      active ? C.white : C.textSub,
              boxShadow:  active ? "0 2px 6px rgba(26,42,74,0.2)" : "none",
              display:"flex", alignItems:"center", gap:"4px",
            }}>
              {d === "all" ? "すべて" : d}
              <span style={{ fontSize:"10px", background: active?"rgba(255,255,255,0.2)":"#eee", borderRadius:"10px", padding:"1px 5px" }}>{cnt}</span>
              {ariCnt > 0 && <span style={{ fontSize:"10px", background:"#FAEEDA", color: C.amber, borderRadius:"10px", padding:"1px 5px" }}>⚠{ariCnt}</span>}
            </button>
          );
        })}
      </div>

      {/* 日付ナビ */}
      {hiyaDates.length > 0 && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"14px", marginBottom:"14px", padding:"10px 0", borderTop:`1px solid ${C.gray2}`, borderBottom:`1px solid ${C.gray2}` }}>
          <button onClick={() => setFilterDateIdx(i => Math.min(i+1, hiyaDates.length-1))} disabled={safeHiyaIdx >= hiyaDates.length-1} style={{
            width:"32px", height:"32px", borderRadius:"50%", border:`1.5px solid ${C.gray2}`,
            background:C.white, color:C.text, fontSize:"16px", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            opacity: safeHiyaIdx >= hiyaDates.length-1 ? 0.3 : 1,
          }}>‹</button>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color:C.text }}>{hm}/{hd}</div>
            <div style={{ fontSize:"11px", color:C.textSub }}>
              {dateRecords.length}件
              {hiyaAriCount > 0 && <span style={{ marginLeft:"6px", color:C.amber }}>⚠️ あり {hiyaAriCount}件</span>}
            </div>
          </div>
          <button onClick={() => setFilterDateIdx(i => Math.max(i-1, 0))} disabled={safeHiyaIdx <= 0} style={{
            width:"32px", height:"32px", borderRadius:"50%", border:`1.5px solid ${C.gray2}`,
            background:C.white, color:C.text, fontSize:"16px", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            opacity: safeHiyaIdx <= 0 ? 0.3 : 1,
          }}>›</button>
        </div>
      )}

      {hiyaDates.length === 0 ? (
        <div style={{ background: C.white, borderRadius:"16px", padding:"40px", textAlign:"center", color: C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          報告がありません
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
          {dateRecords.map(r => (
              <div key={r.id} onClick={() => setSelected(r)} style={{
                background: C.white, borderRadius:"14px", padding:"14px 16px",
                boxShadow:"0 2px 6px rgba(0,0,0,0.05)", cursor:"pointer",
                borderLeft:`3px solid ${r.hiya1==='あり' ? C.amber : C.gray2}`,
                transition:"box-shadow .15s",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"5px" }}>
                  <span style={{
                    padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"600",
                    background: r.hiya1==='あり'?'#FAEEDA':'#f0f0f0',
                    color: r.hiya1==='あり'? C.amber : C.textSub,
                  }}>{r.hiya1}</span>
                  <span style={{ flex:1, fontSize:"13px", fontWeight:"600", color: C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {r.placeName}
                  </span>
                </div>
                <div style={{ fontSize:"11px", color: C.textSub }}>
                  {r.name} · {r.group}
                </div>
              </div>
          ))}
        </div>
      )}

      {/* 詳細パネル（スマホは全画面オーバーレイ） */}
      {selected && (
        <div style={{
          position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:300,
          background:"rgba(0,0,0,0.5)", backdropFilter:"blur(4px)",
          display:"flex", alignItems: isMobile?"flex-end":"center", justifyContent:"center",
        }} onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white,
            borderRadius: isMobile ? "20px 20px 0 0" : "20px",
            width: isMobile ? "100%" : "420px",
            maxHeight: isMobile ? "90dvh" : "85vh",
            overflow:"hidden", display:"flex", flexDirection:"column",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)",
          }}>
            {/* ヘッダー */}
            <div style={{ background: C.navy, padding:"16px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              {isMobile && <div style={{ width:"40px", height:"4px", borderRadius:"2px", background:"rgba(255,255,255,0.3)", position:"absolute", top:"8px", left:"50%", transform:"translateX(-50%)" }} />}
              <span style={{ color: C.white, fontWeight:"700", fontSize:"14px" }}>報告詳細</span>
              <div style={{ display:"flex", gap:"8px" }}>
                {user.role==='safety_admin' && (
                  <button onClick={async () => {
                    if (!window.confirm('この報告を削除しますか？')) return;
                    await fetch(`${API}/reports.php?id=${selected.id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
                    setReports(prev => prev.filter(r => r.id !== selected.id));
                    setSelected(null);
                  }} style={{ background: C.red, border:"none", color: C.white, padding:"5px 12px", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>
                    削除
                  </button>
                )}
                <button onClick={() => setSelected(null)} style={{ background:"rgba(255,255,255,0.15)", border:"none", color: C.white, width:"30px", height:"30px", borderRadius:"50%", cursor:"pointer", fontSize:"16px" }}>×</button>
              </div>
            </div>

            <div style={{ overflowY:"auto", padding:"16px 18px" }}>
              {/* 詳細情報 */}
              {[
                ['グループ', selected.group],
                ['報告者',   selected.name],
                ['メール',   selected.email],
                ['ヒヤリハット', selected.hiya1],
                ['日付',     selected.date],
                ['局舎',     selected.placeType || '—'],
                ['局舎名',   selected.placeName],
                ['どんな時に', selected.when || '—'],
                ['どうなった', selected.result || '—'],
                ['なぜそうなった', selected.reason || '—'],
                ['今後どうする', selected.action || '—'],
                ['画像',     selected.photo || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ marginBottom:"10px", paddingBottom:"10px", borderBottom:`1px solid ${C.gray2}` }}>
                  <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"3px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</div>
                  <div style={{ fontSize:"13px", color: C.text }}>{val}</div>
                </div>
              ))}

              {selected.comment && (
                <div style={{ marginBottom:"10px" }}>
                  <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"6px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.05em" }}>自由コメント</div>
                  <div style={{ fontSize:"13px", color: C.text, background: C.gray1, borderRadius:"10px", padding:"10px 12px", lineHeight:"1.6", whiteSpace:"pre-wrap" }}>
                    {selected.comment}
                  </div>
                </div>
              )}

              <div style={{ fontSize:"10px", color: C.gray3, textAlign:"right", marginTop:"8px" }}>
                登録：{(selected.created_at || selected.createdAt) ? new Date(String(selected.created_at || selected.createdAt).replace(' ', 'T')).toLocaleString('ja-JP') : '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// トラブル報告
// ================================
function TroublePage({ user, token }) {
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError]     = useState("");
  const [photos, setPhotos]   = useState([]);
  const [previews, setPreviews] = useState([]);

  const TYPES = ["0報", "1報", "クレーム", "その他"];

  const isPartnerUser = user.role === "partner" || user.role === "employee_dispatch";
  const emptyForm = {
    group: (() => {
      if (isPartnerUser) return "";
      const raw = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
      return DEPARTMENTS.find(d =>
        d === raw ||
        d.replace(/課$/, "") === raw.replace(/課$/, "") ||
        d.includes(raw.replace(/課$/, "")) ||
        raw.includes(d.replace(/課$/, ""))
      ) || DEPARTMENTS[0];
    })(),
    name:        user.name  || "",
    email:       user.email || "",
    type:        "0報",
    title:       "",
    description: "",
  };
  const [form, setForm] = useState(emptyForm);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    setPhotos(prev => [...prev, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => setPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
  };
  const removePhoto = (i) => {
    setPhotos(prev => prev.filter((_,idx) => idx !== i));
    setPreviews(prev => prev.filter((_,idx) => idx !== i));
  };

  const handleSubmit = async () => {
    setError(""); setSaving(true);
    if (!form.title || !form.description) {
      setError("件名と報告内容は必須です"); setSaving(false); return;
    }
    try {
      // 写真は縮小してから送る（スマホの写真は数MBあり、そのままだと送信に失敗するため）
      const photoData = await Promise.all(photos.map(async f => ({
        fileName: f.name,
        fileData: await resizeImage(f).catch(() => fileToBase64(f)),
      })));
      const res = await fetch(`${API}/trouble_reports.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          category:    form.type,
          title:       `[${form.group}] ${form.title}`,
          description: `報告者：${form.name}（${form.email}）\n種別：${form.type}\n\n${form.description}`,
          occurredAt:  new Date().toISOString().slice(0, 19).replace("T", " "),
          impactScope: form.group,
          photos:      photoData,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "送信に失敗しました"); setSaving(false); return; }
      setForm(emptyForm); setPhotos([]); setPreviews([]);
      setSuccess("トラブル報告を送信しました");
      setTimeout(() => setSuccess(""), 4000);
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  const typeColor = (t) => ({
    "0報":     { bg:"#E6F1FB", color: C.blue },
    "1報":     { bg:"#FAEEDA", color: C.amber },
    "クレーム": { bg:"#FCEBEB", color: C.red },
    "その他":  { bg: C.gray1,  color: C.gray3 },
  }[t] || { bg: C.gray1, color: C.gray3 });

  return (
    <div style={{ background: C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
      {success && (
        <div style={{ background:"#E6F9F2", border:`1px solid ${C.green}`, borderRadius:"10px", padding:"10px 14px", fontSize:"12px", color: C.green, marginBottom:"16px" }}>
          ✅ {success}
        </div>
      )}

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>グループ *</label>
        {isPartnerUser && <div style={{ fontSize:"11px", color: C.textSub, marginBottom:"6px" }}>担当しているTFEの課を選択してください</div>}
        <select value={form.group} onChange={e => set("group", e.target.value)} required style={{ ...inputStyle, cursor:"pointer" }}>
          {isPartnerUser && <option value="">-- 課を選択 --</option>}
          {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
        </select>
      </div>

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>報告者</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = C.gray2} />
      </div>

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>メールアドレス</label>
        <input type="email" value={form.email} onChange={e => set("email", e.target.value)} style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = C.gray2} />
      </div>

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>報告種別</label>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
          {TYPES.map(t => {
            const tc = typeColor(t);
            const checked = form.type === t;
            return (
              <div key={t} onClick={() => set("type", t)} style={{
                padding:"7px 16px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                cursor:"pointer", transition:"all .15s",
                border:`1.5px solid ${checked ? tc.color : C.gray2}`,
                background: checked ? tc.bg : C.white,
                color: checked ? tc.color : C.textSub,
              }}>{t}</div>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>件名 ※</label>
        <input value={form.title} onChange={e => set("title", e.target.value)}
          placeholder="例：○○現場でのトラブル" style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = C.gray2} />
      </div>

      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>報告内容 ※</label>
        <textarea value={form.description} onChange={e => set("description", e.target.value)}
          placeholder="発生した状況、経緯、影響範囲などを記入してください"
          rows={6} style={{ ...inputStyle, resize:"vertical", lineHeight:"1.6" }}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = C.gray2} />
      </div>

      {/* 写真添付 */}
      <div style={{ marginBottom:"20px" }}>
        <label style={labelStyle}>写真（最大3枚）</label>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"8px" }}>
          {previews.map((src, i) => (
            <div key={i} style={{ position:"relative" }}>
              <img src={src} alt="" style={{ width:"80px", height:"80px", objectFit:"cover", borderRadius:"10px", border:`1px solid ${C.gray2}` }} />
              <button onClick={() => removePhoto(i)} style={{
                position:"absolute", top:"-6px", right:"-6px", width:"20px", height:"20px",
                borderRadius:"50%", background: C.red, color: C.white, border:"none",
                cursor:"pointer", fontSize:"11px", fontWeight:"700", lineHeight:"20px", textAlign:"center", padding:0,
              }}>×</button>
            </div>
          ))}
          {photos.length < 3 && (
            <label style={{ width:"80px", height:"80px", borderRadius:"10px", border:`2px dashed ${C.gray2}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexDirection:"column", gap:"4px" }}>
              <span style={{ fontSize:"22px", color: C.gray3 }}>📷</span>
              <span style={{ fontSize:"10px", color: C.gray3 }}>追加</span>
              <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotoChange} style={{ display:"none" }} />
            </label>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px", padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px" }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ display:"flex", gap:"8px" }}>
        <button onClick={handleSubmit} disabled={saving} style={primaryBtnStyle(saving)}>
          {saving ? "送信中..." : "送信する"}
        </button>
        <button onClick={() => { setForm(emptyForm); setPhotos([]); setPreviews([]); setError(""); }} style={cancelBtnStyle}>
          リセット
        </button>
      </div>
    </div>
  );
}

// ================================
// トラブル一覧（安全管理室専用）
// ================================
function TroubleListPage({ user, token, focusId, onFocusDone }) {
  const isAdminUser   = user.role === "safety_admin" || user.role === "manager";
  const isPartnerUser = user.role === "partner" || user.role === "employee_dispatch";
  const [sevMap, setSevMap]       = useState({});   // { トラブルID: "major" | "minor" }（賞罰制度への振り分け）
  const [sevSaving, setSevSaving] = useState(false);
  const [sevError, setSevError]   = useState("");
  const [commentText, setCommentText]     = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError]   = useState("");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [popupPhoto, setPopupPhoto] = useState(null);
  const rawDept = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
  const matchedDept = DEPARTMENTS.find(d =>
    d === rawDept ||
    d.replace(/課$/, "") === rawDept.replace(/課$/, "") ||
    d.includes(rawDept.replace(/課$/, "")) ||
    rawDept.includes(d.replace(/課$/, ""))
  ) || "all";
  // パートナーは自社の報告しか見えないので課の絞り込みはしない
  const [filterDept, setFilterDept] = useState(isPartnerUser ? "all" : matchedDept);

  const fetchSeverity = () => {
    if (!isAdminUser) return;
    fetch(`${API}/incident.php?action=trouble_map`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSevMap(d && !Array.isArray(d) && !d.error ? d : {}))
      .catch(() => {});
  };

  const handleComment = async () => {
    const text = commentText.trim();
    if (!text || !selected) return;
    setCommentSaving(true); setCommentError("");
    try {
      const res = await fetch(`${API}/trouble_reports.php?id=${selected.id}&action=comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: text }),
      });
      const data = await res.json();
      if (!res.ok) setCommentError(data.error || "送信に失敗しました");
      else {
        const add = (r) => r.id === selected.id ? { ...r, comments: [...(r.comments || []), data] } : r;
        setReports(prev => prev.map(add));
        setSelected(prev => (prev ? add(prev) : prev));
        setCommentText("");
      }
    } catch { setCommentError("サーバーに接続できません"); }
    setCommentSaving(false);
  };

  const handleSeverity = async (id, severity) => {
    setSevSaving(true); setSevError("");
    try {
      const res = await fetch(`${API}/incident.php?action=classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ troubleId: id, severity }),
      });
      const data = await res.json();
      if (!res.ok) { setSevError(data.error || "振り分けに失敗しました"); }
      else setSevMap(prev => { const n = { ...prev }; if (severity) n[id] = severity; else delete n[id]; return n; });
    } catch { setSevError("サーバーに接続できません"); }
    setSevSaving(false);
  };

  const fetchReports = (silent = false) => {
    fetchSeverity();
    if (!silent) setLoading(true); // 更新ボタンなどの再読み込みは画面を差し替えない
    fetch(`${API}/trouble_reports.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setReports(data);
          // 賞罰制度から開かれた場合は、そのトラブルの詳細を開く
          if (focusId) {
            const t = data.find(r => r.id === focusId);
            if (t) setSelected(t);
            onFocusDone && onFocusDone();
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchReports(); }, []);

  const handleStatusChange = async (id, newStatus) => {
    const res = await fetch(`${API}/trouble_reports.php?id=${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: newStatus }),
    });
    const updated = await res.json();
    setReports(prev => prev.map(r => r.id === id ? updated : r));
    setSelected(prev => prev?.id === id ? updated : prev);
  };

  // トラブル報告の削除（安管室のみ）
  const canDeleteTrouble = user.role === "safety_admin";
  const handleDeleteTrouble = async (r) => {
    if (!window.confirm(`このトラブル報告を削除しますか？\n${r.title || ""}\n（写真・コメント・インセンティブ＆ペナルティーへの振り分けも削除されます。元に戻せません）`)) return;
    try {
      const res = await fetch(`${API}/trouble_report_delete.php?id=${encodeURIComponent(r.id)}`, { method:"DELETE", headers:{ Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(data.error || "削除に失敗しました"); return; }
      setSelected(null);
      fetchReports(true);
    } catch { window.alert("削除に失敗しました"); }
  };

  const statusInfo = (s) => ({
    open:        { label:"未対応", bg:"#FCEBEB", color: C.red },
    in_progress: { label:"対応中", bg:"#FAEEDA", color: C.amber },
    closed:      { label:"完了",   bg:"#EAF3DE", color: C.green },
  }[s] || { label:s, bg: C.gray1, color: C.gray3 });

  const typeColor = (t) => ({
    "0報":     { bg:"#E6F1FB", color: C.blue },
    "1報":     { bg:"#FAEEDA", color: C.amber },
    "クレーム": { bg:"#FCEBEB", color: C.red },
    "その他":  { bg: C.gray1,  color: C.gray3 },
  }[t] || { bg: C.gray1, color: C.gray3 });

  if (loading) return <div style={{ textAlign:"center", padding:"40px", color: C.gray3 }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px" }}>
        <span style={{ fontWeight:"700", fontSize:"15px", color: C.text }}>トラブル一覧</span>
        <button onClick={() => fetchReports(true)} style={{ padding:"5px 12px", background: C.gray1, border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"11px", color: C.textSub }}>更新</button>
      </div>

      {/* 課フィルタ（パートナーは自社分のみなので非表示） */}
      {!isPartnerUser && (
        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"12px" }}>
          {["all", ...DEPARTMENTS].map(d => (
            <button key={d} onClick={() => setFilterDept(d)} style={{
              padding:"5px 12px", borderRadius:"20px", border:"none", cursor:"pointer",
              fontSize:"11px", fontWeight:"600",
              background: filterDept === d ? C.navy : C.gray1,
              color: filterDept === d ? C.white : C.textSub,
            }}>{d === "all" ? "すべて" : d}</button>
          ))}
        </div>
      )}

      {reports.filter(r => filterDept === "all" || (r.impact_scope || "").includes(filterDept)).length === 0 ? (
        <div style={{ background: C.white, borderRadius:"16px", padding:"40px", textAlign:"center", color: C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          報告がありません
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
          {reports.filter(r => filterDept === "all" || (r.impact_scope || "").includes(filterDept)).map(r => {
            const si = statusInfo(r.status);
            const tc = typeColor(r.category);
            return (
              <div key={r.id} onClick={() => setSelected(r)} style={{
                background: C.white, borderRadius:"14px", padding:"14px 16px",
                boxShadow:"0 2px 6px rgba(0,0,0,0.05)", cursor:"pointer",
                borderLeft:`3px solid ${tc.color}`,
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"5px" }}>
                  <span style={{ padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"600", background: tc.bg, color: tc.color }}>{r.category}</span>
                  <span style={{ flex:1, fontSize:"13px", fontWeight:"600", color: C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.title}</span>
                  {isAdminUser && sevMap[r.id] && (() => {
                    const sv = INCIDENT_TYPES.find(t => t.value === sevMap[r.id]);
                    return sv ? <span style={{ padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"700", background: sv.bg, color: sv.color, border:`1px solid ${sv.color}`, flexShrink:0 }}>{sv.label}</span> : null;
                  })()}
                  <span style={{ padding:"2px 8px", borderRadius:"20px", fontSize:"10px", fontWeight:"600", background: si.bg, color: si.color, flexShrink:0 }}>{si.label}</span>
                </div>
                <div style={{ fontSize:"11px", color: C.textSub }}>
                  {new Date(r.created_at).toLocaleString("ja-JP")}
                  {r.comments?.length > 0 && <span style={{ marginLeft:"10px" }}>💬 {r.comments.length}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 詳細モーダル */}
      {selected && (
        <div style={{
          position:"fixed", inset:0, zIndex:300, background:"rgba(0,0,0,0.5)", backdropFilter:"blur(4px)",
          display:"flex", alignItems:"flex-end", justifyContent:"center",
        }} onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius:"20px 20px 0 0",
            width:"100%", maxWidth:"500px", maxHeight:"85dvh",
            overflow:"hidden", display:"flex", flexDirection:"column",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ background: C.navy, padding:"16px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <span style={{ color: C.white, fontWeight:"700", fontSize:"14px" }}>トラブル詳細</span>
              <button onClick={() => setSelected(null)} style={{ background:"rgba(255,255,255,0.15)", border:"none", color: C.white, width:"30px", height:"30px", borderRadius:"50%", cursor:"pointer", fontSize:"16px" }}>×</button>
            </div>
            <div style={{ overflowY:"auto", padding:"16px 18px" }}>
              <div style={{ marginBottom:"16px", padding:"12px", background: C.gray1, borderRadius:"12px" }}>
                <div style={{ fontSize:"11px", color: C.textSub, marginBottom:"8px", fontWeight:"600" }}>ステータス変更</div>
                <div style={{ display:"flex", gap:"6px" }}>
                  {[
                    { val:"open",        label:"未対応", bg:"#FCEBEB", color: C.red },
                    { val:"in_progress", label:"対応中", bg:"#FAEEDA", color: C.amber },
                    { val:"closed",      label:"完了",   bg:"#EAF3DE", color: C.green },
                  ].map(s => (
                    <button key={s.val} onClick={() => handleStatusChange(selected.id, s.val)}
                      disabled={selected.status === s.val} style={{
                        flex:1, padding:"8px 4px", border:"none", borderRadius:"10px",
                        cursor: selected.status === s.val ? "default" : "pointer",
                        fontSize:"12px", fontWeight:"600", transition:"all .15s",
                        background: selected.status === s.val ? s.color : s.bg,
                        color: selected.status === s.val ? C.white : s.color,
                      }}>{s.label}</button>
                  ))}
                </div>
              </div>

              {isAdminUser && (
                <div style={{ marginBottom:"16px", padding:"12px", background: C.gray1, borderRadius:"12px" }}>
                  <div style={{ fontSize:"11px", color: C.textSub, marginBottom:"8px", fontWeight:"600" }}>インセンティブ＆ペナルティーへの振り分け</div>
                  <div style={{ display:"flex", gap:"6px" }}>
                    {[
                      { val:"major", label:"重度", bg:"#fee2e2", color:"#ef4444" },
                      { val:"minor", label:"軽微", bg:"#fef3c7", color:"#d97706" },
                      { val:"",      label:"未分類", bg: C.white, color: C.gray3 },
                    ].map(o => {
                      const cur = sevMap[selected.id] || "";
                      const on = cur === o.val;
                      return (
                        <button key={o.val || "none"} disabled={sevSaving || on}
                          onClick={() => handleSeverity(selected.id, o.val || null)} style={{
                            flex:1, padding:"8px 4px", border:`1.5px solid ${o.color}`, borderRadius:"10px",
                            cursor: on ? "default" : "pointer", fontSize:"12px", fontWeight:"700",
                            background: on ? o.color : o.bg, color: on ? C.white : o.color,
                          }}>{o.label}</button>
                      );
                    })}
                  </div>
                  {sevError && <div style={{ fontSize:"11px", color: C.red, marginTop:"8px" }}>⚠️ {sevError}</div>}
                </div>
              )}

              {canDeleteTrouble && (
                <div style={{ marginBottom:"16px", textAlign:"right" }}>
                  <button onClick={() => handleDeleteTrouble(selected)} style={{
                    padding:"7px 14px", background:"#fff", border:`1.5px solid ${C.red}`, color: C.red,
                    borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"700",
                  }}>🗑️ このトラブル報告を削除</button>
                </div>
              )}

              {[
                ["件名",     selected.title],
                ["種別",     selected.category],
                ["発生日時", new Date(selected.occurred_at).toLocaleString("ja-JP")],
                ["影響範囲", selected.impact_scope || "—"],
              ].map(([label, val]) => (
                <div key={label} style={{ marginBottom:"10px", paddingBottom:"10px", borderBottom:`1px solid ${C.gray2}` }}>
                  <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"3px", fontWeight:"600" }}>{label}</div>
                  <div style={{ fontSize:"13px", color: C.text }}>{val}</div>
                </div>
              ))}

              <div style={{ marginBottom:"10px" }}>
                <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"6px", fontWeight:"600" }}>報告内容</div>
                <div style={{ fontSize:"13px", color: C.text, background: C.gray1, borderRadius:"10px", padding:"10px 12px", lineHeight:"1.6", whiteSpace:"pre-wrap" }}>
                  {selected.description}
                </div>
              </div>

              {/* 写真 */}
              {selected.photos?.length > 0 && (
                <div style={{ marginTop:"10px" }}>
                  <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"8px", fontWeight:"600" }}>📷 添付写真（{selected.photos.length}枚）</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                    {selected.photos.map((p, i) => (
                      <img key={i}
                        src={`${API}/uploads/trouble/${p}`} alt=""
                        onClick={() => setPopupPhoto({ url:`${API}/uploads/trouble/${p}`, name: p, idx: i, list: selected.photos.map(q => ({ url:`${API}/uploads/trouble/${q}`, name: q })) })}
                        style={{ width:"80px", height:"80px", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.gray2}`, cursor:"pointer" }} />
                    ))}
                  </div>
                </div>
              )}

              {/* 対応チャット（投稿は安全管理室・管理職のみ） */}
              <div style={{ marginTop:"18px", paddingTop:"14px", borderTop:`1px solid ${C.gray2}` }}>
                <div style={{ fontSize:"11px", color: C.textSub, marginBottom:"10px", fontWeight:"700" }}>💬 対応チャット</div>
                {(selected.comments || []).length === 0 ? (
                  <div style={{ fontSize:"12px", color: C.gray3, marginBottom:"10px" }}>まだやり取りはありません</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"12px" }}>
                    {selected.comments.map(c => {
                      const mine = c.author_id === (user.userId || user.id);
                      return (
                        <div key={c.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth:"85%" }}>
                          <div style={{ fontSize:"10px", color: C.gray3, marginBottom:"2px", textAlign: mine ? "right" : "left" }}>
                            {c.author_name} · {new Date(c.created_at).toLocaleString("ja-JP", { month:"numeric", day:"numeric", hour:"2-digit", minute:"2-digit" })}
                          </div>
                          <div style={{
                            padding:"8px 12px", borderRadius:"12px", fontSize:"13px", lineHeight:"1.5",
                            whiteSpace:"pre-wrap", wordBreak:"break-word",
                            background: mine ? C.navy : C.gray1, color: mine ? C.white : C.text,
                          }}>{c.body}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {isAdminUser && (
                  <div>
                    <textarea value={commentText} onChange={e => setCommentText(e.target.value)} rows={3}
                      placeholder="対応状況・指示などを入力"
                      style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid ${C.gray2}`, borderRadius:"10px", padding:"8px 10px", fontSize:"13px", fontFamily:"inherit", resize:"vertical", outline:"none" }} />
                    {commentError && <div style={{ fontSize:"11px", color: C.red, marginTop:"4px" }}>⚠️ {commentError}</div>}
                    <button onClick={handleComment} disabled={commentSaving || !commentText.trim()} style={{
                      marginTop:"6px", width:"100%", padding:"9px", border:"none", borderRadius:"10px",
                      background: commentText.trim() ? C.blue : C.gray2, color: commentText.trim() ? C.white : C.gray3,
                      fontSize:"13px", fontWeight:"700", cursor: commentText.trim() ? "pointer" : "default",
                    }}>{commentSaving ? "送信中..." : "送信"}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {popupPhoto && <PhotoLightbox photo={popupPhoto} setPhoto={setPopupPhoto} />}
    </div>
  );
}

// ================================
// KY報告
// ================================
function KYPage({ user, token }) {
  const isMobile = useIsMobile();
  const isAdmin = user.role === "safety_admin" || user.role === "manager";
  const canDeleteReport = user.role === "safety_admin"; // KY報告の削除は安管室のみ
  const DEPTS = DEPARTMENTS_LIST;

  // 共通
  const [tabRaw, setTab] = useStoredState("tfe_ky_tab", isAdmin ? "list" : "report");
  const kyTabs = isAdmin ? ["list","manage"] : ["report","history"];
  const tab = kyTabs.includes(tabRaw) ? tabRaw : kyTabs[0];
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");

  // 報告フォーム（社員・パートナー）
  const initDept = (() => {
    const raw = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
    return DEPARTMENTS.find(d =>
      d === raw || d.replace(/課$/,"") === raw.replace(/課$/,"") ||
      d.includes(raw.replace(/課$/,"")) || raw.includes(d.replace(/課$/,""))
    ) || DEPARTMENTS[0];
  })();
  const isPartner = user.role === "employee_dispatch" || user.role === "partner";
  const [dept, setDept] = useState(initDept);
  const [workTitle, setWorkTitle] = useState(""); // 工事件名
  const [workers, setWorkers] = useState([]);     // 選択した作業員ID[]
  const [userList, setUserList] = useState(null);  // ユーザー一覧（null=未取得）
  const [memberDept, setMemberDept] = useState(initDept); // 作業員絞り込み用の課
  const [extraWorkers, setExtraWorkers] = useState([]); // パートナー用手入力メンバー
  const [extraWorkerInput, setExtraWorkerInput] = useState(""); // 手入力中の名前
  const [selectedCat, setSelectedCat] = useState(null);
  const [catDetail, setCatDetail] = useState({ accidents: [], checks: [] });
  const [shownAccident, setShownAccident] = useState(null); // ランダム表示中の事故事例
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // 一覧（安管室）
  const [reports, setReports] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [kyDateIdx, setKyDateIdx] = useState(0); // 日付ナビ（0=最新）
  const [listLoading, setListLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // 写真ポップアップ
  const [popupPhoto, setPopupPhoto] = useState(null); // {url, name}

  // 管理（安管室）
  const [manageCat, setManageCat] = useState(null);
  const [manageCatDetail, setManageCatDetail] = useState({ accidents: [], checks: [] });
  const [newCatName, setNewCatName] = useState("");
  const [newAccident, setNewAccident] = useState("");
  const [newCheck, setNewCheck] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const loadCategories = async () => {
    const res = await fetch(`${API}/ky_reports.php?type=categories`, { headers });
    const data = await res.json();
    const cats = Array.isArray(data) ? data : [];
    setCategories(cats);
    if (cats.length > 0 && !selectedCat) { setSelectedCat(cats[0]); }
    if (cats.length > 0 && !manageCat) { setManageCat(cats[0]); }
  };

  const loadUsers = async (deptFilter) => {
    try {
      const params = new URLSearchParams({ action: 'members' });
      if (deptFilter) params.append('department', deptFilter);
      const res = await fetch(`${API}/users.php?${params}`, { headers });
      const data = await res.json();
      setUserList(Array.isArray(data) ? data : []);
      setWorkers([]); // 課が変わったら選択リセット
    } catch {
      setUserList([]);
    }
  };

  const pickRandomAccident = (accidents) => {
    if (!accidents || accidents.length === 0) { setShownAccident(null); return; }
    const shuffled = [...accidents].sort(() => Math.random() - 0.5);
    setShownAccident(shuffled.slice(0, 3));
  };

  const loadCatDetail = async (catId, forManage = false) => {
    const res = await fetch(`${API}/ky_reports.php?type=category_detail&category_id=${catId}`, { headers });
    const data = await res.json();
    if (forManage) setManageCatDetail(data);
    else {
      setCatDetail(data);
      pickRandomAccident(data.accidents);
    }
  };

  const loadReports = async () => {
    setListLoading(true);
    const params = new URLSearchParams({ type: 'reports' });
    const res = await fetch(`${API}/ky_reports.php?${params}`, { headers });
    const data = await res.json();
    setReports(Array.isArray(data) ? data : []);
    setListLoading(false);
  };

  useEffect(() => { loadCategories(); loadUsers(initDept); }, []);
  useEffect(() => { if (memberDept) loadUsers(memberDept); }, [memberDept]);
  useEffect(() => { if (selectedCat) loadCatDetail(selectedCat.id); }, [selectedCat]);
  useEffect(() => { if (manageCat) loadCatDetail(manageCat.id, true); }, [manageCat]);
  useEffect(() => {
    if (isAdmin && tab === 'list') { loadReports(); }
    if (!isAdmin && tab === 'history') { loadReports(); }
  }, [tab]);

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    setPhotos(prev => [...prev, ...files].slice(0, 3));
    setPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))].slice(0, 3));
  };

  const removePhoto = (i) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  // 名簿にいない人の手入力欄（社員・パートナー共通）
  const renderExtraWorkers = () => (
    <div style={{ marginTop:"10px" }}>
        {/* 追加メンバー手入力 */}
        <div style={{ fontSize:"11px", color:C.gray3, marginBottom:"6px" }}>その他の作業員を追加</div>
        <div style={{ display:"flex", gap:"8px", marginBottom:"8px" }}>
          <input
            value={extraWorkerInput}
            onChange={e => setExtraWorkerInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && extraWorkerInput.trim()) {
                setExtraWorkers(prev => [...prev, extraWorkerInput.trim()]);
                setExtraWorkerInput("");
              }
            }}
            placeholder="氏名を入力してEnter"
            style={{ ...inputStyle, flex:1, marginBottom:0, fontSize:"12px" }}
            onFocus={e => e.target.style.borderColor = C.blue}
            onBlur={e => e.target.style.borderColor = C.gray2}
          />
          <button onClick={() => {
            if (extraWorkerInput.trim()) {
              setExtraWorkers(prev => [...prev, extraWorkerInput.trim()]);
              setExtraWorkerInput("");
            }
          }} style={{ padding:"8px 14px", borderRadius:"10px", fontSize:"12px", fontWeight:"700",
            background:C.navy, color:C.white, border:"none", cursor:"pointer", whiteSpace:"nowrap" }}>
            追加
          </button>
        </div>
        {extraWorkers.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
            {extraWorkers.map((name, i) => (
              <span key={i} onClick={() => setExtraWorkers(prev => prev.filter((_, idx) => idx !== i))}
                style={{ padding:"5px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                  background:C.green, color:C.white, cursor:"pointer" }}>
                {name} ×
              </span>
            ))}
          </div>
        )}
    </div>
  );

  const handleSubmit = async () => {
    if (!workTitle.trim()) { setError("工事件名を入力してください"); return; }
    if (!dept) { setError("課を選択してください"); return; }
    setSubmitting(true); setError("");
    try {
      const photoData = await Promise.all(photos.map(async f => ({ fileName: f.name, fileData: await fileToBase64(f) })));
      // パートナー：自分＋手入力メンバー、それ以外：IDから名前解決
      let workerNames;
      if (isPartner) {
        workerNames = [user.name, ...extraWorkers].filter(Boolean);
      } else {
        workerNames = [
          ...((userList && userList.length > 0)
            ? workers.map(id => { const u = userList.find(u => String(u.id) === String(id)); return u ? u.name : id; })
            : workers),
          ...extraWorkers,
        ];
      }
      const res = await fetch(`${API}/ky_reports.php?type=report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({
          report_date: new Date().toISOString().split('T')[0],
          department: dept, work_title: workTitle,
          workers: workerNames.join("、"),
          category_id: selectedCat?.id || null,
          category_name: selectedCat?.name || "",
          photos: photoData,
        }),
      });
      if (!res.ok) { const d = await res.json().catch(()=>({})); setError(d.error || "送信に失敗しました"); setSubmitting(false); return; }
      setSent(true);
      setWorkTitle(""); setWorkers([]); setExtraWorkers([]); setExtraWorkerInput(""); setPhotos([]); setPreviews([]);
    } catch { setError("送信に失敗しました"); }
    setSubmitting(false);
  };

  // 管理機能
  // 追加系の共通処理：二重送信を防ぎ、失敗したら理由を表示する（入力欄は成功したときだけ空にする）
  const [kyBusy, setKyBusy] = useState(false);
  const postKy = async (type, payload) => {
    if (kyBusy) return false;
    setKyBusy(true);
    try {
      const res = await fetch(`${API}/ky_reports.php?type=${type}`, { method:"POST", headers:{"Content-Type":"application/json",...headers}, body:JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) { window.alert(`追加に失敗しました${data.error ? `（${data.error}）` : `（通信エラー ${res.status}）`}`); return false; }
      return true;
    } catch { window.alert("追加に失敗しました（サーバーに接続できません）"); return false; }
    finally { setKyBusy(false); }
  };
  const sameText = (list, text) => list.some(x => String(x.content ?? x.name ?? "").trim() === text);

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    if (sameText(categories, name)) { window.alert("同じ名前のカテゴリがすでにあります"); return; }
    if (await postKy("category", { name })) { setNewCatName(""); await loadCategories(); }
  };
  const deleteReport = async (r) => {
    if (!window.confirm(`このKY報告を削除しますか？\n${r.report_date}　${r.department || ''}　${r.work_title || r.work_type || r.category_name || ''}\n（写真も削除されます。元に戻せません）`)) return;
    try {
      const res = await fetch(`${API}/ky_report_delete.php?id=${encodeURIComponent(r.id)}`, { method:"DELETE", headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(data.error || "削除に失敗しました"); return; }
      setExpandedId(null);
      loadReports();
    } catch { window.alert("削除に失敗しました"); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("カテゴリと紐づく事故事例・チェック項目も削除されます。よろしいですか？")) return;
    await fetch(`${API}/ky_reports.php?type=category&id=${id}`, { method:"DELETE", headers });
    await loadCategories();
  };
  const addAccident = async () => {
    const content = newAccident.trim();
    if (!content || !manageCat) return;
    if (sameText(manageCatDetail.accidents, content)) { window.alert("同じ事故事例がすでに登録されています"); return; }
    if (await postKy("accident", { category_id: manageCat.id, content })) { setNewAccident(""); await loadCatDetail(manageCat.id, true); }
  };
  const deleteAccident = async (id) => {
    await fetch(`${API}/ky_reports.php?type=accident&id=${id}`, { method:"DELETE", headers });
    await loadCatDetail(manageCat.id, true);
  };
  const addCheckItem = async () => {
    const content = newCheck.trim();
    if (!content || !manageCat) return;
    if (sameText(manageCatDetail.checks, content)) { window.alert("同じチェック項目がすでに登録されています"); return; }
    if (await postKy("check_item", { category_id: manageCat.id, content })) { setNewCheck(""); await loadCatDetail(manageCat.id, true); }
  };
  const deleteCheckItem = async (id) => {
    await fetch(`${API}/ky_reports.php?type=check_item&id=${id}`, { method:"DELETE", headers });
    await loadCatDetail(manageCat.id, true);
  };

  const tabStyle = (t) => ({
    padding:"8px 16px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
    background: tab === t ? C.navy : C.gray1, color: tab === t ? C.white : C.text,
  });

  return (
    <div style={{ maxWidth:"720px" }}>
      <div style={{ fontSize:"16px", fontWeight:"700", color: C.navy, marginBottom:"16px" }}>📷 KY報告</div>

      {/* タブ */}
      <div style={{ display:"flex", gap:"8px", marginBottom:"16px", flexWrap:"wrap" }}>
        {!isAdmin && <button style={tabStyle("report")} onClick={() => { setTab("report"); setSent(false); }}>報告する</button>}
        {!isAdmin && <button style={tabStyle("history")} onClick={() => setTab("history")}>提出履歴</button>}
        {isAdmin && <button style={tabStyle("list")} onClick={() => setTab("list")}>提出一覧</button>}
        {isAdmin && <button style={tabStyle("manage")} onClick={() => setTab("manage")}>管理</button>}
      </div>

      {/* 報告フォーム */}
      {tab === "report" && !isAdmin && (
        sent ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"36px", marginBottom:"10px" }}>✅</div>
            <div style={{ fontSize:"14px", fontWeight:"600", color:C.green, marginBottom:"16px" }}>KY報告を提出しました！</div>
            <button onClick={() => setSent(false)} style={{ ...primaryBtnStyle(false), width:"auto", padding:"10px 24px" }}>続けて報告する</button>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
            {/* 工事件名 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>📋 工事件名 *</div>
              <input value={workTitle} onChange={e=>setWorkTitle(e.target.value)} style={inputStyle} placeholder="例：NE課 GE-PON新設工事" />
            </div>

            {/* 課 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>🏢 課 *</div>
              <select value={dept} onChange={e=>setDept(e.target.value)} style={inputStyle}>
                <option value="">選択してください</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* 作業員選択 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>👷 作業員（複数選択可）</div>

              {isPartner ? (
                /* パートナー用：自分自身＋手入力 */
                <div>
                  {/* 自分自身（固定） */}
                  <div style={{ marginBottom:"10px" }}>
                    <div style={{ fontSize:"11px", color:C.gray3, marginBottom:"6px" }}>報告者（自動）</div>
                    <span style={{ display:"inline-block", padding:"7px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                      background:C.green, color:C.white }}>
                      ✓ {user.name}
                    </span>
                  </div>
                  {renderExtraWorkers()}
                </div>
              ) : (
                /* TFE社員用：課フィルタ＋メンバー選択 */
                <div>
                  <div style={{ marginBottom:"10px" }}>
                    <div style={{ fontSize:"11px", color:C.gray3, marginBottom:"5px" }}>課を選択</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                      {DEPARTMENTS.map(d => (
                        <button key={d} onClick={() => setMemberDept(d)}
                          style={{ padding:"5px 12px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"11px", fontWeight:"600",
                            background: memberDept === d ? C.navy : C.gray1,
                            color: memberDept === d ? C.white : C.text }}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>

                  {userList === null ? (
                    <div style={{ fontSize:"12px", color:C.gray3, padding:"8px 0" }}>読み込み中...</div>
                  ) : userList.length === 0 ? (
                    <div style={{ fontSize:"12px", color:C.gray3, padding:"8px 0" }}>該当するメンバーがいません</div>
                  ) : (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                      {userList.map(u => {
                        const selected = workers.includes(String(u.id));
                        return (
                          <button key={u.id} onClick={() => setWorkers(prev => selected ? prev.filter(id => id !== String(u.id)) : [...prev, String(u.id)])}
                            style={{ padding:"7px 14px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                              background: selected ? C.green : C.gray2,
                              color: selected ? C.white : C.text }}>
                            {selected ? "✓ " : ""}{u.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {workers.length > 0 && (
                    <div style={{ marginTop:"10px", padding:"8px 12px", background:`${C.green}12`, borderRadius:"10px", display:"flex", alignItems:"center", gap:"6px", flexWrap:"wrap" }}>
                      <span style={{ fontSize:"11px", color:C.green, fontWeight:"700" }}>選択中:</span>
                      {workers.map(id => {
                        const u = (userList||[]).find(u => String(u.id) === id);
                        return (
                          <span key={id} style={{ fontSize:"12px", padding:"3px 10px", background:C.green, color:C.white, borderRadius:"20px", fontWeight:"600", cursor:"pointer" }}
                            onClick={() => setWorkers(prev => prev.filter(wid => wid !== id))}>
                            {u?.name || id} ×
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {renderExtraWorkers()}
                </div>
              )}
            </div>

            {/* 作業カテゴリ */}
            {categories.length > 0 && (
              <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>🔧 作業カテゴリ（任意）</div>
                <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                  <button onClick={() => setSelectedCat(null)}
                    style={{ padding:"7px 14px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                      background: selectedCat === null ? C.gray3 : C.gray1, color: selectedCat === null ? C.white : C.text }}>
                    指定なし
                  </button>
                  {categories.map(cat => (
                    <button key={cat.id} onClick={() => setSelectedCat(cat)}
                      style={{ padding:"7px 14px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                        background: selectedCat?.id === cat.id ? C.blue : C.gray1,
                        color: selectedCat?.id === cat.id ? C.white : C.text }}>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 過去事故事例ビジュアルカード（ランダム3枚） */}
            {selectedCat && catDetail.accidents.length > 0 && shownAccident && (
              <div style={{ background:"linear-gradient(135deg, #FEF5E6 0%, #FDEBD0 100%)", border:`2px solid ${C.amber}40`, borderRadius:"16px", padding:"16px 18px" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"12px" }}>
                  <div style={{ fontSize:"12px", fontWeight:"700", color:C.amber }}>⚠️ 過去の事故事例</div>
                  <button onClick={() => pickRandomAccident(catDetail.accidents)}
                    style={{ padding:"5px 12px", borderRadius:"20px", border:`1px solid ${C.amber}`, background:"transparent", color:C.amber, fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                    🔀 シャッフル
                  </button>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                  {(Array.isArray(shownAccident) ? shownAccident : [shownAccident]).map((a, i) => (
                    <div key={a.id || i} style={{ background:C.white, borderRadius:"12px", padding:"12px 14px", boxShadow:"0 2px 8px rgba(0,0,0,0.08)", display:"flex", gap:"10px", alignItems:"flex-start" }}>
                      <span style={{ fontSize:"18px", flexShrink:0 }}>⛑️</span>
                      <div style={{ fontSize:"13px", color:C.text, lineHeight:"1.6" }}>{a.content}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:"10px", color:C.amber, marginTop:"8px", textAlign:"right" }}>
                  全{catDetail.accidents.length}件の事故事例よりランダム表示
                </div>
              </div>
            )}

            {/* KY用紙写真 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"4px" }}>📷 KY用紙写真（最大3枚）</div>
              <div style={{ fontSize:"11px", color:C.gray3, marginBottom:"12px" }}>KY用紙を撮影してください</div>
              <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                {previews.map((src, i) => (
                  <div key={i} style={{ position:"relative" }}>
                    <img src={src} alt="" style={{ width:"90px", height:"90px", objectFit:"cover", borderRadius:"10px", border:`1px solid ${C.gray2}` }} />
                    <button onClick={() => removePhoto(i)} style={{ position:"absolute", top:"-6px", right:"-6px", width:"22px", height:"22px", borderRadius:"50%", background:C.red, border:"none", color:C.white, fontSize:"12px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                  </div>
                ))}
                {photos.length < 3 && (
                  <label style={{ width:"90px", height:"90px", border:`2px dashed ${C.blue}60`, borderRadius:"10px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.blue, fontSize:"11px", gap:"4px", background:`${C.blue}08` }}>
                    <span style={{ fontSize:"28px" }}>📷</span>
                    <span style={{ fontWeight:"600" }}>撮影・追加</span>
                    <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotoChange} style={{ display:"none" }} />
                  </label>
                )}
              </div>
            </div>

            {error && <div style={{ color:C.red, fontSize:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
            <button onClick={handleSubmit} disabled={submitting} style={primaryBtnStyle(submitting)}>
              {submitting ? "送信中..." : "📤 KY報告を提出する"}
            </button>
          </div>
        )
      )}

      {/* 提出履歴（社員・パートナー） */}
      {tab === "history" && !isAdmin && (
        <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          {listLoading ? (
            <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>読み込み中...</div>
          ) : reports.length === 0 ? (
            <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"32px", marginBottom:"8px" }}>📂</div>
              <div style={{ fontSize:"13px" }}>まだ提出した報告がありません</div>
            </div>
          ) : reports.map(r => (
            <div key={r.id} style={{ background:C.white, borderRadius:"14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                style={{ padding:"14px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:"10px" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"13px", fontWeight:"600", color:C.text }}>{r.report_date}　{r.category_name}</div>
                  <div style={{ fontSize:"11px", color:C.gray3, marginTop:"2px" }}>{r.department}{r.team ? ` ${r.team}` : ""}</div>
                </div>
                <div style={{ fontSize:"12px", color:C.gray3 }}>{expandedId === r.id ? "▲" : "▼"}</div>
              </div>
              {expandedId === r.id && (
                <div style={{ padding:"0 16px 14px", borderTop:`1px solid ${C.gray1}` }}>
                  {(r.work_title || r.work_type) && <div style={{ fontSize:"12px", color:C.gray3, marginTop:"10px" }}>工事件名：{r.work_title || r.work_type}</div>}
                  {r.workers && <div style={{ fontSize:"12px", color:C.gray3, marginTop:"4px" }}>作業員：{r.workers}</div>}
                  {r.photos?.length > 0 && (
                    <div style={{ marginTop:"10px", display:"flex", gap:"8px", flexWrap:"wrap" }}>
                      {r.photos.map((p, i) => (
                        <img key={i} src={`${API}/uploads/ky/${p}`} alt=""
                          onClick={() => setPopupPhoto({ url:`${API}/uploads/ky/${p}`, name: p, idx: i, list: r.photos.map(q => ({ url:`${API}/uploads/ky/${q}`, name: q })) })}
                          style={{ width:"72px", height:"72px", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.gray2}`, cursor:"pointer" }} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}


      {/* 提出一覧（安管室） */}
      {tab === "list" && isAdmin && (() => {
        const deptTabs = ["all", ...Array.from(new Set(reports.map(r => r.department).filter(Boolean))).sort((a,b) => DEPTS.indexOf(a)-DEPTS.indexOf(b))];
        const deptFiltered = filterDept ? reports.filter(r => r.department === filterDept) : reports;
        // 日付一覧（降順）
        const kyDates = [...new Set(deptFiltered.map(r => r.report_date).filter(Boolean))].sort((a,b) => b.localeCompare(a));
        const safeIdx = Math.min(kyDateIdx, Math.max(0, kyDates.length - 1));
        const currentDate = kyDates[safeIdx] || null;
        const dateRecords = currentDate ? deptFiltered.filter(r => r.report_date === currentDate) : [];
        const [m, d2] = currentDate ? currentDate.slice(5).split("-").map(Number) : [null, null];

        return (
          <div>
            {/* 課タブ */}
            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"12px" }}>
              {deptTabs.map(dept => {
                const cnt = dept === "all" ? reports.length : reports.filter(r=>r.department===dept).length;
                const active = dept === "all" ? !filterDept : filterDept === dept;
                return (
                  <button key={dept} onClick={() => { setFilterDept(dept === "all" ? "" : dept); setKyDateIdx(0); }} style={{
                    padding:"5px 12px", borderRadius:"20px", fontSize:"11px", fontWeight:"600",
                    cursor:"pointer", border:"none", transition:"all .15s",
                    background: active ? C.navy : C.white,
                    color:      active ? C.white : C.textSub,
                    boxShadow:  active ? "0 2px 6px rgba(26,42,74,0.2)" : "none",
                    display:"flex", alignItems:"center", gap:"4px",
                  }}>
                    {dept === "all" ? "すべて" : dept}
                    <span style={{ fontSize:"10px", background: active?"rgba(255,255,255,0.2)":"#eee", borderRadius:"10px", padding:"1px 5px" }}>{cnt}</span>
                  </button>
                );
              })}
            </div>

            {/* 日付ナビ */}
            {kyDates.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"14px", marginBottom:"14px", padding:"10px 0", borderTop:`1px solid ${C.gray2}`, borderBottom:`1px solid ${C.gray2}` }}>
                <button onClick={() => setKyDateIdx(i => Math.min(i+1, kyDates.length-1))} disabled={safeIdx >= kyDates.length-1} style={{
                  width:"32px", height:"32px", borderRadius:"50%", border:`1.5px solid ${C.gray2}`,
                  background:C.white, color:C.text, fontSize:"16px", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  opacity: safeIdx >= kyDates.length-1 ? 0.3 : 1,
                }}>‹</button>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:"16px", fontWeight:"700", color:C.text }}>{m}/{d2}</div>
                  <div style={{ fontSize:"11px", color:C.textSub }}>{dateRecords.length}件</div>
                </div>
                <button onClick={() => setKyDateIdx(i => Math.max(i-1, 0))} disabled={safeIdx <= 0} style={{
                  width:"32px", height:"32px", borderRadius:"50%", border:`1.5px solid ${C.gray2}`,
                  background:C.white, color:C.text, fontSize:"16px", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  opacity: safeIdx <= 0 ? 0.3 : 1,
                }}>›</button>
              </div>
            )}

            {listLoading ? (
              <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>読み込み中...</div>
            ) : kyDates.length === 0 ? (
              <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:"32px", marginBottom:"8px" }}>📂</div>
                <div style={{ fontSize:"13px" }}>報告がありません</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                {dateRecords.map(r => (
                  <div key={r.id} style={{ background:C.white, borderRadius:"14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                    <div onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                      style={{ padding:"13px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:"10px" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"13px", fontWeight:"600", color:C.text }}>{r.department}</div>
                        <div style={{ fontSize:"11px", color:C.gray3, marginTop:"2px" }}>
                          {r.work_title || r.work_type || r.category_name}
                          {r.uploader_name && <span style={{ marginLeft:"8px" }}>👤 {r.uploader_name}</span>}
                        </div>
                      </div>
                      <div style={{ fontSize:"12px", color:C.gray3 }}>{expandedId === r.id ? "▲" : "▼"}</div>
                    </div>
                    {expandedId === r.id && (
                      <div style={{ padding:"0 16px 14px", borderTop:`1px solid ${C.gray1}` }}>
                        {(r.work_title || r.work_type) && <div style={{ fontSize:"12px", color:C.gray3, marginTop:"10px" }}>工事件名：{r.work_title || r.work_type}</div>}
                        {r.workers && <div style={{ fontSize:"12px", color:C.gray3, marginTop:"4px" }}>作業員：{r.workers}</div>}
                        {r.photos?.length > 0 && (
                          <div style={{ marginTop:"10px", display:"flex", gap:"8px", flexWrap:"wrap" }}>
                            {r.photos.map((p, i) => (
                              <img key={i} src={`${API}/uploads/ky/${p}`} alt=""
                                onClick={() => setPopupPhoto({ url:`${API}/uploads/ky/${p}`, name: p, idx: i, list: r.photos.map(q => ({ url:`${API}/uploads/ky/${q}`, name: q })) })}
                                style={{ width:"72px", height:"72px", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.gray2}`, cursor:"pointer" }} />
                            ))}
                          </div>
                        )}
                        {canDeleteReport && (
                          <div style={{ marginTop:"12px", textAlign:"right" }}>
                            <button onClick={() => deleteReport(r)} style={{ ...deleteBtnStyle, padding:"6px 14px", fontSize:"12px", fontWeight:"700" }}>🗑️ この報告を削除</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* 管理（安管室） */}
      {tab === "manage" && isAdmin && (
        <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
          {/* カテゴリ管理 */}
          <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>カテゴリ管理</div>
            {categories.map(cat => (
              <div key={cat.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"7px 0", borderBottom:`1px solid ${C.gray1}` }}>
                <span style={{ flex:1, fontSize:"13px", color:C.text }}>{cat.name}</span>
                <button onClick={() => setManageCat(cat)} style={{ ...addBtnStyle, fontSize:"11px", padding:"4px 10px" }}>編集</button>
                <button onClick={() => deleteCategory(cat.id)} style={deleteBtnStyle}>🗑️</button>
              </div>
            ))}
            <div style={{ display:"flex", gap:"8px", marginTop:"10px" }}>
              <input value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="新しいカテゴリ名" style={{ ...inputStyle, flex:1 }} />
              <button onClick={addCategory} disabled={kyBusy} style={{ ...addBtnStyle, opacity: kyBusy ? 0.5 : 1 }}>追加</button>
            </div>
          </div>

          {/* 事故事例・チェック項目管理 */}
          {manageCat && (
            <>
              <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>「{manageCat.name}」の事故事例</div>
                {manageCatDetail.accidents.map(a => (
                  <div key={a.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"7px 0", borderBottom:`1px solid ${C.gray1}` }}>
                    <span style={{ flex:1, fontSize:"12px", color:C.text }}>{a.content}</span>
                    <button onClick={() => deleteAccident(a.id)} style={deleteBtnStyle}>🗑️</button>
                  </div>
                ))}
                <div style={{ display:"flex", gap:"8px", marginTop:"10px" }}>
                  <input value={newAccident} onChange={e=>setNewAccident(e.target.value)} onKeyDown={e=>{ if (e.key==="Enter" && !e.nativeEvent.isComposing) addAccident(); }} placeholder="事故事例を追加" style={{ ...inputStyle, flex:1 }} />
                  <button onClick={addAccident} disabled={kyBusy} style={{ ...addBtnStyle, opacity: kyBusy ? 0.5 : 1 }}>追加</button>
                </div>
              </div>

              <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>「{manageCat.name}」のチェック項目</div>
                {manageCatDetail.checks.map(c => (
                  <div key={c.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"7px 0", borderBottom:`1px solid ${C.gray1}` }}>
                    <span style={{ flex:1, fontSize:"12px", color:C.text }}>{c.content}</span>
                    <button onClick={() => deleteCheckItem(c.id)} style={deleteBtnStyle}>🗑️</button>
                  </div>
                ))}
                <div style={{ display:"flex", gap:"8px", marginTop:"10px" }}>
                  <input value={newCheck} onChange={e=>setNewCheck(e.target.value)} onKeyDown={e=>{ if (e.key==="Enter" && !e.nativeEvent.isComposing) addCheckItem(); }} placeholder="チェック項目を追加" style={{ ...inputStyle, flex:1 }} />
                  <button onClick={addCheckItem} disabled={kyBusy} style={{ ...addBtnStyle, opacity: kyBusy ? 0.5 : 1 }}>追加</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {popupPhoto && <PhotoLightbox photo={popupPhoto} setPhoto={setPopupPhoto} />}
    </div>
  );
}

// ================================
// 賞罰制度ページ（会社ごと・無事故日数）
// ================================
const INCIDENT_TYPES = [
  { value: "minor",        label: "軽微",           color: "#f59e0b", bg: "#fef3c7" },
  { value: "major",        label: "重度",           color: "#ef4444", bg: "#fee2e2" },
  { value: "commendation", label: "表彰",           color: "#16a34a", bg: "#dcfce7" },
];

// 端末のローカル日付（JST）で YYYY-MM-DD を返す
const localDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "2026-06-03" → "6月3日"（今年以外は年も付ける）
const fmtStartDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const md = `${m}月${d}日`;
  return y === new Date().getFullYear() ? md : `${y}年${md}`;
};

function IncidentPage({ token, user, onOpenTrouble }) {
  const isAdmin = user?.role === 'safety_admin' || user?.role === 'manager';
  const authH = { 'Authorization': `Bearer ${token}` };
  const jsonH = { ...authH, 'Content-Type': 'application/json' };

  const [tabRaw, setTab]     = useStoredState("tfe_inc_tab", "list");   // "list" | "free" | "input"
  const tab = ["list","free","input"].includes(tabRaw) ? tabRaw : "list";
  const [data, setData]       = useState({ me: null, records: [], accidentFree: [], deptFree: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [notice, setNotice]   = useState("");
  const [month, setMonth]     = useState(() => localDateStr().slice(0, 7)); // "" = 全期間
  const [filterType, setFilterType] = useState("all");
  const [filterOrg, setFilterOrg]   = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [search, setSearch]         = useState("");
  const [freeSearch, setFreeSearch] = useState("");
  const [freeEdit, setFreeEdit]     = useState({});   // { 組織ID: 編集中の日付 }
  const [savingFree, setSavingFree] = useState("");
  const [form, setForm] = useState({
    organizationId: "", department: "", type: "commendation",
    occurred_at: localDateStr(), description: "",
  });
  const [saving, setSaving] = useState(false);

  const call = async (path, options) => {
    const res = await fetch(`${API}/${path}`, options);
    let json = {};
    try { json = await res.json(); } catch (e) { /* ignore */ }
    if (!res.ok) throw new Error(json.error || `エラーが発生しました (${res.status})`);
    return json;
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true); // 登録・削除・保存のあとの再読み込みは画面を差し替えない
    setError("");
    try {
      const q = month ? `?month=${month}` : "";
      const json = await call(`incident.php${q}`, { headers: authH });
      setData({
        me:           json.me || null,
        records:      Array.isArray(json.records) ? json.records : [],
        accidentFree: Array.isArray(json.accidentFree) ? json.accidentFree : [],
        deptFree:     Array.isArray(json.deptFree) ? json.deptFree : [],
      });
    } catch (e) {
      setError(e.message || "読み込みに失敗しました");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [month]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await call('incident.php', { method: 'POST', headers: jsonH, body: JSON.stringify(form) });
      setNotice("✅ 登録しました");
      setForm(p => ({ ...p, department: "", description: "" }));
      const m = form.occurred_at.slice(0, 7);
      if (month && m !== month) setMonth(m); else await load(true);
      setTimeout(() => { setNotice(""); setTab("list"); }, 1200);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この記録を削除しますか？')) return;
    try {
      await call(`incident.php?id=${encodeURIComponent(id)}`, { method: 'DELETE', headers: authH });
      await load(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const saveFree = async (item, value) => {
    setSavingFree(item.key);
    setError("");
    try {
      await call('incident.php', {
        method: 'PATCH', headers: jsonH,
        body: JSON.stringify({ organizationId: item.orgId, department: item.dept, lastAccidentDate: value || null }),
      });
      setFreeEdit(p => { const n = { ...p }; delete n[item.key]; return n; });
      await load(true);
    } catch (err) {
      setError(err.message);
    }
    setSavingFree("");
  };

  const { me, records, accidentFree, deptFree } = data;
  const myFree = accidentFree.find(a => a.organizationId === me?.organizationId);
  // 「あなたは○○です」: パートナーは会社名のみ、それ以外は所属課のみ
  const isPartnerUser = user?.role === 'partner';
  const myLabel = isPartnerUser
    ? (me?.organizationName || "")
    : ((me?.departments || []).join(" / ") || me?.organizationName || "");
  // 課の無事故日数があればそれを、なければ会社の日数を表示
  const myDeptFree = isPartnerUser ? null
    : deptFree.find(d => d.organizationId === me?.organizationId && (me?.departments || []).includes(d.department));
  const bannerFree = myDeptFree || myFree;
  const typeInfo = (v) => INCIDENT_TYPES.find(t => t.value === v) || INCIDENT_TYPES[0];

  // 課の絞り込み候補（表示中の記録に出てくる課）
  const deptOptions = Array.from(new Set(records.map(r => r.department).filter(Boolean))).sort();

  const kw = search.trim().toLowerCase();
  const baseFiltered = records.filter(r => {
    if (isAdmin && filterOrg !== "all" && r.organizationId !== filterOrg) return false;
    if (filterDept !== "all" && (r.department || "") !== filterDept) return false;
    if (isAdmin && kw) {
      const hay = `${r.organizationName || ""} ${r.department || ""} ${r.description || ""}`.toLowerCase();
      if (!hay.includes(kw)) return false;
    }
    return true;
  });
  const filtered = baseFiltered.filter(r => filterType === "all" || r.type === filterType);

  // 会社ごとにまとめる（組織マスタの並び順）
  const groups = accidentFree
    .map(af => ({ af, items: filtered.filter(r => r.organizationId === af.organizationId) }))
    .filter(g => g.items.length > 0);
  const knownIds = new Set(accidentFree.map(a => a.organizationId));
  const orphans = filtered.filter(r => !knownIds.has(r.organizationId));
  if (orphans.length > 0) {
    groups.push({
      af: { organizationId: "", organizationName: "（会社不明）", days: null, lastAccidentDate: null },
      items: orphans,
    });
  }

  const headerFree = (af) => {
    if (filterDept === "all") return af;
    return deptFree.find(d => d.organizationId === af.organizationId && d.department === filterDept)
      || { days: null, lastAccidentDate: null };
  };

  // 無事故日数の管理一覧: 会社ごとの行 + 自社(TFE)の課ごとの行
  const freeKw = freeSearch.trim().toLowerCase();
  const freeItems = [];
  accidentFree.forEach(a => {
    const nameHit = !freeKw || (a.organizationName || "").toLowerCase().includes(freeKw);
    const depts = a.organizationId === me?.organizationId ? DEPARTMENTS_LIST : [];
    const deptHits = depts.filter(d => !freeKw || nameHit || d.toLowerCase().includes(freeKw));
    if (nameHit || deptHits.length > 0) {
      freeItems.push({ key: `${a.organizationId}|`, orgId: a.organizationId, dept: "", name: a.organizationName, af: a });
    }
    deptHits.forEach(d => freeItems.push({
      key: `${a.organizationId}|${d}`, orgId: a.organizationId, dept: d, name: d,
      af: deptFree.find(x => x.organizationId === a.organizationId && x.department === d) || { lastAccidentDate: null, days: null },
    }));
  });

  const inputStyle = {
    width: "100%", border: "1.5px solid #e2e8f0", borderRadius: "8px",
    padding: "8px 10px", fontSize: "14px", fontFamily: "inherit",
    outline: "none", background: "#fff", color: "#0f172a", boxSizing: "border-box",
  };
  const smallInput = { ...inputStyle, width: "auto", fontSize: "13px", padding: "6px 10px" };
  const labelStyle = { fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "4px", display: "block" };
  const pillBtn = (active) => ({
    background: active ? C.blue : "#f1f5f9", color: active ? "#fff" : "#475569",
    border: "none", borderRadius: "20px", padding: "6px 14px",
    fontSize: "13px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit",
  });

  // 無事故日数の表示
  const DaysBadge = ({ af, size }) => {
    const big = size === "big";
    if (!af || af.days === null || af.days === undefined) {
      return <span style={{ fontSize: big ? "13px" : "11px", color: big ? "#cbd5e1" : "#94a3b8" }}>無事故日数 未設定</span>;
    }
    return (
      <span style={{ color: big ? "#fff" : "#16a34a", fontWeight: "800", whiteSpace: "nowrap" }}>
        <span style={{ fontSize: big ? "13px" : "11px", fontWeight: "700" }}>無事故 </span>
        <span style={{ fontSize: big ? "34px" : "16px", lineHeight: 1 }}>{af.days.toLocaleString()}</span>
        <span style={{ fontSize: big ? "13px" : "11px", fontWeight: "700" }}> 日</span>
      </span>
    );
  };

  return (
    <div>
      {/* ヘッダー */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", gap: "8px" }}>
        <span style={{ fontWeight: "700", fontSize: "15px", color: C.text }}>インセンティブ＆ペナルティー</span>
        {isAdmin && (
          <button type="button"
            onClick={() => { setTab(tab === "input" ? "list" : "input"); setError(""); }}
            style={{
              background: tab === "input" ? "#64748b" : C.blue,
              color: "#fff", border: "none", borderRadius: "10px",
              padding: "8px 16px", fontSize: "13px", fontWeight: "700",
              cursor: "pointer", fontFamily: "inherit",
            }}>
            {tab === "input" ? "← 一覧に戻る" : "+ 表彰を登録"}
          </button>
        )}
      </div>

      {/* あなたの会社 + 無事故日数 */}
      <div style={{
        background: "linear-gradient(135deg,#0f2540,#1e4a7a)", color: "#fff",
        borderRadius: "16px", padding: "16px 18px", marginBottom: "14px",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "18px", fontWeight: "800", wordBreak: "break-all" }}>
            {myLabel || (loading ? "…" : "未設定")}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <DaysBadge af={bannerFree} size="big" />
          {bannerFree?.lastAccidentDate && (
            <div style={{ fontSize: "11px", color: "#cbd5e1", marginTop: "4px" }}>{fmtStartDate(bannerFree.lastAccidentDate)}～</div>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", fontSize: "13px", fontWeight: "600" }}>
          {error}
        </div>
      )}

      {/* 管理者タブ */}
      {isAdmin && tab !== "input" && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <button type="button" style={pillBtn(tab === "list")} onClick={() => setTab("list")}>記録</button>
          <button type="button" style={pillBtn(tab === "free")} onClick={() => setTab("free")}>無事故日数の管理</button>
        </div>
      )}

      {/* 入力フォーム（安管室のみ） */}
      {tab === "input" && isAdmin && (
        <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "16px", padding: "20px", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "16px", color: "#0f2540", marginTop: 0 }}>表彰の登録</h3>
          <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>※ 重度・軽微はトラブル一覧から振り分けます</div>
          {notice && (
            <div style={{ background: "#dcfce7", color: "#16a34a", borderRadius: "8px", padding: "10px", marginBottom: "12px", fontWeight: "700", textAlign: "center" }}>
              {notice}
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div>
              <label style={labelStyle}>会社 *</label>
              <select value={form.organizationId} onChange={e => setForm(p => ({ ...p, organizationId: e.target.value }))} required style={inputStyle}>
                <option value="">選択してください</option>
                {accidentFree.map(a => <option key={a.organizationId} value={a.organizationId}>{a.organizationName}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>課（任意）</label>
              <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))} style={inputStyle}>
                <option value="">（会社全体）</option>
                {DEPARTMENTS_LIST.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>発生日</label>
              <input type="date" value={form.occurred_at}
                onChange={e => setForm(p => ({ ...p, occurred_at: e.target.value }))}
                required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>内容</label>
              <textarea rows={4} value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="詳細を入力してください"
                style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <button type="submit" disabled={saving} style={{
              background: C.blue, color: "#fff", border: "none", borderRadius: "10px",
              padding: "10px", fontSize: "14px", fontWeight: "700",
              cursor: saving ? "default" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1,
            }}>
              {saving ? "登録中..." : "登録する"}
            </button>
          </form>
        </div>
      )}

      {/* 無事故日数の管理（安管室のみ） */}
      {tab === "free" && isAdmin && (
        <>
          <input type="search" value={freeSearch} onChange={e => setFreeSearch(e.target.value)}
            placeholder="🔍 会社名・課で検索" style={{ ...inputStyle, marginBottom: "12px" }} />
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>読み込み中...</div>
          ) : freeItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", background: "#f8fafc", borderRadius: "16px", fontSize: "14px" }}>
              該当する会社・課がありません
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {freeItems.map(it => {
                const isDept = it.dept !== "";
                const edited = freeEdit[it.key];
                const value = edited !== undefined ? edited : (it.af.lastAccidentDate || "");
                const dirty = edited !== undefined && edited !== (it.af.lastAccidentDate || "");
                return (
                  <div key={it.key} style={{
                    background: isDept ? "#f8fafc" : "#fff", border: "1.5px solid #e2e8f0", borderRadius: "12px",
                    padding: "12px 14px", marginLeft: isDept ? "18px" : 0,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <span style={{ fontSize: isDept ? "13px" : "14px", fontWeight: "700", color: "#0f172a", minWidth: 0, wordBreak: "break-all" }}>
                        {isDept ? `└ ${it.name}` : `🏢 ${it.name}`}
                      </span>
                      <DaysBadge af={it.af} />
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>無事故開始日</span>
                      <input type="date" value={value} max={localDateStr()}
                        onChange={e => setFreeEdit(p => ({ ...p, [it.key]: e.target.value }))}
                        style={smallInput} />
                      <button type="button" disabled={!dirty || savingFree === it.key}
                        onClick={() => saveFree(it, value)}
                        style={{
                          background: dirty ? C.blue : "#e2e8f0", color: dirty ? "#fff" : "#94a3b8",
                          border: "none", borderRadius: "8px", padding: "6px 14px",
                          fontSize: "13px", fontWeight: "700", cursor: dirty ? "pointer" : "default", fontFamily: "inherit",
                        }}>
                        {savingFree === it.key ? "保存中..." : "保存"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "10px" }}>
            無事故開始日（事故のあった日、または集計を始める日）を登録すると、今日までの「無事故○日」が自動で数えられます。事故が起きたら日付を更新してください。
          </div>
        </>
      )}

      {/* 記録一覧 */}
      {tab === "list" && (
        <>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px", alignItems: "center" }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={smallInput} />
            {month && (
              <button type="button" onClick={() => setMonth("")} style={{ ...pillBtn(false), padding: "6px 12px" }}>全期間</button>
            )}
            {isAdmin && (
              <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)} style={smallInput}>
                <option value="all">すべての会社</option>
                {accidentFree.map(a => <option key={a.organizationId} value={a.organizationId}>{a.organizationName}</option>)}
              </select>
            )}
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={smallInput}>
              <option value="all">すべての課</option>
              {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {isAdmin && (
            <input type="search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="🔍 会社名・課・内容で検索" style={{ ...inputStyle, marginBottom: "12px" }} />
          )}

          {/* 区分ボタン（押すと絞り込み） */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
            {[{ value: "all", label: "すべて", color: "#0f2540", bg: "#e2e8f0" }, ...INCIDENT_TYPES].map(t => {
              const cnt = t.value === "all" ? baseFiltered.length : baseFiltered.filter(r => r.type === t.value).length;
              const active = filterType === t.value;
              return (
                <button type="button" key={t.value} onClick={() => setFilterType(t.value)} style={{
                  background: active ? t.color : t.bg, border: `1.5px solid ${t.color}`,
                  borderRadius: "20px", padding: "6px 16px", cursor: "pointer", fontFamily: "inherit",
                  fontSize: "13px", fontWeight: "700", color: active ? "#fff" : t.color,
                }}>
                  {t.label}：{cnt}件
                </button>
              );
            })}
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>読み込み中...</div>
          ) : groups.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", background: "#f8fafc", borderRadius: "16px" }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏅</div>
              <div style={{ fontSize: "14px" }}>この条件の記録はありません</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              {groups.map(g => (
                <div key={g.af.organizationId || "unknown"}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px",
                    padding: "6px 4px", marginBottom: "8px", borderBottom: "2px solid #0f2540",
                  }}>
                    <span style={{ fontSize: "14px", fontWeight: "800", color: "#0f2540", minWidth: 0, wordBreak: "break-all" }}>
                      🏢 {g.af.organizationName}
                      <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", marginLeft: "8px" }}>{g.items.length}件</span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                      {filterDept !== "all" && <span style={{ fontSize: "11px", color: "#64748b" }}>{filterDept}</span>}
                      <DaysBadge af={headerFree(g.af)} />
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {g.items.map(r => {
                      const t = typeInfo(r.type);
                      return (
                        <div key={r.id}
                          onClick={r.troubleId && onOpenTrouble ? () => onOpenTrouble(r.troubleId) : undefined}
                          style={{
                          background: "#fff", border: "1.5px solid #e2e8f0",
                          borderRadius: "12px", padding: "14px 16px",
                          display: "flex", gap: "12px", alignItems: "flex-start",
                          cursor: r.troubleId && onOpenTrouble ? "pointer" : "default",
                        }}>
                          <span style={{
                            background: t.bg, color: t.color, border: `1px solid ${t.color}`,
                            borderRadius: "6px", padding: "2px 8px",
                            fontSize: "11px", fontWeight: "700", flexShrink: 0,
                          }}>{t.label}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "2px" }}>
                              {r.department || "会社全体"} · {r.occurredAt}{r.troubleId ? " · トラブル報告 ›" : ""}
                            </div>
                            <div style={{ fontSize: "14px", color: "#0f172a", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.description}</div>
                          </div>
                          {isAdmin && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }} style={{
                              background: "none", border: "none", color: "#94a3b8", cursor: "pointer",
                              fontSize: "12px", fontFamily: "inherit", flexShrink: 0,
                            }}>削除</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ================================
// 安全メール
// ================================
const MAIL_CATEGORIES = ["事故速報","安全周知","注意喚起","その他"];
const DEPTS_MAIL = ["本社","各部","モバイル","社フラ","NE","東京","ICT","HS"];

function SafetyMailPage({ user, token }) {
  const [tabRaw, setTab] = useStoredState("tfe_mail_tab", "compose");
  const tab = ["compose","history"].includes(tabRaw) ? tabRaw : "compose";
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [category, setCategory] = useState(MAIL_CATEGORIES[0]);
  const [targetType, setTargetType] = useState("all"); // all | dept | individual
  const [targetDepts, setTargetDepts] = useState([]);   // 複数課チェックボックス
  const [users, setUsers] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);   // 添付ファイル
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { sent_count, total }
  const [error, setError] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const loadUsers = async () => {
    const res = await fetch(`${API}/users.php`, { headers });
    const data = await res.json();
    setUsers(Array.isArray(data) ? data : []);
  };

  const loadHistory = async () => {
    setHistLoading(true);
    try {
      const res = await fetch(`${API}/safety_mail.php`, { headers });
      const data = await res.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch {}
    setHistLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

  // ファイルドロップ処理
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };
  const handleFileInput = (e) => {
    addFiles(Array.from(e.target.files));
  };
  const addFiles = (files) => {
    const valid = files.filter(f => f.size <= 10 * 1024 * 1024); // 10MB制限
    if (valid.length < files.length) setError("10MBを超えるファイルはスキップされました");
    setAttachments(prev => [...prev, ...valid]);
  };
  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  // 課チェックボックス
  const toggleDept = (dept) => {
    setTargetDepts(prev => prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]);
  };

  // 個人チェックボックス
  const toggleUser = (id) => {
    setTargetUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  };

  const handleSend = async () => {
    if (!subject.trim()) { setError("件名を入力してください"); return; }
    if (!body.trim()) { setError("本文を入力してください"); return; }
    if (targetType === "dept" && targetDepts.length === 0) { setError("課を1つ以上選択してください"); return; }
    if (targetType === "individual" && targetUsers.length === 0) { setError("送信先を1人以上選択してください"); return; }
    setSending(true); setError("");

    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("target_type", targetType);
      if (targetType === "dept") {
        fd.append("target_label", targetDepts.join(","));
      } else if (targetType === "individual") {
        fd.append("target_label", targetUsers.join(","));
      }
      fd.append("subject", subject.trim());
      fd.append("body", body.trim());
      attachments.forEach((f, i) => fd.append(`attachment_${i}`, f));

      const res = await fetch(`${API}/safety_mail.php`, {
        method: "POST",
        headers,   // Content-Typeは自動設定（FormData）
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "送信に失敗しました"); setSending(false); return; }
      setSent({ sent_count: data.sent_count, total: data.total });
      setSubject(""); setBody(""); setTargetUsers([]); setTargetDepts([]); setAttachments([]);
    } catch { setError("送信に失敗しました"); }
    setSending(false);
  };

  const tabStyle = (t) => ({
    padding:"7px 16px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
    background: tab === t ? C.navy : C.gray1, color: tab === t ? C.white : C.text,
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + "B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + "KB";
    return (bytes / 1024 / 1024).toFixed(1) + "MB";
  };

  return (
    <div style={{ maxWidth:"720px" }}>
      <div style={{ fontSize:"16px", fontWeight:"700", color:C.navy, marginBottom:"16px" }}>📧 安全メール</div>

      <div style={{ display:"flex", gap:"8px", marginBottom:"16px" }}>
        <button style={tabStyle("compose")} onClick={() => { setTab("compose"); setSent(null); }}>作成・送信</button>
        <button style={tabStyle("history")} onClick={() => setTab("history")}>送信履歴</button>
      </div>

      {tab === "compose" && (
        sent ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"36px", marginBottom:"10px" }}>✅</div>
            <div style={{ fontSize:"14px", fontWeight:"600", color:C.green, marginBottom:"4px" }}>送信しました！</div>
            <div style={{ fontSize:"12px", color:C.gray3, marginBottom:"20px" }}>{sent.total}人中 {sent.sent_count}人に送信完了</div>
            <button onClick={() => setSent(null)} style={{ ...primaryBtnStyle(false), width:"auto", padding:"10px 24px" }}>続けて送信する</button>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>

            {/* カテゴリ */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>カテゴリ</div>
              <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                {MAIL_CATEGORIES.map(c => (
                  <button key={c} onClick={() => setCategory(c)} style={{
                    padding:"6px 14px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                    background: category === c ? C.blue : C.gray1,
                    color: category === c ? C.white : C.text,
                  }}>{c}</button>
                ))}
              </div>
            </div>

            {/* 宛先 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>宛先</div>
              <div style={{ display:"flex", gap:"8px", marginBottom:"12px" }}>
                {[["all","全員"],["dept","課・部門別"],["individual","個人指定"]].map(([val,label]) => (
                  <button key={val} onClick={() => setTargetType(val)} style={{
                    padding:"6px 14px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                    background: targetType === val ? C.navy : C.gray1,
                    color: targetType === val ? C.white : C.text,
                  }}>{label}</button>
                ))}
              </div>

              {/* 課チェックボックス（複数選択） */}
              {targetType === "dept" && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                  {DEPTS_MAIL.map(d => (
                    <div key={d} onClick={() => toggleDept(d)} style={{
                      display:"flex", alignItems:"center", gap:"6px", padding:"7px 12px", borderRadius:"10px",
                      background: targetDepts.includes(d) ? "#EBF3FC" : C.gray1,
                      border: `1.5px solid ${targetDepts.includes(d) ? C.blue : "transparent"}`,
                      cursor:"pointer", userSelect:"none",
                    }}>
                      <div style={{
                        width:"16px", height:"16px", borderRadius:"4px", flexShrink:0,
                        border:`2px solid ${targetDepts.includes(d) ? C.blue : C.gray3}`,
                        background: targetDepts.includes(d) ? C.blue : "transparent",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        {targetDepts.includes(d) && <span style={{ color:C.white, fontSize:"10px", fontWeight:"700" }}>✓</span>}
                      </div>
                      <span style={{ fontSize:"13px", color:C.text, fontWeight:targetDepts.includes(d)?"600":"400" }}>{d}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* 個人指定チェックボックス */}
              {targetType === "individual" && (
                <div style={{ display:"flex", flexDirection:"column", gap:"6px", maxHeight:"200px", overflowY:"auto" }}>
                  {users.filter(u => u.role !== "safety_admin").map(u => (
                    <div key={u.id} onClick={() => toggleUser(u.id)}
                      style={{ display:"flex", alignItems:"center", gap:"10px", padding:"8px 10px", borderRadius:"8px",
                        background: targetUsers.includes(u.id) ? "#EBF3FC" : C.gray1, cursor:"pointer" }}>
                      <div style={{ width:"18px", height:"18px", borderRadius:"4px",
                        border:`2px solid ${targetUsers.includes(u.id) ? C.blue : C.gray2}`,
                        background: targetUsers.includes(u.id) ? C.blue : "transparent",
                        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {targetUsers.includes(u.id) && <span style={{ color:C.white, fontSize:"11px", fontWeight:"700" }}>✓</span>}
                      </div>
                      <span style={{ fontSize:"13px", color:C.text }}>{u.name}</span>
                      <span style={{ fontSize:"11px", color:C.gray3 }}>{u.departments || ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 件名・本文 */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <Row label="件名 *">
                <input value={subject} onChange={e=>setSubject(e.target.value)} style={inputStyle} placeholder="例：【事故速報】○○現場での転落事故について" />
              </Row>
              <Row label="本文 *">
                <textarea value={body} onChange={e=>setBody(e.target.value)} style={{ ...inputStyle, height:"160px", resize:"none" }}
                  placeholder="本文を入力してください" />
              </Row>
            </div>

            {/* 添付ファイル */}
            <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"12px", fontWeight:"600", color:C.navy, marginBottom:"10px" }}>添付ファイル（PDF・画像等、各10MB以内）</div>

              {/* ドロップゾーン */}
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('mail-file-input').click()}
                style={{
                  border:`2px dashed ${dragOver ? C.blue : C.gray2}`,
                  borderRadius:"12px",
                  padding:"20px",
                  textAlign:"center",
                  cursor:"pointer",
                  background: dragOver ? "#EBF3FC" : C.gray1,
                  transition:"all 0.2s",
                  marginBottom: attachments.length > 0 ? "10px" : "0",
                }}
              >
                <div style={{ fontSize:"24px", marginBottom:"6px" }}>📎</div>
                <div style={{ fontSize:"12px", color:C.gray3 }}>ここにファイルをドロップ、またはクリックして選択</div>
                <input
                  id="mail-file-input"
                  type="file"
                  multiple
                  style={{ display:"none" }}
                  onChange={handleFileInput}
                />
              </div>

              {/* 添付ファイルリスト */}
              {attachments.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                  {attachments.map((f, idx) => (
                    <div key={idx} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"8px 10px", background:C.gray1, borderRadius:"8px" }}>
                      <span style={{ fontSize:"16px" }}>
                        {f.type.startsWith("image/") ? "🖼️" : f.name.endsWith(".pdf") ? "📄" : "📎"}
                      </span>
                      <span style={{ fontSize:"12px", color:C.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                      <span style={{ fontSize:"11px", color:C.gray3, flexShrink:0 }}>{formatFileSize(f.size)}</span>
                      <button onClick={() => removeAttachment(idx)} style={{ border:"none", background:"transparent", cursor:"pointer", color:C.gray3, fontSize:"14px", padding:"2px 4px", lineHeight:1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div style={{ color:C.red, fontSize:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
            <button onClick={handleSend} disabled={sending} style={primaryBtnStyle(sending)}>
              {sending ? "送信中..." : "📤 送信する"}
            </button>
          </div>
        )
      )}

      {tab === "history" && (
        histLoading ? (
          <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>読み込み中...</div>
        ) : history.length === 0 ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"32px", marginBottom:"8px" }}>📭</div>
            <div style={{ fontSize:"13px" }}>まだ送信履歴がありません</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            {history.map(m => (
              <div key={m.id} style={{ background:C.white, borderRadius:"14px", padding:"14px 16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                  <span style={{ fontSize:"11px", fontWeight:"600", padding:"2px 8px", borderRadius:"20px", background:"#EBF3FC", color:C.blue }}>{m.category}</span>
                  <span style={{ fontSize:"11px", color:C.gray3 }}>{m.target_label || "全員"}</span>
                  <span style={{ fontSize:"11px", color:C.gray3, marginLeft:"auto" }}>{m.created_at ? new Date(m.created_at).toLocaleString("ja-JP") : ""}</span>
                </div>
                <div style={{ fontSize:"13px", fontWeight:"600", color:C.text, marginBottom:"4px" }}>{m.subject}</div>
                <div style={{ fontSize:"12px", color:C.gray3, whiteSpace:"pre-wrap" }}>{m.body}</div>
                {m.sent_count > 0 && <div style={{ fontSize:"11px", color:C.green, marginTop:"6px" }}>✓ {m.sent_count}人に送信済み</div>}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ================================
// 改善メモ
// ================================
const STATUS_CONFIG = {
  pending:  { label: "未対応", color: C.gray3,  bg: C.gray1 },
  accepted: { label: "採用",   color: "#1A6630", bg: "#EAF3DE" },
  review:   { label: "検討中", color: "#854F0B", bg: "#FEF5E6" },
  rejected: { label: "不採用", color: "#A32D2D", bg: "#FCEBEB" },
};

// リロードしても選んでいたタブ・切替を保つ（sessionStorage）
function useStoredState(key, init) {
  const [v, setV] = useState(() => {
    try { const x = sessionStorage.getItem(key); return x !== null ? x : init; } catch (e) { return init; }
  });
  const set = (n) => { setV(n); try { sessionStorage.setItem(key, n); } catch (e) {} };
  return [v, set];
}

function ImprovementPage({ user, token }) {
  const isAdmin = user.role === "safety_admin";
  const [tabRaw, setTab] = useStoredState("tfe_imp_tab", isAdmin ? "list" : "post");
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({ total:0, accepted:0, review:0, rejected:0, pending:0 });
  const [rankPeriod, setRankPeriod] = useStoredState('tfe_imp_period', 'fiscal'); // 'fiscal' | 'month'
  const [rankBy, setRankBy] = useStoredState('tfe_imp_rankby', 'accepted');          // 'accepted'（採用件数）| 'posts'（提出件数）
  const [rankMonth, setRankMonth] = useState(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [rewardEdit, setRewardEdit] = useState({}); // {id: amount}

  const headers = { Authorization: `Bearer ${token}` };
  const validTabs = isAdmin ? ["list", "ranking"] : ["post", "mine", "ranking"];
  const tab = validTabs.includes(tabRaw) ? tabRaw : validTabs[0];

  const load = async (silent = false) => {
    if (!silent) setLoading(true); // 操作後の再読み込みは画面を差し替えない（スクロール位置を保つ）
    try {
      const res = await fetch(`${API}/improvement.php`, { headers });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setIdeas(list);
      setCounts({
        total:    list.length,
        accepted: list.filter(i => i.status === "accepted").length,
        review:   list.filter(i => i.status === "review").length,
        rejected: list.filter(i => i.status === "rejected").length,
        pending:  list.filter(i => i.status === "pending").length,
      });
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    if (!title.trim()) { setError("タイトルを入力してください"); return; }
    if (!content.trim()) { setError("内容を入力してください"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await fetch(`${API}/improvement.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ title: title.trim(), content: content.trim() }),
      });
      if (!res.ok) { setError("送信に失敗しました"); setSubmitting(false); return; }
      setTitle(""); setContent(""); setSent(true);
      await load(true);
    } catch { setError("送信に失敗しました"); }
    setSubmitting(false);
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API}/improvement.php?id=${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({ status }),
    });
    await load(true);
  };

  const handleLike = async (id) => {
    await fetch(`${API}/improvement.php?action=like&id=${id}`, {
      method: 'POST', headers,
    });
    await load(true);
  };

  const updateReward = async (id, amount) => {
    await fetch(`${API}/improvement.php?id=${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({ reward_amount: parseInt(amount)||0 }),
    });
    setRewardEdit(p => { const n={...p}; delete n[id]; return n; });
    await load(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この投稿を削除しますか？')) return;
    await fetch(`${API}/improvement.php?id=${id}`, { method: 'DELETE', headers });
    await load(true);
  };

  // ランキング計算
  const getRankingData = (period = rankPeriod, month = rankMonth) => {
    const today = new Date();
    const cm = today.getMonth()+1;
    const cy = today.getFullYear();
    const fiscalStart = cm>=10 ? `${cy}-10` : `${cy-1}-10`;

    const filtered = ideas.filter(i => {
      if (!i.created_at) return false;
      const ym = i.created_at.slice(0,7);
      if (period === 'month') return ym === month;
      return ym >= fiscalStart;
    });

    const map = {};
    filtered.forEach(i => {
      const id = String(i.submitted_by);
      const name = i.submitter_name || (id === String(user.userId) ? user.name : '') || '不明';
      if (!map[id]) map[id] = { id, name, posts:0, accepted:0, reward:0 };
      map[id].posts++;
      if (i.status === 'accepted') {
        map[id].accepted++;
        map[id].reward += parseInt(i.reward_amount)||0;
      }
    });
    // 提出件数 / 採用件数 のどちらで並べるか（同数なら、もう一方の件数→報奨金の順）
    return Object.values(map).sort((a,b) => rankBy === 'posts'
      ? (b.posts - a.posts || b.accepted - a.accepted || b.reward - a.reward)
      : (b.accepted - a.accepted || b.posts - a.posts || b.reward - a.reward));
  };

  const MEDAL = ['🥇','🥈','🥉'];

  // 自分の順位と報奨金を大きく見せるカード
  const renderHero = (data, label) => {
    const idx = data.findIndex(r => String(r.id) === String(user.userId));
    const me = idx >= 0 ? data[idx] : null;
    const rank = idx >= 0 ? idx + 1 : null;
    return (
      <div style={{ background:"linear-gradient(135deg,#1a2a4a 0%,#185FA5 55%,#2b86d9 100%)", borderRadius:"20px", padding:"20px 22px", marginBottom:"16px",
        color:"#fff", boxShadow:"0 8px 24px rgba(24,95,165,0.28)", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", right:"-30px", top:"-30px", width:"140px", height:"140px", borderRadius:"50%", background:"rgba(255,255,255,0.08)" }} />
        <div style={{ fontSize:"11px", fontWeight:"700", opacity:0.85, marginBottom:"10px", letterSpacing:"0.05em" }}>{label}</div>
        <div style={{ display:"flex", gap:"20px", alignItems:"flex-end", flexWrap:"wrap", position:"relative" }}>
          <div style={{ flex:"1 1 160px" }}>
            <div style={{ fontSize:"11px", opacity:0.8 }}>💰 あなたの報奨金</div>
            <div style={{ fontSize:"38px", fontWeight:"800", lineHeight:1.15, fontVariantNumeric:"tabular-nums" }}>¥{(me ? me.reward : 0).toLocaleString()}</div>
          </div>
          <div style={{ flex:"0 0 auto", textAlign:"center", background:"rgba(255,255,255,0.15)", borderRadius:"14px", padding:"8px 18px" }}>
            <div style={{ fontSize:"11px", opacity:0.85 }}>{rankBy === 'posts' ? '提出件数' : '採用件数'}の順位</div>
            <div style={{ fontSize:"30px", fontWeight:"800", lineHeight:1.2 }}>
              {rank ? <>{rank <= 3 ? MEDAL[rank-1] : ''}{rank}<span style={{ fontSize:"15px" }}>位</span></> : <span style={{ fontSize:"18px" }}>圏外</span>}
            </div>
            <div style={{ fontSize:"10px", opacity:0.8 }}>{data.length}人中</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:"16px", marginTop:"12px", fontSize:"12px", fontWeight:"600", position:"relative" }}>
          <span>📝 提出 {me ? me.posts : 0}件</span>
          <span>✅ 採用 {me ? me.accepted : 0}件</span>
        </div>
      </div>
    );
  };

  const myIdeas = ideas.filter(i => String(i.submitted_by) === String(user.userId));
  // 自分の今期（10月〜9月）の報奨金
  const myReward = (() => {
    const t = new Date();
    const fy = t.getMonth() + 1 >= 10 ? `${t.getFullYear()}-10` : `${t.getFullYear() - 1}-10`;
    return myIdeas
      .filter(i => i.status === 'accepted' && (i.created_at || '').slice(0, 7) >= fy)
      .reduce((sum, i) => sum + (parseInt(i.reward_amount) || 0), 0);
  })();

  const tabStyle = (t) => ({
    padding:"7px 16px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
    background: tab === t ? C.navy : C.gray1, color: tab === t ? C.white : C.text,
  });

  const Badge = ({ status }) => {
    const s = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:s.bg, color:s.color, whiteSpace:"nowrap" }}>{s.label}</span>;
  };

  return (
    <div style={{ maxWidth:"720px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"16px" }}>
        <div style={{ fontSize:"16px", fontWeight:"700", color:C.navy }}>💡 改善メモ</div>
        {/* 件数バッジ */}
        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
          {isAdmin ? (
            <>
              <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:C.gray1, color:C.text }}>全{counts.total}件</span>
              {counts.accepted > 0 && <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:"#EAF3DE", color:"#1A6630" }}>採用{counts.accepted}</span>}
              {counts.review > 0 && <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:"#FEF5E6", color:"#854F0B" }}>検討中{counts.review}</span>}
              {counts.pending > 0 && <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:C.gray1, color:C.gray3 }}>未対応{counts.pending}</span>}
            </>
          ) : (
            <>
              {myIdeas.length > 0 && <span style={{ fontSize:"11px", fontWeight:"600", padding:"3px 10px", borderRadius:"20px", background:C.gray1, color:C.text }}>自分の投稿{myIdeas.length}件</span>}
              {myReward > 0 && <span style={{ fontSize:"11px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background:"#FEF3C7", color:"#B45309" }}>💰 今期の報奨金 ¥{myReward.toLocaleString()}</span>}
            </>
          )}
        </div>
      </div>

      {/* 自分の順位・報奨金（投稿/自分の投稿タブ。ランキングタブは下で期間に合わせて表示） */}
      {!isAdmin && tab !== "ranking" && renderHero(getRankingData('fiscal'), '今期（10月〜9月）の成績')}

      {/* タブ */}
      <div style={{ display:"flex", gap:"8px", marginBottom:"16px", flexWrap:"wrap" }}>
        {!isAdmin && <button style={tabStyle("post")} onClick={() => { setTab("post"); setSent(false); }}>投稿する</button>}
        {!isAdmin && <button style={tabStyle("mine")} onClick={() => setTab("mine")}>自分の投稿</button>}
        {isAdmin && <button style={tabStyle("list")} onClick={() => setTab("list")}>一覧</button>}
        <button style={tabStyle("ranking")} onClick={() => setTab("ranking")}>🏆 ランキング</button>
      </div>

      {/* 投稿フォーム */}
      {tab === "post" && !isAdmin && (
        sent ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"36px", marginBottom:"10px" }}>✅</div>
            <div style={{ fontSize:"14px", fontWeight:"600", color:C.green, marginBottom:"16px" }}>送信しました！</div>
            <button onClick={() => setSent(false)} style={{ ...primaryBtnStyle(false), width:"auto", padding:"10px 24px" }}>続けて投稿する</button>
          </div>
        ) : (
          <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <Row label="タイトル *">
              <input value={title} onChange={e=>setTitle(e.target.value)} style={inputStyle} placeholder="例：KY報告をもっと簡単に" />
            </Row>
            <Row label="内容 *">
              <textarea value={content} onChange={e=>setContent(e.target.value)} style={{ ...inputStyle, height:"100px", resize:"none" }} placeholder="改善してほしい内容を具体的に書いてください" />
            </Row>
            {error && <div style={{ color:C.red, fontSize:"12px", marginBottom:"10px", padding:"8px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
            <button onClick={handleSubmit} disabled={submitting} style={primaryBtnStyle(submitting)}>
              {submitting ? "送信中..." : "送信する"}
            </button>
          </div>
        )
      )}

      {/* 自分の投稿 */}
      {tab === "mine" && !isAdmin && (
        loading ? <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>読み込み中...</div>
        : myIdeas.length === 0 ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"32px", marginBottom:"8px" }}>📝</div>
            <div style={{ fontSize:"13px" }}>まだ投稿がありません</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            {myIdeas.map(idea => (
              <div key={idea.id} style={{ background:C.white, borderRadius:"14px", padding:"14px 16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"6px" }}>
                  <div style={{ fontSize:"13px", fontWeight:"600", color:C.text, flex:1, marginRight:"8px" }}>{idea.title}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:"6px", flexShrink:0 }}>
                    <Badge status={idea.status} />
                    {idea.status === 'pending' && (
                      <button onClick={() => handleDelete(idea.id)} style={{
                        fontSize:"11px", padding:"3px 8px", borderRadius:"8px",
                        border:`1px solid ${C.red}`, background:"transparent",
                        color:C.red, cursor:"pointer", lineHeight:"1.4",
                      }}>削除</button>
                    )}
                  </div>
                </div>
                <div style={{ fontSize:"12px", color:C.gray3, marginBottom:"4px" }}>{idea.content}</div>
                <div style={{ fontSize:"11px", color:C.gray3, display:"flex", alignItems:"center", gap:"10px" }}>
                  <span>{idea.created_at ? new Date(idea.created_at).toLocaleDateString("ja-JP") : ""}</span>
                  {idea.status === 'accepted' && parseInt(idea.reward_amount) > 0 && (
                    <span style={{ fontWeight:"700", color:"#B45309", background:"#FEF3C7", padding:"1px 8px", borderRadius:"10px" }}>💰 ¥{Number(idea.reward_amount).toLocaleString()}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* 一覧（安管室） */}
      {tab === "list" && isAdmin && (
        loading ? <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>読み込み中...</div>
        : (
          <div>
            {/* 集計サマリー */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:"10px", marginBottom:"14px" }}>
              {[
                { label:"総投稿数", val:counts.total,    color:C.navy,    bg:C.gray1 },
                { label:"採用",     val:counts.accepted,  color:"#1A6630", bg:"#EAF3DE" },
                { label:"検討中",   val:counts.review,    color:"#854F0B", bg:"#FEF5E6" },
                { label:"未対応",   val:counts.pending,   color:C.gray3,   bg:C.gray1 },
              ].map((s,i) => (
                <div key={i} style={{ background:s.bg, borderRadius:"14px", padding:"14px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:"10px", color:s.color, fontWeight:"600", marginBottom:"4px" }}>{s.label}</div>
                  <div style={{ fontSize:"28px", fontWeight:"700", color:s.color }}>{s.val}</div>
                </div>
              ))}
            </div>
            {ideas.length === 0 ? (
              <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:"32px", marginBottom:"8px" }}>📝</div>
                <div style={{ fontSize:"13px" }}>まだ投稿がありません</div>
              </div>
            ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            {ideas.map(idea => (
              <div key={idea.id} style={{ background:C.white, borderRadius:"14px", padding:"14px 16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"6px" }}>
                  <div style={{ fontSize:"13px", fontWeight:"600", color:C.text }}>{idea.title}</div>
                  <Badge status={idea.status} />
                </div>
                <div style={{ fontSize:"12px", color:C.gray3, marginBottom:"6px" }}>{idea.content}</div>
                <div style={{ fontSize:"11px", color:C.gray3, marginBottom:"10px" }}>
                  {idea.submitter_name || "不明"}　{idea.created_at ? new Date(idea.created_at).toLocaleDateString("ja-JP") : ""}
                </div>
                <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", alignItems:"center" }}>
                  {["accepted","review","rejected"].map(s => (
                    <button key={s} onClick={() => updateStatus(idea.id, s)}
                      style={{ fontSize:"11px", padding:"4px 12px", borderRadius:"20px", border:`1px solid ${STATUS_CONFIG[s].color}`,
                        background: idea.status === s ? STATUS_CONFIG[s].bg : "transparent",
                        color: STATUS_CONFIG[s].color, cursor:"pointer", fontWeight: idea.status === s ? "600" : "400" }}>
                      {STATUS_CONFIG[s].label}
                    </button>
                  ))}
                  <button onClick={() => handleLike(idea.id)} style={{
                    marginLeft:"auto", fontSize:"12px", padding:"4px 12px", borderRadius:"20px",
                    border:"none", cursor:"pointer",
                    background: idea.liked_by_me ? C.red+'18' : C.gray1,
                    color: idea.liked_by_me ? C.red : C.gray3, fontWeight:"600",
                  }}>
                    {idea.liked_by_me ? '❤️' : '🤍'} {idea.like_count || 0}
                  </button>
                  <button onClick={() => handleDelete(idea.id)} style={{
                    fontSize:"11px", padding:"4px 10px", borderRadius:"20px",
                    border:`1px solid ${C.red}`, background:"transparent",
                    color:C.red, cursor:"pointer", fontWeight:"600",
                  }}>🗑️ 削除</button>
                </div>
                {idea.status === 'accepted' && (
                  <div style={{ marginTop:"8px", display:"flex", alignItems:"center", gap:"6px" }}>
                    <span style={{ fontSize:"11px", color:C.textSub }}>💰 報奨金</span>
                    {rewardEdit[idea.id] !== undefined ? (
                      <>
                        <input type="number" value={rewardEdit[idea.id]}
                          onChange={e => setRewardEdit(p=>({...p,[idea.id]:e.target.value}))}
                          style={{ width:"90px", padding:"3px 8px", borderRadius:"8px", border:`1.5px solid ${C.navy}`, fontSize:"12px" }} />
                        <span style={{ fontSize:"11px", color:C.textSub }}>円</span>
                        <button onClick={() => updateReward(idea.id, rewardEdit[idea.id])}
                          style={{ fontSize:"11px", padding:"3px 10px", borderRadius:"8px", background:C.navy, color:"#fff", border:"none", cursor:"pointer" }}>保存</button>
                        <button onClick={() => setRewardEdit(p=>{ const n={...p}; delete n[idea.id]; return n; })}
                          style={{ fontSize:"11px", padding:"3px 8px", borderRadius:"8px", background:C.gray1, color:C.textSub, border:"none", cursor:"pointer" }}>✕</button>
                      </>
                    ) : (
                      <button onClick={() => setRewardEdit(p=>({...p,[idea.id]: idea.reward_amount||0}))}
                        style={{ fontSize:"11px", padding:"3px 10px", borderRadius:"8px", background:"#EAF3DE", color:"#1A6630", border:"none", cursor:"pointer", fontWeight:"600" }}>
                        {idea.reward_amount ? `¥${Number(idea.reward_amount).toLocaleString()}` : '金額を設定'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
            )}
          </div>
        )
      )}

      {/* ランキングタブ */}
      {tab === "ranking" && (() => {
        const rankData = getRankingData();
        return (
          <div>
            {!isAdmin && renderHero(rankData, rankPeriod === 'month' ? `${rankMonth.replace('-', '年')}月の成績` : '今期（10月〜9月）の成績')}
            {/* 期間切替 */}
            <div style={{ display:"flex", gap:"8px", alignItems:"center", marginBottom:"16px", flexWrap:"wrap" }}>
              <div style={{ display:"flex", background:C.gray1, borderRadius:"20px", padding:"3px" }}>
                {[['fiscal','今期'],['month','月別']].map(([v,l])=>(
                  <button key={v} type="button" onClick={()=>setRankPeriod(v)}
                    style={{ padding:"5px 16px", borderRadius:"18px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                      background: rankPeriod===v ? C.navy : "transparent",
                      color: rankPeriod===v ? "#fff" : C.textSub, transition:"all .15s" }}>
                    {l}
                  </button>
                ))}
              </div>
              {rankPeriod==='month' && (
                <input type="month" value={rankMonth} onChange={e=>setRankMonth(e.target.value)}
                  style={{ padding:"5px 10px", borderRadius:"10px", border:`1.5px solid ${C.gray2}`, fontSize:"12px" }} />
              )}
            </div>

            {/* 提出件数 / 採用件数 の切替 */}
            <div style={{ display:"flex", background:C.gray1, borderRadius:"20px", padding:"3px", marginBottom:"16px", width:"fit-content" }}>
              {[['posts','📝 提出件数'],['accepted','✅ 採用件数']].map(([v,l])=>(
                <button key={v} type="button" onClick={()=>setRankBy(v)}
                  style={{ padding:"5px 16px", borderRadius:"18px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                    background: rankBy===v ? C.navy : "transparent",
                    color: rankBy===v ? "#fff" : C.textSub, transition:"all .15s" }}>
                  {l}
                </button>
              ))}
            </div>

            {/* ランキングリスト */}
            {rankData.length === 0 ? (
              <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3 }}>
                <div style={{ fontSize:"32px", marginBottom:"8px" }}>🏆</div>
                <div style={{ fontSize:"13px" }}>まだデータがありません</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                {rankData.map((r,i) => {
                  const isMe = String(r.id) === String(user.userId);
                  const isTop = i === 0;
                  return (
                    <div key={r.id} style={{
                      background: isTop ? "linear-gradient(135deg,#fffbeb,#fef3c7)" : isMe ? "#EEF4FF" : C.white,
                      border: isTop ? "2px solid #F59E0B" : isMe ? `2px solid ${C.blue}` : `1px solid ${C.gray2}`,
                      borderRadius:"14px", padding:"12px 16px",
                      display:"flex", alignItems:"center", gap:"12px",
                      boxShadow: isTop ? "0 4px 16px rgba(245,158,11,0.2)" : "0 2px 8px rgba(0,0,0,0.05)",
                    }}>
                      {/* 順位 */}
                      <div style={{ fontSize: i<3?"24px":"16px", fontWeight:"700", minWidth:"32px", textAlign:"center",
                        color: i===0?"#F59E0B":i===1?"#9CA3AF":i===2?"#CD7F32":C.textSub }}>
                        {i<3 ? MEDAL[i] : `${i+1}位`}
                      </div>
                      {/* 名前 */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, display:"flex", alignItems:"center", gap:"5px" }}>
                          {r.name}
                          {isMe && <span style={{ fontSize:"10px", background:C.blue, color:"#fff", borderRadius:"10px", padding:"1px 7px" }}>あなた</span>}
                        </div>
                        <div style={{ display:"flex", gap:"10px", marginTop:"4px", flexWrap:"wrap" }}>
                          <span style={{ fontSize:"11px", color: rankBy==='posts' ? C.text : C.textSub, fontWeight: rankBy==='posts' ? "700" : "400" }}>📝 提出 {r.posts}件</span>
                          <span style={{ fontSize:"11px", color:"#1A6630", fontWeight: (rankBy==='accepted' || r.accepted>0) ? "700" : "400" }}>✅ 採用 {r.accepted}件</span>
                        </div>
                      </div>
                      {/* 報奨金 */}
                      <div style={{ textAlign:"right", flexShrink:0 }}>
                        {r.reward > 0 ? (
                          <>
                            <div style={{ fontSize:"16px", fontWeight:"700", color:i===0?"#F59E0B":C.navy }}>¥{r.reward.toLocaleString()}</div>
                            <div style={{ fontSize:"10px", color:C.textSub }}>獲得</div>
                          </>
                        ) : (
                          <div style={{ fontSize:"11px", color:C.gray3 }}>—</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
}


// ================================
// 新規入場者教育報告
// ================================
const DEPARTMENTS_LIST = ["本社(総務経理課)", "各部", "NE課", "東京営業所", "ホームソリューション課", "ICTエンジニアリング課", "第一エンジニアリング課", "第二エンジニアリング課", "安全管理室"];

// ================================
// 新規入場者教育報告 ── 階層構造版
// 課 → 元請け → 現場(project) → 教育記録 → 詳細
// ================================
// ================================
// 教育記録詳細ビュー（写真ポップアップ付き）
// ================================
function EduDetailView({ selEdu, photos, members, onBack, onDelete, C, card, backBtn }) {
  const [popupPhoto, setPopupPhoto] = useState(null); // {url, name}

  const handleSave = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'photo';
    a.target = '_blank';
    a.click();
  };

  return (
    <div>
      {backBtn('現場に戻る', onBack)}
      {card(<>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"14px" }}>
          <div>
            <div style={{ fontSize:"15px", fontWeight:"700", color:C.text }}>{selEdu.project_name}</div>
            {selEdu.contractor_name && <div style={{ fontSize:"12px", color:C.textSub, marginTop:"2px" }}>元請け：{selEdu.contractor_name}</div>}
            <div style={{ fontSize:"12px", color:C.textSub, marginTop:"4px", fontWeight:"600" }}>教育実施日：{selEdu.edu_date}</div>
            {selEdu.department && <div style={{ display:"inline-block", marginTop:"6px", padding:"2px 8px", borderRadius:"10px", background:C.navy+'15', color:C.navy, fontSize:"11px", fontWeight:"600" }}>{selEdu.department}</div>}
          </div>
          <button onClick={onDelete} style={{ background:"#FFF0F0", border:"none", borderRadius:"8px", padding:"6px 10px", cursor:"pointer", fontSize:"12px", color:C.red, fontWeight:"600" }}>削除</button>
        </div>

        {photos.length > 0 && (
          <div style={{ marginBottom:"16px" }}>
            <div style={{ fontSize:"12px", color:C.textSub, marginBottom:"8px", fontWeight:"600" }}>📷 教育書類写真（{photos.length}枚）</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
              {photos.map((p,i) => (
                <img key={i} src={p.url} alt={p.name}
                  onClick={() => setPopupPhoto({ ...p, idx: i, list: photos })}
                  style={{ width:"130px", height:"98px", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.gray2}`, cursor:"pointer" }} />
              ))}
            </div>
          </div>
        )}

        {members.length > 0 && (
          <div style={{ marginBottom:"14px" }}>
            <div style={{ fontSize:"12px", color:C.textSub, marginBottom:"8px", fontWeight:"600" }}>👷 入場者（{members.length}名）</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
              {members.map((m,i) => (
                <span key={i} style={{ fontSize:"12px", padding:"5px 12px", borderRadius:"20px", background: m.user_id ? C.navy+'15' : C.gray2, color: m.user_id ? C.navy : C.textSub, fontWeight:"600" }}>
                  {m.name}{!m.user_id && ' (外部)'}
                </span>
              ))}
            </div>
          </div>
        )}

        {selEdu.memo && (
          <div>
            <div style={{ fontSize:"12px", color:C.textSub, marginBottom:"4px", fontWeight:"600" }}>メモ</div>
            <div style={{ fontSize:"13px", color:C.text, lineHeight:"1.6", whiteSpace:"pre-wrap" }}>{selEdu.memo}</div>
          </div>
        )}
      </>)}

      {popupPhoto && <PhotoLightbox photo={popupPhoto} setPhoto={setPopupPhoto} />}
    </div>
  );
}

function NewEntryEducationPage({ token, user }) {
  const isAdmin = user.role === 'safety_admin' || user.role === 'manager';
  const H = { Authorization: `Bearer ${token}` };
  const api = (qs) => fetch(`${API}/new_entry_education.php?${qs}`, { headers: H }).then(r => r.json());

  // ── ナビ状態 ──────────────────────────────
  // view: 'projects' | 'project_detail' | 'edu_detail' | 'new_project' | 'new_edu'
  const [view,           setView]           = useState('projects');
  const [filterDept,     setFilterDept]     = useState(() => {
    if (isAdmin) return '';
    const raw = Array.isArray(user.departments) && user.departments.length > 0 ? user.departments[0] : "";
    return DEPARTMENTS_LIST.find(d =>
      d === raw ||
      d.includes(raw.replace(/課$/, "")) ||
      raw.includes(d.replace(/課$/, ""))
    ) || '';
  });
  const [filterContr,    setFilterContr]    = useState('');
  const [filterStatus,   setFilterStatus]   = useState('active'); // 'active'|'all'|'ended'
  const [searchMember,   setSearchMember]   = useState(''); // 人名検索
  const [contractors,    setContractors]    = useState([]);
  const [projects,       setProjects]       = useState([]);
  const [selProject,     setSelProject]     = useState(null); // project + educations[]
  const [selEdu,         setSelEdu]         = useState(null); // edu detail
  const [userList,       setUserList]       = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');

  // ── 新規プロジェクトフォーム ──────────────
  const blankProj = { department:'', contractor_name:'', project_name:'', contract_name:'', start_date:'', end_date:'' };
  const [projForm,      setProjForm]      = useState(blankProj);
  const [editingProjId, setEditingProjId] = useState(null); // null=新規, id=編集
  const [savingProj, setSavingProj] = useState(false);
  const [projError,  setProjError]  = useState('');

  // ── 新規教育記録フォーム ──────────────────
  const [eduForm,         setEduForm]         = useState({ edu_date: new Date().toISOString().slice(0,10), memo:'' });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [freeNames,       setFreeNames]       = useState(['']);
  const [photoFiles,      setPhotoFiles]      = useState([]);
  const [savingEdu,       setSavingEdu]       = useState(false);
  const [eduError,        setEduError]        = useState('');
  const [eduSuccess,      setEduSuccess]      = useState(false);
  const [memberTab,       setMemberTab]       = useState('all'); // 'all' | dept名 | 'partner'
  const [memberCo,        setMemberCo]        = useState('');    // パートナータブで選んだ会社名

  // ── データ読み込み ────────────────────────
  const loadProjects = () => {
    setLoading(true);
    let qs = 'path=projects';
    if (filterDept)    qs += `&dept=${encodeURIComponent(filterDept)}`;
    if (filterContr)   qs += `&contractor=${encodeURIComponent(filterContr)}`;
    if (searchMember)  qs += `&member=${encodeURIComponent(searchMember)}`;
    api(qs).then(d => { setProjects(Array.isArray(d.projects) ? d.projects : []); setLoading(false); })
           .catch(() => setLoading(false));
  };
  const loadContractors = () => {
    let qs = 'path=contractors';
    if (filterDept) qs += `&dept=${encodeURIComponent(filterDept)}`;
    api(qs).then(d => setContractors(Array.isArray(d.contractors) ? d.contractors : []));
  };
  const loadUsers = () => {
    api('path=users').then(d => setUserList(Array.isArray(d.users) ? d.users : [])).catch(() => {});
  };
  const loadProjectDetail = (id) => {
    setLoading(true);
    api(`path=project_detail&id=${id}`)
      .then(d => { setSelProject(d); setView('project_detail'); setLoading(false); })
      .catch(() => setLoading(false));
  };
  const loadEduDetail = (id) => {
    setLoading(true);
    api(`path=edu_detail&id=${id}`)
      .then(d => { setSelEdu(d); setView('edu_detail'); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadProjects(); loadContractors(); loadUsers(); }, [filterDept, filterContr, searchMember]);

  // ── 削除 ─────────────────────────────────
  const deleteProject = async (id) => {
    if (!window.confirm('この現場と全ての教育記録を削除しますか？')) return;
    await fetch(`${API}/new_entry_education.php?path=project&id=${id}`, { method:'DELETE', headers:H });
    setView('projects'); setSelProject(null); loadProjects();
  };
  const deleteEdu = async (id) => {
    if (!window.confirm('この教育記録を削除しますか？')) return;
    await fetch(`${API}/new_entry_education.php?path=education&id=${id}`, { method:'DELETE', headers:H });
    setView('project_detail'); setSelEdu(null);
    loadProjectDetail(selProject.id);
  };

  // ── 新規プロジェクト送信 / 編集 ──────────────────
  const submitProject = async (e) => {
    e.preventDefault(); setSavingProj(true); setProjError('');
    try {
      const method = editingProjId ? 'PUT' : 'POST';
      const body   = editingProjId ? JSON.stringify({ ...projForm, id: editingProjId }) : JSON.stringify(projForm);
      const res = await fetch(`${API}/new_entry_education.php?path=project`, {
        method, headers:{ ...H, 'Content-Type':'application/json' }, body,
      });
      const data = await res.json();
      if (!res.ok) { setProjError(data.error||'保存に失敗しました'); setSavingProj(false); return; }
      setProjForm(blankProj); setEditingProjId(null); loadProjects();
      if (editingProjId) {
        // 編集後は現場詳細を再ロードして戻る
        await loadProjectDetail(editingProjId);
      } else {
        setView('projects');
      }
    } catch { setProjError('サーバーに接続できません'); }
    setSavingProj(false);
  };

  // ── 新規教育記録送信 ──────────────────────
  const toggleMember = (uid, name) =>
    setSelectedMembers(p => p.find(m=>m.user_id===uid) ? p.filter(m=>m.user_id!==uid) : [...p, {user_id:uid,name}]);
  const updateFreeName = (i, v) => setFreeNames(p => { const a=[...p]; a[i]=v; return a; });

  const submitEdu = async (e) => {
    e.preventDefault(); setSavingEdu(true); setEduError(''); setEduSuccess(false);
    try {
      const fd = new FormData();
      fd.append('project_id', selProject.id);
      fd.append('edu_date',   eduForm.edu_date);
      fd.append('memo',       eduForm.memo);
      fd.append('members',    JSON.stringify(selectedMembers));
      fd.append('free_names', JSON.stringify(freeNames.filter(n=>n.trim()!=='')));
      photoFiles.forEach(f => fd.append('photos[]', f));
      const res = await fetch(`${API}/new_entry_education.php?path=education`, {
        method:'POST', headers:H, body:fd,
      });
      const data = await res.json();
      if (!res.ok) { setEduError(data.error||'送信に失敗しました'); setSavingEdu(false); return; }
      setEduSuccess(true);
      setEduForm({ edu_date: new Date().toISOString().slice(0,10), memo:'' });
      setSelectedMembers([]); setFreeNames(['']); setPhotoFiles([]);
      loadProjectDetail(selProject.id); setView('project_detail');
    } catch { setEduError('サーバーに接続できません'); }
    setSavingEdu(false);
  };

  // ── ヘルパーUI ────────────────────────────
  const chip = (label, active, onClick) => (
    <div onClick={onClick} style={{
      padding:"4px 10px", borderRadius:"16px", fontSize:"11px", fontWeight:"600", cursor:"pointer",
      background: active ? C.navy : C.gray2, color: active ? C.white : C.textSub,
    }}>{label}</div>
  );
  const backBtn = (label, onClick) => (
    <button onClick={onClick} style={{ background:"none", border:"none", color:C.blue, cursor:"pointer", fontSize:"13px", fontWeight:"600", marginBottom:"12px", padding:"0" }}>← {label}</button>
  );
  const card = (children, extra={}) => (
    <div style={{ background:C.white, borderRadius:"16px", padding:"16px 18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"12px", ...extra }}>{children}</div>
  );

  // ════════════════════════════════════════
  // VIEW: edu_detail（教育記録詳細）
  // ════════════════════════════════════════
  if (view === 'edu_detail' && selEdu) {
    const photos  = Array.isArray(selEdu.photos)  ? selEdu.photos  : [];
    const members = Array.isArray(selEdu.members) ? selEdu.members : [];
    return (
      <EduDetailView
        selEdu={selEdu}
        photos={photos}
        members={members}
        onBack={() => { setView('project_detail'); setSelEdu(null); }}
        onDelete={() => deleteEdu(selEdu.id)}
        C={C}
        card={card}
        backBtn={backBtn}
      />
    );
  }

  // ════════════════════════════════════════
  // VIEW: new_edu（教育記録追加フォーム）
  // ════════════════════════════════════════
  if (view === 'new_edu' && selProject) {
    return (
      <div>
        {backBtn('現場に戻る', () => { setView('project_detail'); setEduError(''); setEduSuccess(false); })}
        <div style={{ fontSize:"14px", fontWeight:"700", color:C.text, marginBottom:"12px" }}>
          📝 教育記録追加：{selProject.project_name}
        </div>
        <form onSubmit={submitEdu}>
          {card(<>
            <Row label="教育実施日 *">
              <input type="date" value={eduForm.edu_date} required
                onChange={e=>setEduForm(p=>({...p,edu_date:e.target.value}))} style={inputStyle} />
            </Row>

            {/* 写真 */}
            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>📷 教育書類写真（複数可）</label>
              <label style={{ display:"block", padding:"14px", border:`2px dashed ${C.gray2}`, borderRadius:"12px", textAlign:"center", cursor:"pointer", color:C.textSub, fontSize:"12px" }}>
                <input type="file" accept="image/*,application/pdf" multiple style={{ display:"none" }}
                  onChange={e=>setPhotoFiles(p=>[...p,...Array.from(e.target.files)])} />
                📷 タップして写真・PDFを選択
              </label>
              {photoFiles.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginTop:"8px" }}>
                  {photoFiles.map((f,i) => (
                    <div key={i} style={{ position:"relative" }}>
                      {f.type.startsWith('image/') ? (
                        <img src={URL.createObjectURL(f)} alt="" style={{ width:"80px", height:"60px", objectFit:"cover", borderRadius:"6px", border:`1px solid ${C.gray2}` }} />
                      ) : (
                        <div style={{ width:"80px", height:"60px", borderRadius:"6px", border:`1px solid ${C.gray2}`, background:C.gray1, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", color:C.textSub, textAlign:"center", padding:"4px" }}>📄 {f.name.slice(0,12)}</div>
                      )}
                      <div onClick={()=>setPhotoFiles(p=>p.filter((_,idx)=>idx!==i))}
                        style={{ position:"absolute", top:"-6px", right:"-6px", background:C.red, color:C.white, borderRadius:"50%", width:"18px", height:"18px", fontSize:"11px", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontWeight:"700" }}>×</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 登録ユーザー */}
            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>👷 入場者（社員・パートナー）</label>
              {/* タブ */}
              {(() => {
                // パートナー社名一覧（partners）
                const partners = userList
                  .filter(u => u.role === 'partner')
                  .reduce((acc, u) => {
                    const co = u.organization_name || 'その他';
                    if (!acc.includes(co)) acc.push(co);
                    return acc;
                  }, []);
                const tabs = ['all', ...DEPARTMENTS, 'partner'];
                const filteredUsers = memberTab === 'all'
                  ? userList.filter(u => u.role !== 'partner')
                  : memberTab === 'partner'
                    ? userList.filter(u => u.role === 'partner')
                    : userList.filter(u => {
                        const depts = Array.isArray(u.departments) ? u.departments : (u.departments ? [u.departments] : []);
                        return u.role !== 'partner' && depts.includes(memberTab);
                      });
                // パートナータブのとき社名グループ別に表示
                const partnerGroups = memberTab === 'partner'
                  ? filteredUsers.reduce((acc, u) => {
                      const co = u.organization_name || 'その他';
                      if (!acc[co]) acc[co] = [];
                      acc[co].push(u);
                      return acc;
                    }, {})
                  : null;
                return (<>
                  <div style={{ display:"flex", gap:"4px", flexWrap:"wrap", marginBottom:"8px" }}>
                    {tabs.map(t => (
                      <div key={t} onClick={() => setMemberTab(t)} style={{
                        padding:"4px 10px", borderRadius:"16px", fontSize:"10px", fontWeight:"600", cursor:"pointer",
                        background: memberTab === t ? C.navy : C.gray2,
                        color: memberTab === t ? C.white : C.textSub,
                      }}>{t === 'all' ? 'すべて（社員）' : t === 'partner' ? 'パートナー' : t}</div>
                    ))}
                  </div>
                  {partnerGroups ? (
                    <div>
                      <div style={{ fontSize:"11px", fontWeight:"700", color:C.textSub, marginBottom:"5px" }}>会社を選んでください</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginBottom:"10px" }}>
                        {Object.keys(partnerGroups).sort().map(co => {
                          const cnt = partnerGroups[co].filter(u => selectedMembers.find(m => m.user_id === u.id)).length;
                          const on = memberCo === co;
                          return (
                            <div key={co} onClick={() => setMemberCo(on ? '' : co)} style={{
                              padding:"6px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"700", cursor:"pointer",
                              border:`1.5px solid ${on ? C.navy : C.gray2}`,
                              background: on ? C.navy : C.white, color: on ? C.white : C.text,
                            }}>{co}{cnt > 0 ? `（${cnt}）` : ''}</div>
                          );
                        })}
                      </div>
                      {memberCo && partnerGroups[memberCo] ? (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                          {partnerGroups[memberCo].map(u => {
                            const sel = selectedMembers.find(m=>m.user_id===u.id);
                            return (
                              <div key={u.id} onClick={()=>toggleMember(u.id, u.name)} style={{
                                padding:"6px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                                cursor:"pointer", border:`1.5px solid ${sel?C.amber:C.gray2}`,
                                background: sel?C.amber:C.white, color: sel?C.white:C.textSub, transition:"all .15s",
                              }}>{u.name}</div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ fontSize:"12px", color:C.gray3, padding:"4px 0" }}>会社を選ぶと、その会社の方が表示されます</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                      {filteredUsers.map(u => {
                        const sel = selectedMembers.find(m=>m.user_id===u.id);
                        return (
                          <div key={u.id} onClick={()=>toggleMember(u.id, u.name)} style={{
                            padding:"6px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                            cursor:"pointer", border:`1.5px solid ${sel?C.navy:C.gray2}`,
                            background: sel?C.navy:C.white, color: sel?C.white:C.textSub, transition:"all .15s",
                          }}>{u.name}</div>
                        );
                      })}
                    </div>
                  )}
                </>);
              })()}
              {selectedMembers.length > 0 && (
                <div style={{ marginTop:"8px", fontSize:"11px", color:C.navy, fontWeight:"600" }}>✓ {selectedMembers.map(m=>m.name).join('、')}</div>
              )}
            </div>

            {/* 未登録者 */}
            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>👤 入場者（未登録・手入力）</label>
              {freeNames.map((name,i) => (
                <div key={i} style={{ display:"flex", gap:"6px", marginBottom:"6px" }}>
                  <input value={name} onChange={e=>updateFreeName(i,e.target.value)}
                    placeholder={`氏名 ${i+1}`} style={{...inputStyle,flex:1,marginBottom:0}}
                    onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
                  {freeNames.length > 1 && (
                    <button type="button" onClick={()=>setFreeNames(p=>p.filter((_,idx)=>idx!==i))}
                      style={{ padding:"0 10px", border:"none", borderRadius:"8px", background:"#FFF0F0", color:C.red, cursor:"pointer", fontWeight:"700" }}>×</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={()=>setFreeNames(p=>[...p,''])}
                style={{ fontSize:"12px", color:C.blue, background:"none", border:`1px dashed ${C.blue}`, borderRadius:"8px", padding:"4px 12px", cursor:"pointer" }}>＋ 追加</button>
            </div>

            <Row label="メモ">
              <textarea value={eduForm.memo} onChange={e=>setEduForm(p=>({...p,memo:e.target.value}))} rows={3}
                placeholder="備考・注意事項など"
                style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}}
                onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
            </Row>
          </>)}
          {eduError   && <div style={{ color:C.red,   fontSize:"12px", marginBottom:"10px", padding:"10px", background:"#FFF0F0", borderRadius:"10px" }}>⚠️ {eduError}</div>}
          {eduSuccess  && <div style={{ color:C.green, fontSize:"12px", marginBottom:"10px", padding:"10px", background:"#EAF3DE", borderRadius:"10px" }}>✅ 記録を追加しました！</div>}
          <button type="submit" disabled={savingEdu} style={primaryBtnStyle(savingEdu)}>{savingEdu?'保存中...':'記録を保存'}</button>
        </form>
      </div>
    );
  }

  // ════════════════════════════════════════
  // VIEW: new_project（新規現場作成フォーム）
  // ════════════════════════════════════════
  if (view === 'new_project') {
    return (
      <div>
        {backBtn(editingProjId ? '現場詳細に戻る' : '現場一覧に戻る', () => {
          if (editingProjId) { setView('project_detail'); setEditingProjId(null); setProjError(''); }
          else { setView('projects'); setProjError(''); }
        })}
        <div style={{ fontSize:"14px", fontWeight:"700", color:C.text, marginBottom:"12px" }}>{editingProjId ? '✏️ 現場情報を編集' : '🏗️ 新規現場登録'}</div>
        <form onSubmit={submitProject}>
          {card(<>
            <Row label="課">
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                {DEPARTMENTS_LIST.map(d => (
                  <div key={d} onClick={()=>setProjForm(p=>({...p,department:p.department===d?'':d}))} style={{
                    padding:"5px 10px", borderRadius:"16px", fontSize:"12px", fontWeight:"600", cursor:"pointer",
                    border:`1.5px solid ${projForm.department===d?C.navy:C.gray2}`,
                    background: projForm.department===d?C.navy:C.white, color: projForm.department===d?C.white:C.textSub,
                  }}>{d}</div>
                ))}
              </div>
            </Row>

            <Row label="元請け会社名">
              <input value={projForm.contractor_name} onChange={e=>setProjForm(p=>({...p,contractor_name:e.target.value}))}
                list="contractor-list"
                placeholder="例：○○建設株式会社（入力 or 選択）" style={inputStyle}
                onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
              <datalist id="contractor-list">
                {contractors.map((c,i) => <option key={i} value={c} />)}
              </datalist>
              {contractors.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:"5px", marginTop:"6px" }}>
                  {contractors.map((c,i) => (
                    <div key={i} onClick={()=>setProjForm(p=>({...p,contractor_name:c}))}
                      style={{ fontSize:"11px", padding:"3px 9px", borderRadius:"12px", cursor:"pointer",
                        background: projForm.contractor_name===c ? C.navy : C.gray1,
                        color: projForm.contractor_name===c ? C.white : C.textSub,
                        border: `1px solid ${projForm.contractor_name===c ? C.navy : C.gray2}`,
                        fontWeight:"600" }}>{c}</div>
                  ))}
                </div>
              )}
            </Row>

            <Row label="現場名 *">
              <input value={projForm.project_name} onChange={e=>setProjForm(p=>({...p,project_name:e.target.value}))} required
                placeholder="例：○○局工事現場" style={inputStyle}
                onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
            </Row>

            <Row label="工事件名">
              <input value={projForm.contract_name} onChange={e=>setProjForm(p=>({...p,contract_name:e.target.value}))}
                placeholder="例：○○幹線設備更新工事" style={inputStyle}
                onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
            </Row>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>工期（任意）</label>
              <div style={{ display:"flex", gap:"8px", alignItems:"center", flexWrap:"wrap" }}>
                <input type="date" value={projForm.start_date} onChange={e=>setProjForm(p=>({...p,start_date:e.target.value}))} style={{...inputStyle,width:"145px"}}
                  onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
                <span style={{ fontSize:"12px", color:C.textSub }}>〜</span>
                <input type="date" value={projForm.end_date} onChange={e=>setProjForm(p=>({...p,end_date:e.target.value}))} style={{...inputStyle,width:"145px"}}
                  onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.gray2} />
              </div>
            </div>
          </>)}
          {projError && <div style={{ color:C.red, fontSize:"12px", marginBottom:"10px", padding:"10px", background:"#FFF0F0", borderRadius:"10px" }}>⚠️ {projError}</div>}
          <button type="submit" disabled={savingProj} style={primaryBtnStyle(savingProj)}>{savingProj?(editingProjId?'更新中...':'登録中...'):(editingProjId?'変更を保存':'現場を登録')}</button>
        </form>
      </div>
    );
  }

  // ════════════════════════════════════════
  // VIEW: project_detail（現場詳細・教育記録一覧）
  // ════════════════════════════════════════
  if (view === 'project_detail' && selProject) {
    const edus = Array.isArray(selProject.educations) ? selProject.educations : [];
    return (
      <div>
        {backBtn('現場一覧に戻る', () => { setView('projects'); setSelProject(null); })}
        {card(<>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:"15px", fontWeight:"700", color:C.text }}>{selProject.project_name}</div>
              {selProject.contractor_name && <div style={{ fontSize:"12px", color:C.textSub, marginTop:"2px" }}>元請け：{selProject.contractor_name}</div>}
              {selProject.contract_name   && <div style={{ fontSize:"12px", color:C.textSub }}>工事件名：{selProject.contract_name}</div>}
              {(selProject.start_date || selProject.end_date) && (
                <div style={{ fontSize:"11px", color:C.textSub, marginTop:"2px" }}>工期：{selProject.start_date||'?'} 〜 {selProject.end_date||'未定'}</div>
              )}
              {selProject.department && <div style={{ display:"inline-block", marginTop:"6px", padding:"2px 8px", borderRadius:"10px", background:C.navy+'15', color:C.navy, fontSize:"11px", fontWeight:"600" }}>{selProject.department}</div>}
            </div>
            <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", justifyContent:"flex-end" }}>
              <button onClick={()=>{ setView('new_edu'); setEduError(''); setEduSuccess(false); }}
                style={{ background:C.blue, color:C.white, border:"none", borderRadius:"10px", padding:"7px 12px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>＋ 教育記録追加</button>
              <button onClick={()=>{
                setEditingProjId(selProject.id);
                setProjForm({
                  department:    selProject.department    || '',
                  contractor_name: selProject.contractor_name || '',
                  project_name:  selProject.project_name  || '',
                  contract_name: selProject.contract_name  || '',
                  start_date:    selProject.start_date     || '',
                  end_date:      selProject.end_date       || '',
                });
                setProjError(''); loadContractors(); setView('new_project');
              }} style={{ background:"#F0F4FF", border:"none", borderRadius:"10px", padding:"7px 10px", cursor:"pointer", fontSize:"12px", color:C.navy, fontWeight:"600" }}>✏️ 編集</button>
              <button onClick={()=>deleteProject(selProject.id)}
                style={{ background:"#FFF0F0", border:"none", borderRadius:"10px", padding:"7px 10px", cursor:"pointer", fontSize:"12px", color:C.red, fontWeight:"600" }}>削除</button>
            </div>
          </div>
        </>)}

        <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, marginBottom:"10px", marginTop:"4px" }}>📋 教育記録一覧（{edus.length}件）</div>
        {edus.length === 0 ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"28px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>教育記録がありません</div>
        ) : edus.map(e => (
          <div key={e.id} onClick={()=>loadEduDetail(e.id)}
            style={{ background:C.white, borderRadius:"14px", padding:"13px 16px", marginBottom:"8px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontSize:"13px", fontWeight:"700", color:C.text }}>{e.edu_date}</div>
              <div style={{ fontSize:"11px", color:C.textSub, marginTop:"2px" }}>
                👷 {e.member_count||0}名　{e.photo_count>0?`📷 ${e.photo_count}枚`:''}
              </div>
              {e.memo && <div style={{ fontSize:"11px", color:C.textSub, marginTop:"2px", maxWidth:"240px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.memo}</div>}
            </div>
            <div style={{ fontSize:"18px", color:C.gray3 }}>›</div>
          </div>
        ))}
      </div>
    );
  }

  // ════════════════════════════════════════
  // VIEW: projects（現場一覧 ＝ トップ）
  // ════════════════════════════════════════
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
        <div style={{ fontSize:"15px", fontWeight:"700", color:C.text }}>🎓 新規入場者教育報告</div>
        <button onClick={()=>{ setProjForm(blankProj); setEditingProjId(null); setProjError(''); loadContractors(); setView('new_project'); }}
          style={{ background:C.blue, color:C.white, border:"none", borderRadius:"10px", padding:"7px 14px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>＋ 現場登録</button>
      </div>

      {/* 課フィルター */}
      <div style={{ background:C.white, borderRadius:"14px", padding:"12px 14px", marginBottom:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:"11px", color:C.textSub, fontWeight:"600", marginBottom:"7px" }}>🏢 課で絞り込み</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
          {chip('すべて', filterDept==='', ()=>{ setFilterDept(''); setFilterContr(''); })}
          {DEPARTMENTS_LIST.map(d => chip(d, filterDept===d, ()=>{ setFilterDept(d===filterDept?'':d); setFilterContr(''); }))}
        </div>

        {/* 元請けフィルター */}
        {contractors.length > 0 && (
          <>
            <div style={{ fontSize:"11px", color:C.textSub, fontWeight:"600", marginBottom:"7px", marginTop:"10px" }}>🏗️ 元請けで絞り込み</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
              {chip('すべて', filterContr==='', ()=>setFilterContr(''))}
              {contractors.map(c => chip(c, filterContr===c, ()=>setFilterContr(c===filterContr?'':c)))}
            </div>
          </>
        )}

        {/* 人名検索 */}
        <div style={{ fontSize:"11px", color:C.textSub, fontWeight:"600", marginBottom:"7px", marginTop:"10px" }}>🔍 入場者名で検索</div>
        <input
          value={searchMember}
          onChange={e => setSearchMember(e.target.value)}
          placeholder="氏名を入力..."
          style={{ width:"100%", padding:"7px 10px", borderRadius:"8px", border:`1px solid ${C.gray2}`, fontSize:"13px", boxSizing:"border-box", outline:"none" }}
        />

        {/* 工期ステータスフィルター */}
        <div style={{ fontSize:"11px", color:C.textSub, fontWeight:"600", marginBottom:"7px", marginTop:"10px" }}>📅 工期で絞り込み</div>
        <div style={{ display:"flex", gap:"6px" }}>
          {[['active','進行中'],['all','全て'],['ended','終了済み']].map(([val,label]) => (
            <div key={val} onClick={()=>setFilterStatus(val)}
              style={{ fontSize:"12px", padding:"5px 14px", borderRadius:"20px", cursor:"pointer", fontWeight:"600",
                background: filterStatus===val ? C.blue : C.gray1,
                color: filterStatus===val ? C.white : C.textSub,
                border: `1px solid ${filterStatus===val ? C.blue : C.gray2}` }}>{label}</div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"24px", color:C.textSub, fontSize:"13px" }}>読み込み中...</div>
      ) : (() => {
        const today = new Date().toISOString().slice(0,10);
        const fp = projects.filter(p => {
          if (filterStatus === 'all') return true;
          if (filterStatus === 'ended') return p.end_date && p.end_date < today;
          return !p.end_date || p.end_date >= today;
        });
        if (fp.length === 0) return (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            {projects.length === 0
              ? '現場がありません'
              : `「${filterStatus==='active'?'進行中':filterStatus==='ended'?'終了済み':'全て'}」の現場はありません`}
          </div>
        );
        return fp.map(p => (
          <div key={p.id} onClick={()=>loadProjectDetail(p.id)}
            style={{ background:C.white, borderRadius:"16px", padding:"14px 16px", marginBottom:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", cursor:"pointer" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, marginBottom:"2px" }}>{p.project_name}</div>
                {p.contractor_name && <div style={{ fontSize:"11px", color:C.textSub, marginBottom:"1px" }}>元請け：{p.contractor_name}</div>}
                {p.contract_name   && <div style={{ fontSize:"11px", color:C.textSub, marginBottom:"1px" }}>{p.contract_name}</div>}
                <div style={{ fontSize:"11px", color:C.textSub }}>
                  📋 教育 {p.edu_count||0}件
                  {p.last_edu_date ? ` · 最終：${p.last_edu_date}` : ''}
                  {p.start_date ? ` · 工期：${p.start_date}〜${p.end_date||''}` : ''}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"4px" }}>
                {p.department && <span style={{ fontSize:"10px", padding:"2px 6px", borderRadius:"8px", background:C.navy+'15', color:C.navy, fontWeight:"600" }}>{p.department}</span>}
                <span style={{ fontSize:"18px", color:C.gray3 }}>›</span>
              </div>
            </div>
          </div>
        ));
      })()}
    </div>
  );
}

// ================================
// 事故速報
// ================================
function AccidentReportPage({ token, user }) {
  const isAdmin = user.role === 'safety_admin' || user.role === 'manager';
  const [list,   setList]   = useState([]);
  const [detail, setDetail] = useState(null);
  const [tabRaw, setTab] = useStoredState('tfe_acc_tab', 'list');
  const tab = (tabRaw === 'list' || (isAdmin && ['new','reads'].includes(tabRaw))) ? tabRaw : 'list';
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title:'', occurred_at: new Date().toISOString().slice(0,16),
    location:'-', content:'',
  });
  const [kind,      setKind]      = useState('事故速報');
  const [sendMode,  setSendMode]  = useState('admin_mgr');
  const [sendOrgs,  setSendOrgs]  = useState([]);
  const [sendDepts, setSendDepts] = useState([]);
  const [sendUsers, setSendUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [allUsers,  setAllUsers]  = useState([]);
  const [partnerOrgs, setPartnerOrgs] = useState([]);
  const [testEmail,   setTestEmail]   = useState('');
  const [files,          setFiles]          = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [comment,        setComment]        = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${API}/accident_reports.php?type=list`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.json()).then(d=>{ setList(Array.isArray(d.list)?d.list:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);
  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API}/organizations.php`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.json()).then(d => Array.isArray(d) && setPartnerOrgs(d.filter(o => o.org_type === 'partner'))).catch(()=>{});
    fetch(`${API}/users.php`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.json()).then(d => Array.isArray(d) && setAllUsers(d)).catch(()=>{});
  }, []);

  const loadDetail = async (id) => {
    const res = await fetch(`${API}/accident_reports.php?type=detail&id=${id}`, { headers:{ Authorization:`Bearer ${token}` } });
    const d = await res.json();
    setDetail(d);
    fetch(`${API}/accident_reports.php?action=read&id=${id}`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } }).catch(()=>{});
    load();
  };

  const handleConfirm = async () => {
    await fetch(`${API}/accident_reports.php?action=confirm&id=${detail.id}`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
    loadDetail(detail.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k,v));
      fd.append('kind', kind);
      fd.append('send_mode', sendMode);
      fd.append('send_orgs', JSON.stringify(sendOrgs));
      fd.append('send_depts', JSON.stringify(sendDepts));
      fd.append('send_users', JSON.stringify(sendUsers));
      if (testEmail) fd.append('test_email', testEmail);
      files.forEach(f => fd.append('files[]', f));
      const res = await fetch(`${API}/accident_reports.php`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}` }, body:fd,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error||'送信に失敗しました'); setSaving(false); return; }
      setForm({ title:'', occurred_at: new Date().toISOString().slice(0,16), location:'-', content:'' });
      setKind('事故速報'); setSendMode('admin_mgr'); setSendOrgs([]); setSendDepts([]); setSendUsers([]); setUserQuery(''); setTestEmail('');
      setFiles([]); load(); setTab('list');
    } catch { setError('サーバーに接続できません'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この速報を削除しますか？')) return;
    await fetch(`${API}/accident_reports.php?id=${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
    setDetail(null); load();
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    setSendingComment(true);
    await fetch(`${API}/accident_reports.php?action=comment&id=${detail.id}`, {
      method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
      body: JSON.stringify({ comment }),
    });
    setComment(''); loadDetail(detail.id);
    setSendingComment(false);
  };

  const tabStyle = (t) => ({
    padding:"8px 16px", borderRadius:"10px", fontSize:"13px", fontWeight:"600",
    cursor:"pointer", border:"none", transition:"all .15s",
    background: tab===t ? C.red : C.white, color: tab===t ? C.white : C.textSub,
    boxShadow: tab===t ? "0 2px 8px rgba(226,75,74,0.25)" : "none",
  });

  if (detail) {
    return (
      <div>
        <button onClick={()=>setDetail(null)} style={{ background:"none", border:"none", color:C.blue, cursor:"pointer", fontSize:"13px", fontWeight:"600", marginBottom:"12px", padding:"0" }}>← 一覧に戻る</button>
        <div style={{ background:C.white, borderRadius:"16px", padding:"18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"12px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:"15px", fontWeight:"700", color:C.text, marginBottom:"4px" }}>{detail.title}</div>
              <div style={{ fontSize:"11px", color:C.textSub }}>{detail.occurred_at?.replace('T',' ')} ／ {detail.location}</div>
            </div>
            {isAdmin && <button onClick={()=>handleDelete(detail.id)} style={{ background:"#FFF0F0", border:"none", borderRadius:"8px", padding:"6px 10px", cursor:"pointer", fontSize:"12px", color:C.red, fontWeight:"600", marginLeft:"8px" }}>削除</button>}
          </div>
          <div style={{ fontSize:"13px", color:C.text, lineHeight:"1.7", whiteSpace:"pre-wrap", marginBottom:"14px" }}>{detail.content}</div>
          {detail.files?.length > 0 && (
            <div style={{ marginBottom:"14px" }}>
              <div style={{ fontSize:"12px", color:C.textSub, marginBottom:"6px", fontWeight:"600" }}>添付ファイル</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                {detail.files.map((f,i) => (
                  <a key={i} href={f.url} target="_blank" rel="noreferrer"
                    style={{ fontSize:"11px", padding:"4px 10px", borderRadius:"8px", background:C.gray1, color:C.blue, textDecoration:"none", border:`1px solid ${C.gray2}` }}>
                    📎 {f.name}
                  </a>
                ))}
              </div>
            </div>
          )}
          {Number(detail.is_confirmed) !== 1 && (
            <button onClick={handleConfirm} style={{ ...primaryBtnStyle(false), background: C.red }}>✅ 確認済みにする</button>
          )}
          {Number(detail.is_confirmed) === 1 && (
            <div style={{ fontSize:"12px", color:C.green, fontWeight:"600" }}>✅ 確認済み</div>
          )}
        </div>
        {/* コメント */}
        <div style={{ background:C.white, borderRadius:"16px", padding:"16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, marginBottom:"12px" }}>💬 コメント</div>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"12px" }}>
            {(detail.comments||[]).map((c,i) => (
              <div key={i} style={{ background:C.gray1, borderRadius:"10px", padding:"10px 12px" }}>
                <div style={{ fontSize:"11px", color:C.textSub, marginBottom:"4px", fontWeight:"600" }}>{c.name} · {c.created_at?.slice(0,16).replace('T',' ')}</div>
                <div style={{ fontSize:"13px", color:C.text, whiteSpace:"pre-wrap" }}>{c.comment}</div>
              </div>
            ))}
            {(!detail.comments || detail.comments.length===0) && (
              <div style={{ fontSize:"12px", color:C.gray3, textAlign:"center", padding:"12px" }}>コメントはありません</div>
            )}
          </div>
          <div style={{ display:"flex", gap:"8px" }}>
            <input value={comment} onChange={e=>setComment(e.target.value)} placeholder="コメントを入力..."
              style={{ ...inputStyle, flex:1 }} onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendComment();}}} />
            <button onClick={sendComment} disabled={sendingComment||!comment.trim()} style={{ ...primaryBtnStyle(!comment.trim()), width:"auto", padding:"0 16px", flexShrink:0 }}>送信</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize:"15px", fontWeight:"700", marginBottom:"12px", color:C.text }}>🚨 速報メール</div>
      <div style={{ display:"flex", gap:"8px", marginBottom:"16px", flexWrap:"wrap" }}>
        <button onClick={()=>setTab('list')} style={tabStyle('list')}>📋 速報一覧</button>
        {isAdmin && <button onClick={()=>setTab('new')} style={tabStyle('new')}>✏️ 速報を作成</button>}
        {isAdmin && <button onClick={()=>setTab('reads')} style={tabStyle('reads')}>👁️ 既読状況</button>}
      </div>
      {tab==='list' && (
        loading ? <div style={{ textAlign:"center", padding:"20px", color:C.gray3 }}>読み込み中...</div>
        : list.length===0 ? (
          <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3 }}>速報はありません</div>
        ) : list.map(a => (
          <div key={a.id} onClick={()=>loadDetail(a.id)} style={{
            background:C.white, borderRadius:"16px", padding:"14px 16px", marginBottom:"10px",
            boxShadow:"0 2px 8px rgba(0,0,0,0.06)", cursor:"pointer",
            border: !a.is_read ? `2px solid ${C.red}` : `1px solid ${C.gray2}`,
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"4px" }}>
              {!a.is_read && <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:C.red, flexShrink:0, display:"inline-block" }} />}
              <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.title}</div>
              {!a.is_confirmed && <span style={{ fontSize:"10px", padding:"2px 8px", borderRadius:"20px", background:"#FCEBEB", color:C.red, fontWeight:"600", flexShrink:0 }}>未確認</span>}
            </div>
            <div style={{ fontSize:"11px", color:C.textSub }}>{a.occurred_at?.slice(0,16).replace('T',' ')} ／ {a.location}</div>
          </div>
        ))
      )}
      {tab==='new' && isAdmin && (
        <form onSubmit={handleSubmit}>
          <div style={{ background:C.white, borderRadius:"16px", padding:"18px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"12px" }}>
            <Row label="種別 *">
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                {[
                  { val:'事故速報', color:C.red,  bg:'#FCEBEB' },
                  { val:'周知事項', color:C.blue, bg:'#E6F1FB' },
                  { val:'注意喚起', color:'#A66A00', bg:'#FFF4D6' },
                ].map(k => (
                  <button key={k.val} type="button" onClick={()=>setKind(k.val)} style={{
                    padding:'8px 18px', borderRadius:'20px', fontSize:'13px', fontWeight:'700', cursor:'pointer',
                    border: kind===k.val ? `2px solid ${k.color}` : `1px solid ${C.gray2}`,
                    background: kind===k.val ? k.bg : C.white,
                    color: kind===k.val ? k.color : C.textSub,
                  }}>{k.val}</button>
                ))}
              </div>
              <div style={{ fontSize:'11px', color:C.gray3, marginTop:'6px' }}>件名の頭に【{kind}】が自動で付きます</div>
            </Row>
            <Row label="件名 *">
              <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required
                placeholder={kind==='事故速報' ? '例：○○局での現用回線の誤抜去について' : kind==='周知事項' ? '例：来月の安全大会について' : '例：高所作業時の安全帯の使用について'} style={inputStyle} />
            </Row>
            <Row label="本文 *">
              <textarea value={form.content} onChange={e=>setForm(p=>({...p,content:e.target.value}))} required rows={12}
                placeholder="メール文をここに貼り付けてください"
                style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}} />
            </Row>
            <Row label="送信対象">
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {[
                  { val:'admin_mgr',     label:'安管室＋管理職のみ' },
                  { val:'admin_mgr_emp', label:'安管室＋管理職＋社員' },
                  { val:'all',           label:'全ユーザ（協力会社含む）' },
                  { val:'partners',      label:'課・パートナー社を指定（安管室＋管理職＋指定した課・会社）' },
                  { val:'individual',    label:'個人を指定（選んだ人のみ）' },
                ].map(({ val, label }) => (
                  <label key={val} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', cursor:'pointer' }}>
                    <input type="radio" name="accSendMode" checked={sendMode === val}
                      onChange={() => { setSendMode(val); if (val !== 'partners') { setSendOrgs([]); setSendDepts([]); } if (val !== 'individual') { setSendUsers([]); } }} />
                    {label}
                  </label>
                ))}
                {sendMode === 'individual' && (
                  <div style={{ marginLeft:'24px', display:'flex', flexDirection:'column', gap:'8px', paddingTop:'4px' }}>
                    <input value={userQuery} onChange={e=>setUserQuery(e.target.value)} placeholder="名前で検索" style={{ ...inputStyle, maxWidth:'320px' }} />
                    <div style={{ fontSize:'11px', color:C.textSub }}>{sendUsers.length}人選択中</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', maxHeight:'220px', overflowY:'auto' }}>
                      {allUsers.filter(u => u.role !== 'safety_admin' && (!userQuery.trim() || String(u.name||'').includes(userQuery.trim()))).map(u => (
                        <label key={u.id} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', cursor:'pointer', padding:'4px 8px', borderRadius:'8px', background: sendUsers.includes(u.id) ? '#EBF3FC' : C.gray1 }}>
                          <input type="checkbox" checked={sendUsers.includes(u.id)}
                            onChange={e => setSendUsers(p => e.target.checked ? [...p, u.id] : p.filter(v => v !== u.id))} />
                          {u.name}
                        </label>
                      ))}
                      {allUsers.length === 0 && <span style={{ fontSize:'12px', color:C.gray3 }}>ユーザーを読み込めませんでした</span>}
                    </div>
                  </div>
                )}
                {sendMode === 'partners' && (
                  <div style={{ marginLeft:'24px', display:'flex', flexDirection:'column', gap:'8px', paddingTop:'4px' }}>
                    <div style={{ fontSize:'11px', fontWeight:'700', color:C.textSub }}>課</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                      {DEPARTMENTS.map(d => (
                        <label key={d} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', cursor:'pointer' }}>
                          <input type="checkbox" checked={sendDepts.includes(d)}
                            onChange={e => setSendDepts(p => e.target.checked ? [...p, d] : p.filter(v => v !== d))} />
                          {d}
                        </label>
                      ))}
                    </div>
                    <div style={{ fontSize:'11px', fontWeight:'700', color:C.textSub, marginTop:'4px' }}>パートナー社</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                      {partnerOrgs.length === 0 && <span style={{ fontSize:'12px', color:C.gray3 }}>パートナー社が登録されていません</span>}
                      {partnerOrgs.map(o => (
                        <label key={o.id} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', cursor:'pointer' }}>
                          <input type="checkbox" checked={sendOrgs.includes(o.id)}
                            onChange={e => setSendOrgs(p => e.target.checked ? [...p, o.id] : p.filter(v => v !== o.id))} />
                          {o.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Row>
            <Row label="テスト送信先メール">
              <input value={testEmail} onChange={e=>setTestEmail(e.target.value)}
                placeholder="任意メールアドレス（テスト用）" type="email" style={inputStyle} />
            </Row>
            <div style={{ marginBottom:"12px" }}>
              <label style={labelStyle}>添付ファイル</label>
              <input type="file" multiple onChange={e=>setFiles([...e.target.files])}
                style={{ display:"block", fontSize:"12px", color:C.textSub }} />
              {files.length>0 && <div style={{ fontSize:"11px", color:C.textSub, marginTop:"4px" }}>{files.length}件選択中</div>}
            </div>
          </div>
          {error && <div style={{ color:C.red, fontSize:"12px", marginBottom:"10px", padding:"10px", background:"#FFF0F0", borderRadius:"10px" }}>⚠️ {error}</div>}
          <button type="submit" disabled={saving} style={{ ...primaryBtnStyle(saving), background: saving ? C.gray3 : C.red }}>{saving?'送信中...':`🚨 ${kind}を送信`}</button>
        </form>
      )}
      {tab==='reads' && isAdmin && (
        <div>
          {list.map(a => (
            <div key={a.id} style={{ background:C.white, borderRadius:"16px", padding:"14px 16px", marginBottom:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"13px", fontWeight:"700", color:C.text, marginBottom:"8px" }}>{a.title}</div>
              <div style={{ fontSize:"11px", color:C.textSub }}>
                既読 {a.read_count||0}名 ／ 確認済 {a.confirmed_count||0}名
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================
// 安パト
// ================================
// ================================
// 安全パトロール
// ================================
function PatrolPage({ token, user }) {
  const isAdmin = user.role === 'safety_admin' || user.role === 'manager';
  const today   = new Date();
  const ymNow   = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;

  // ── state ────────────────────────────────────────────
  const [tabRaw, setTab] = useStoredState('tfe_patrol_tab', 'list');   // 'list' | 'dept' | 'person'
  const tab = ['list','dept','person'].includes(tabRaw) ? tabRaw : 'list';
  const [month,     setMonth]     = useState(ymNow);
  const [deptFilter,setDeptFilter]= useState('');
  const [personQ,   setPersonQ]   = useState('');
  const [list,      setList]      = useState([]);
  const [stats,     setStats]     = useState(null);
  const [userList,  setUserList]  = useState([]);
  const DEPT_LIST = DEPARTMENTS_LIST;
  const [findings,  setFindings]  = useState({ members:[], overall:[] });
  const [expandedIds, setExpandedIds] = useState({});
  const [showForm,  setShowForm]  = useState(false);
  const [editTarget,setEditTarget]= useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  // フォーム
  const emptyForm = () => ({
    patrol_date: today.toISOString().slice(0,10),
    location:'', department:'', team_name:'',
    result:'', memo:'',
  });
  const [form,    setForm]    = useState(emptyForm());
  const [members, setMembers] = useState([]); // [{user_id, free_name, findings}]
  const [photos,  setPhotos]  = useState([]); // [{file_name, file_url, _preview}]

  // ── helpers ──────────────────────────────────────────
  const authH = { Authorization:`Bearer ${token}` };

  const loadList = () =>
    fetch(`${API}/patrol.php?month=${month}`, { headers:authH })
      .then(r=>r.json()).then(d => {
        const rows = Array.isArray(d) ? d : [];
        // members/photos が文字列で返ってきた場合はパース
        const normalized = rows.map(p => ({
          ...p,
          members: Array.isArray(p.members) ? p.members : (typeof p.members === 'string' ? (() => { try { return JSON.parse(p.members); } catch { return []; } })() : []),
          photos:  Array.isArray(p.photos)  ? p.photos  : (typeof p.photos  === 'string' ? (() => { try { return JSON.parse(p.photos);  } catch { return []; } })() : []),
        }));
        setList(normalized);
      }).catch(()=>{});

  const loadStats = () =>
    fetch(`${API}/patrol.php?type=stats`, { headers:authH })
      .then(r=>r.json()).then(d => setStats(d)).catch(()=>{});

  const loadUsers = () =>
    fetch(`${API}/users.php`, { headers:authH })
      .then(r=>r.json()).then(d => setUserList(Array.isArray(d)?d:[])).catch(()=>{});

  const loadDepts = () => {} // 固定リスト使用;

  const loadFindings = () =>
    fetch(`${API}/patrol.php?type=findings`, { headers:authH })
      .then(r=>r.json()).then(d => setFindings(d)).catch(()=>{});

  useEffect(() => {
    loadList(); loadStats(); loadUsers(); loadDepts();
  }, [month]);

  useEffect(() => {
    if (tab === 'person') loadFindings();
  }, [tab]);

  // ── 参加者操作 ────────────────────────────────────────
  const toggleUser = (uid, name) => {
    setMembers(prev => {
      const exists = prev.find(m => m.user_id === uid);
      if (exists) return prev.filter(m => m.user_id !== uid);
      return [...prev, { user_id:uid, free_name:'', findings:'', _name:name }];
    });
  };
  const addManualMember = () => {
    setMembers(prev => [...prev, { user_id:null, free_name:'', findings:'', _key: Date.now() }]);
  };
  const updateMember = (idx, field, val) => {
    setMembers(prev => prev.map((m,i) => i===idx ? {...m, [field]:val} : m));
  };
  const removeMember = (idx) => {
    setMembers(prev => prev.filter((_,i) => i!==idx));
  };

  // ── 写真アップロード ──────────────────────────────────
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const fd = new FormData();
      fd.append('photo', file);
      try {
        const res = await fetch(`${API}/patrol.php?type=upload`, {
          method:'POST', headers:{ Authorization:`Bearer ${token}` }, body:fd,
        });
        const d = await res.json();
        if (d.ok) {
          const preview = URL.createObjectURL(file);
          setPhotos(prev => [...prev, { file_name:d.file_name, file_url:d.file_url, _preview:preview }]);
        }
      } catch {}
    }
    e.target.value = '';
  };
  const removePhoto = (idx) => setPhotos(prev => prev.filter((_,i)=>i!==idx));

  // ── 登録・更新 ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = {
      ...form,
      members: members.map((m,i) => ({
        user_id:   m.user_id || null,
        free_name: m.free_name || '',
        findings:  m.findings || '',
        sort_order: i,
      })),
      photos: photos.map((p,i) => ({
        file_name: p.file_name, file_url: p.file_url, sort_order:i,
      })),
    };
    try {
      const url    = editTarget ? `${API}/patrol.php?id=${editTarget.id}` : `${API}/patrol.php`;
      const method = editTarget ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method, headers:{'Content-Type':'application/json', ...authH},
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error||'送信に失敗しました'); setSaving(false); return; }
      setShowForm(false); setEditTarget(null);
      setForm(emptyForm()); setMembers([]); setPhotos([]);
      loadList(); loadStats();
    } catch { setError('サーバーに接続できません'); }
    setSaving(false);
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      patrol_date: p.patrol_date || '',
      location:    p.location    || '',
      department:  p.department  || '',
      team_name:   p.team_name   || '',
      result:      p.result      || '',
      memo:        p.memo        || '',
    });
    setMembers((p.members||[]).map(m => ({
      user_id:   m.user_id || null,
      free_name: m.free_name || '',
      findings:  m.findings || '',
      _name:     m.user_name || m.free_name,
    })));
    setPhotos((p.photos||[]).map(ph => ({
      file_name: ph.file_name, file_url: ph.file_url, _preview: null,
    })));
    setShowForm(true); setError('');
  };

  const handleComplete = async (id) => {
    await fetch(`${API}/patrol.php?id=${id}`, {
      method:'PATCH', headers:{'Content-Type':'application/json', ...authH},
      body: JSON.stringify({ status:'done' }),
    });
    loadList(); loadStats();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この安パト記録を削除しますか？')) return;
    await fetch(`${API}/patrol.php?id=${id}`, { method:'DELETE', headers:authH });
    loadList(); loadStats();
  };

  const toggleExpand = (id) => setExpandedIds(prev => ({...prev, [id]: !prev[id]}));

  // ── 表示データ ────────────────────────────────────────
  const fiscalStart = today.getMonth() >= 9
    ? `${today.getFullYear()}-10-01`
    : `${today.getFullYear()-1}-10-01`;

  const prevYm = (() => {
    const d = new Date(month+'-01'); d.setMonth(d.getMonth()-1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  })();

  const monthTotal   = list.filter(p => p.patrol_date?.startsWith(month)).length;
  const prevTotal    = list.filter(p => p.patrol_date?.startsWith(prevYm)).length;
  const fiscalTotal  = stats?.year_total ?? 0;
  const deptStats    = stats?.dept_stats ?? [];

  const filteredList = list.filter(p => {
    if (!p.patrol_date?.startsWith(month)) return false;
    if (deptFilter && p.department !== deptFilter) return false;
    return true;
  });

  // 人別指摘フィルター
  const allPersonRows = [
    ...(findings.overall||[]).map(o => ({
      type:'overall', name:'全体指摘', patrol_id:o.patrol_id,
      patrol_date:o.patrol_date, location:o.location, department:o.department,
      findings:o.findings,
    })),
    ...(findings.members||[]).filter(m => m.findings).map(m => ({
      type:'member',
      name: m.user_name || m.free_name || '不明',
      is_manual: !m.user_id,
      patrol_id: m.patrol_id,
      patrol_date: m.patrol_date, location: m.location, department: m.department,
      findings: m.findings,
    })),
  ];
  const filteredPersonRows = personQ
    ? allPersonRows.filter(r => r.name.includes(personQ) || r.type==='overall')
    : allPersonRows;

  // ── スタイル共通 ──────────────────────────────────────
  const cardStyle = {
    background:C.white, borderRadius:"16px", marginBottom:"10px",
    boxShadow:"0 2px 8px rgba(0,0,0,0.06)", overflow:"hidden",
  };
  const badgeDone    = { fontSize:"10px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background:"#DCFCE7", color:C.green, whiteSpace:"nowrap" };
  const badgePlanned = { fontSize:"10px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background:"#FEF3C7", color:C.amber, whiteSpace:"nowrap" };

  // ── 登録/編集フォーム JSX ─────────────────────────────
  const renderForm = () => (
    <form onSubmit={handleSubmit} style={{ background:C.white, borderRadius:"16px", padding:"18px", marginBottom:"14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:"14px", fontWeight:"700", color:C.navy, marginBottom:"14px" }}>
        {editTarget ? '安パト編集' : '新規安パト登録'}
      </div>

      {/* 日付・課 */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
        <Row label="実施日 *">
          <input type="date" value={form.patrol_date}
            onChange={e=>setForm(p=>({...p,patrol_date:e.target.value}))} required style={inputStyle} />
        </Row>
        <Row label="担当課">
          <select value={form.department} onChange={e=>setForm(p=>({...p,department:e.target.value}))} style={inputStyle}>
            <option value="">選択</option>
            {DEPT_LIST.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Row>
      </div>

      <Row label="班名">
        <input value={form.team_name} onChange={e=>setForm(p=>({...p,team_name:e.target.value}))}
          placeholder="例：○○班" style={inputStyle} />
      </Row>
      <Row label="場所 *">
        <input value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} required
          placeholder="例：〇〇局 構内" style={inputStyle} />
      </Row>

      {/* 全体指摘事項 */}
      <Row label="全体指摘事項">
        <textarea value={form.result} onChange={e=>setForm(p=>({...p,result:e.target.value}))} rows={3}
          placeholder="現場全体への指摘・改善事項（例：通路の整理整頓が不十分、立入禁止区域の表示が薄い）"
          style={{...inputStyle, resize:"vertical"}} />
      </Row>

      {/* 写真 */}
      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>写真（複数可）</label>
        <div style={{ border:`1.5px solid ${C.gray2}`, borderRadius:"10px", padding:"10px", background:C.gray1 }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
            {photos.map((ph,i) => (
              <div key={i} style={{ width:"72px", height:"72px", borderRadius:"8px", overflow:"hidden", position:"relative", flexShrink:0 }}>
                <img src={ph._preview || ph.file_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                <button type="button" onClick={()=>removePhoto(i)} style={{
                  position:"absolute", top:"2px", right:"2px", background:"rgba(0,0,0,0.55)",
                  color:"#fff", border:"none", borderRadius:"50%", width:"18px", height:"18px",
                  fontSize:"11px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                }}>×</button>
              </div>
            ))}
            <label style={{
              width:"72px", height:"72px", borderRadius:"8px", border:`2px dashed ${C.gray2}`,
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              cursor:"pointer", flexShrink:0,
            }}>
              <input type="file" accept="image/*" multiple hidden onChange={handlePhotoSelect} />
              <span style={{ fontSize:"22px", color:C.textSub }}>📷</span>
              <span style={{ fontSize:"10px", color:C.textSub, marginTop:"3px" }}>タップで追加</span>
            </label>
          </div>
        </div>
      </div>

      {/* 参加者ピッカー */}
      <div style={{ marginBottom:"14px" }}>
        <label style={labelStyle}>参加者・指摘事項</label>
        {/* 課フィルタ（userListから動的生成） */}
        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"8px" }}>
          {[...new Set(
            (userList||[]).flatMap(u => Array.isArray(u.departments) ? u.departments : (u.departments ? [u.departments] : []))
          )].filter(Boolean).sort().map(d => {
            const active = (form._memberDept||form.department||'') === d;
            return (
              <button type="button" key={d} onClick={()=>setForm(p=>({...p,_memberDept:d}))}
                style={{ padding:"4px 12px", borderRadius:"20px", fontSize:"11px", fontWeight:"600", cursor:"pointer", border:`1.5px solid ${active?C.navy:C.gray2}`, background:active?C.navy:'transparent', color:active?'#fff':C.textSub }}>
                {d}
              </button>
            );
          })}
        </div>
        <div style={{ border:`1.5px solid ${C.gray2}`, borderRadius:"10px", overflow:"hidden" }}>
          {(() => {
            const selDept = form._memberDept || form.department || '';
            const filtered = selDept
              ? (userList||[]).filter(u => {
                  const depts = Array.isArray(u.departments) ? u.departments : (u.departments ? [u.departments] : []);
                  return depts.includes(selDept);
                })
              : [];
            if (!selDept) return (
              <div style={{ padding:"16px", textAlign:"center", fontSize:"12px", color:C.textSub }}>↑ 課を選んでください</div>
            );
            if (filtered.length === 0) return (
              <div style={{ padding:"16px", textAlign:"center", fontSize:"12px", color:C.textSub }}>{selDept}に所属するユーザーがいません</div>
            );
            return filtered.map(u => {
                  const selIdx = members.findIndex(m => m.user_id === u.id);
                  const sel    = selIdx >= 0;
                  return (
                    <Fragment key={u.id}>
                <div onClick={()=>toggleUser(u.id, u.name)}
                  style={{ display:"flex", alignItems:"center", gap:"8px", padding:"9px 12px",
                    borderBottom:`1px solid ${C.gray2}`, cursor:"pointer",
                    background: sel ? '#F0F7FF' : 'transparent',
                  }}>
                  <div style={{
                    width:"18px", height:"18px", borderRadius:"4px", flexShrink:0,
                    border:`2px solid ${sel?C.navy:C.gray2}`,
                    background: sel?C.navy:'transparent', color:"#fff",
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", fontWeight:"700",
                  }}>{sel?'✓':''}</div>
                  <span style={{ fontSize:"13px", fontWeight:"600", flex:1 }}>{u.name}</span>
                  <span style={{ fontSize:"10px", color:C.textSub, background:C.gray1, border:`1px solid ${C.gray2}`, borderRadius:"4px", padding:"1px 5px" }}>{u.department||u.role}</span>
                </div>
                {sel && (
                  <div style={{ padding:"8px 12px 10px 38px", background:C.gray1, borderBottom:`1px solid ${C.gray2}` }}>
                    <textarea value={members[selIdx].findings}
                      onChange={e=>updateMember(selIdx,'findings',e.target.value)}
                      rows={2} placeholder="指摘事項（任意）"
                      style={{ ...inputStyle, fontSize:"12px", resize:"vertical", padding:"7px 10px" }} />
                  </div>
                )}
                  </Fragment>
                  );
                });
          })()}

          {/* 手打ち行 */}
          {members.filter(m=>!m.user_id).map((m,i) => {
            const realIdx = members.indexOf(m);
            return (
              <Fragment key={m._key||`manual-${i}`}>
                <div style={{ display:"flex", alignItems:"center", gap:"8px", padding:"9px 12px", borderBottom:`1px solid ${C.gray2}`, background:"#FAFAFA" }}>
                  <div style={{ width:"18px", height:"18px", borderRadius:"4px", background:C.textSub, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", color:"#fff", fontWeight:"700", flexShrink:0 }}>✓</div>
                  <input value={m.free_name} onChange={e=>updateMember(realIdx,'free_name',e.target.value)}
                    placeholder="氏名を入力…" style={{ flex:1, border:"none", background:"transparent", fontFamily:"inherit", fontSize:"13px", fontWeight:"600", color:C.text, outline:"none" }} />
                  <button type="button" onClick={()=>removeMember(realIdx)}
                    style={{ background:"none", border:"none", color:C.textSub, cursor:"pointer", fontSize:"16px", padding:"0 4px" }}>×</button>
                </div>
                <div style={{ padding:"8px 12px 10px 38px", background:C.gray1, borderBottom:`1px solid ${C.gray2}` }}>
                  <textarea value={m.findings} onChange={e=>updateMember(realIdx,'findings',e.target.value)}
                    rows={2} placeholder="指摘事項（任意）"
                    style={{ ...inputStyle, fontSize:"12px", resize:"vertical", padding:"7px 10px" }} />
                </div>
              </Fragment>
            );
          })}

          <button type="button" onClick={addManualMember}
            style={{ display:"flex", alignItems:"center", gap:"6px", width:"100%", padding:"9px 12px",
              fontSize:"12px", fontWeight:"600", color:C.blue, background:"none",
              border:"none", borderTop:`1px dashed ${C.gray2}`, cursor:"pointer", textAlign:"left" }}>
            ＋ 名前を手打ちで追加
          </button>
        </div>
      </div>

      {/* メモ */}
      <Row label="メモ・所見">
        <textarea value={form.memo} onChange={e=>setForm(p=>({...p,memo:e.target.value}))} rows={2}
          placeholder="全体所見・備考（内部メモなど）"
          style={{...inputStyle, resize:"vertical"}} />
      </Row>

      {error && <div style={{ color:C.red, fontSize:"12px", marginBottom:"10px", padding:"10px", background:"#FFF0F0", borderRadius:"10px" }}>⚠️ {error}</div>}
      <div style={{ display:"flex", gap:"8px" }}>
        <button type="submit" disabled={saving} style={primaryBtnStyle(saving)}>
          {saving ? '保存中...' : editTarget ? '更新する' : '登録する'}
        </button>
        <button type="button" onClick={()=>{ setShowForm(false); setEditTarget(null); setForm(emptyForm()); setMembers([]); setPhotos([]); }} style={cancelBtnStyle}>
          キャンセル
        </button>
      </div>
    </form>
  );

  // ── 実績カード ────────────────────────────────────────
  const renderCard = (p) => {
    const expanded = expandedIds[p.id];
    const memberCount = (p.members||[]).length;
    const photoCount  = (p.photos||[]).length;
    const hasFinding  = p.result;
    return (
      <div key={p.id} style={cardStyle}>
        {/* ヘッダー */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"14px 14px 0" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:"11px", color:C.textSub, marginBottom:"3px" }}>
              {p.patrol_date} {p.department && `· ${p.department}`}
            </div>
            <div style={{ fontSize:"14px", fontWeight:"700", color:C.text }}>{p.location}</div>
            {p.team_name && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:"4px", marginTop:"5px",
                background:C.gray1, border:`1.5px solid ${C.gray2}`, borderRadius:"20px",
                padding:"3px 10px", fontSize:"11px", fontWeight:"600", color:C.textSub }}>
                🏷 {p.team_name}
              </div>
            )}
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"6px", marginLeft:"8px" }}>
            <span style={p.status==='done' ? badgeDone : badgePlanned}>
              {p.status==='done' ? '完了' : '予定'}
            </span>
            {isAdmin && p.status!=='done' && (
              <button type="button" onClick={()=>handleComplete(p.id)} style={{ fontSize:"11px", padding:"3px 10px", borderRadius:"20px", background:C.green+'18', color:C.green, border:"none", cursor:"pointer", fontWeight:"600" }}>✓ 完了</button>
            )}
          </div>
        </div>

        {/* 参加人数・写真枚数バー */}
        <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"8px 14px 10px", fontSize:"11px", color:C.textSub }}>
          {memberCount > 0 && <span>👥 {memberCount}名</span>}
          {photoCount  > 0 && <span>📸 {photoCount}枚</span>}
          {hasFinding  && <span style={{ color:C.amber, fontWeight:"600" }}>⚠ 全体指摘あり</span>}
          <div style={{ flex:1 }} />
          {isAdmin && (
            <>
              <button type="button" onClick={()=>openEdit(p)} style={{ fontSize:"11px", padding:"3px 8px", borderRadius:"16px", background:C.gray1, color:C.textSub, border:"none", cursor:"pointer" }}>編集</button>
              <button type="button" onClick={()=>handleDelete(p.id)} style={{ fontSize:"11px", padding:"3px 8px", borderRadius:"16px", background:"#FFF0F0", color:C.red, border:"none", cursor:"pointer" }}>削除</button>
            </>
          )}
        </div>

        {/* 展開エリア */}
        {expanded && (
          <>
            {/* 全体指摘 */}
            {p.result && (
              <div style={{ borderTop:`1px solid ${C.gray2}`, padding:"10px 14px", background:"rgba(217,119,6,0.05)" }}>
                <div style={{ fontSize:"11px", fontWeight:"700", color:C.amber, marginBottom:"5px" }}>📋 全体指摘事項</div>
                <div style={{ fontSize:"12px", color:C.text, lineHeight:"1.6", whiteSpace:"pre-wrap" }}>{p.result}</div>
              </div>
            )}

            {/* 参加者 */}
            {(p.members||[]).length > 0 && (
              <div style={{ borderTop:`1px solid ${C.gray2}`, padding:"10px 14px" }}>
                <div style={{ fontSize:"11px", fontWeight:"700", color:C.textSub, letterSpacing:".04em", marginBottom:"8px" }}>参加者 / 指摘事項</div>
                {(p.members||[]).map((m,i) => {
                  const name    = m.user_name || m.free_name || '不明';
                  const initial = name.charAt(0);
                  return (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:"8px", padding:"6px 0", borderBottom: i<p.members.length-1?`1px solid ${C.gray2}`:"none" }}>
                      <div style={{ width:"28px", height:"28px", borderRadius:"50%", background: m.user_id?C.navy:C.textSub, color:"#fff", fontSize:"11px", fontWeight:"700", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initial}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:"12px", fontWeight:"700", color:C.text }}>
                          {name}
                          {!m.user_id && <span style={{ fontSize:"10px", color:C.textSub, fontWeight:"400", marginLeft:"5px" }}>（手打ち）</span>}
                        </div>
                        {m.findings ? (
                          <>
                            <div style={{ display:"inline-flex", alignItems:"center", gap:"3px", background:"#FFF7ED", color:C.amber, border:"1px solid #FDE68A", borderRadius:"6px", padding:"2px 6px", fontSize:"10px", fontWeight:"700", marginTop:"3px" }}>⚠ 指摘あり</div>
                            <div style={{ fontSize:"11px", color:C.textSub, marginTop:"3px", lineHeight:"1.5", whiteSpace:"pre-wrap" }}>{m.findings}</div>
                          </>
                        ) : (
                          <div style={{ fontSize:"11px", color:C.textSub, marginTop:"2px" }}>指摘なし</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 写真 */}
            {(p.photos||[]).length > 0 && (
              <div style={{ borderTop:`1px solid ${C.gray2}`, padding:"10px 14px", display:"flex", gap:"8px", flexWrap:"wrap" }}>
                {(p.photos||[]).map((ph,i) => (
                  <div key={i} style={{ width:"64px", height:"64px", borderRadius:"8px", overflow:"hidden", flexShrink:0 }}>
                    <img src={ph.file_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  </div>
                ))}
              </div>
            )}

            {/* メモ */}
            {p.memo && (
              <div style={{ borderTop:`1px solid ${C.gray2}`, padding:"8px 14px" }}>
                <div style={{ fontSize:"11px", color:C.textSub, whiteSpace:"pre-wrap" }}>{p.memo}</div>
              </div>
            )}
          </>
        )}

        {/* 展開トグル */}
        {(memberCount>0 || photoCount>0 || p.result || p.memo) && (
          <button type="button" onClick={()=>toggleExpand(p.id)} style={{
            padding:"8px 14px", borderTop:`1px solid ${C.gray2}`,
            fontSize:"12px", color:C.blue, fontWeight:"600", cursor:"pointer",
            display:"flex", alignItems:"center", gap:"4px",
            background:"none", border:"none", borderLeft:"none", borderRight:"none", borderBottom:"none",
            width:"100%", textAlign:"left",
          }}>
            {expanded ? '▴ 閉じる' : '▾ 詳細・写真を見る'}
          </button>
        )}
      </div>
    );
  };

  // ── render ────────────────────────────────────────────
  return (
    <div>
      {/* ヘッダー */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"14px" }}>
        <div style={{ fontSize:"15px", fontWeight:"700", color:C.text }}>🛡️ 安パト</div>
        {isAdmin && (
          <button type="button" onClick={()=>{ if(showForm && !editTarget){ setShowForm(false); } else { setShowForm(true); setEditTarget(null); setForm(emptyForm()); setMembers([]); setPhotos([]); setError(''); } }}
            style={{ ...primaryBtnStyle(false), width:"auto", padding:"8px 16px" }}>
            {showForm && !editTarget ? 'キャンセル' : '＋ 新規登録'}
          </button>
        )}
      </div>

      {/* 統計 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px", marginBottom:"14px" }}>
        {[
          { label:"今月",  val:monthTotal,  color:C.blue },
          { label:"先月",  val:prevTotal,   color:C.textSub },
          { label:"今期",  val:fiscalTotal, color:C.navy },
        ].map((s,i) => (
          <div key={i} style={{ background:C.white, borderRadius:"14px", padding:"12px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"10px", color:C.textSub, marginBottom:"4px", fontWeight:"600" }}>{s.label}</div>
            <div style={{ fontSize:"26px", fontWeight:"700", color:s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* タブ */}
      <div style={{ display:"flex", gap:"4px", marginBottom:"14px", background:C.white, borderRadius:"12px", padding:"4px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        {[['list','実績一覧'],['dept','課別集計'],['person','人別指摘']].map(([t,label]) => (
          <button key={t} type="button" onClick={()=>setTab(t)} style={{
            flex:1, padding:"7px 4px", border:"none", borderRadius:"9px", cursor:"pointer",
            fontSize:"12px", fontWeight:"600", transition:"all .15s",
            background: tab===t ? C.navy : "transparent",
            color:       tab===t ? "#fff" : C.textSub,
          }}>{label}</button>
        ))}
      </div>

      {/* ===================== 実績一覧 ===================== */}
      {tab === 'list' && (
        <>
          {/* フォーム（新規） */}
          {showForm && !editTarget && renderForm()}

          {/* フィルター */}
          <div style={{ display:"flex", gap:"8px", marginBottom:"14px", flexWrap:"wrap" }}>
            <input type="month" value={month} onChange={e=>setMonth(e.target.value)}
              style={{ ...inputStyle, width:"140px" }} />
            <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}
              style={{ ...inputStyle, flex:1, minWidth:"100px" }}>
              <option value="">全ての課</option>
              {DEPT_LIST.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {filteredList.length === 0 ? (
            <div style={{ background:C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color:C.gray3 }}>記録がありません</div>
          ) : filteredList.map(renderCard)}
        </>
      )}

      {/* ===================== 課別集計 ===================== */}
      {tab === 'dept' && (
        <div style={{ background:C.white, borderRadius:"14px", padding:"14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:"11px", fontWeight:"700", color:C.textSub, letterSpacing:".06em", textTransform:"uppercase", marginBottom:"12px" }}>今期 課別実施回数</div>
          {deptStats.length === 0 ? (
            <div style={{ color:C.textSub, fontSize:"12px" }}>データがありません</div>
          ) : (() => {
            const max = Math.max(...deptStats.map(d=>parseInt(d.count)||0), 1);
            return deptStats.map((d,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
                <div style={{ fontSize:"12px", fontWeight:"600", color:C.text, width:"150px", flexShrink:0 }}>{d.department}</div>
                <div style={{ flex:1, height:"8px", background:C.gray2, borderRadius:"99px", overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:"99px", background:C.blue, width:`${((parseInt(d.count)||0)/max)*100}%` }} />
                </div>
                <div style={{ fontSize:"12px", fontWeight:"700", color:C.blue, width:"24px", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>{d.count}</div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ===================== 人別指摘 ===================== */}
      {tab === 'person' && (
        <>
          <div style={{ marginBottom:"10px" }}>
            <input value={personQ} onChange={e=>setPersonQ(e.target.value)}
              placeholder="🔍 名前で絞り込み…"
              style={{ ...inputStyle, width:"100%" }} />
          </div>
          <div style={{ background:C.white, borderRadius:"14px", padding:"14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"11px", fontWeight:"700", color:C.textSub, letterSpacing:".06em", textTransform:"uppercase", marginBottom:"12px" }}>今期 指摘一覧</div>
            {filteredPersonRows.length === 0 ? (
              <div style={{ color:C.textSub, fontSize:"12px" }}>指摘事項がありません</div>
            ) : filteredPersonRows.map((r,i) => (
              <div key={i} style={{
                display:"grid", gridTemplateColumns:"auto 1fr auto",
                gap:"10px", alignItems:"start",
                padding:"10px 0", borderBottom: i<filteredPersonRows.length-1?`1px solid ${C.gray2}`:"none",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
                  <div style={{
                    width:"24px", height:"24px", borderRadius:"50%",
                    background: r.type==='overall' ? C.amber : r.is_manual ? C.textSub : C.navy,
                    color:"#fff", fontSize: r.type==='overall'?"12px":"10px",
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                  }}>
                    {r.type==='overall' ? '📋' : r.name.charAt(0)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:"12px", fontWeight:"700", color: r.type==='overall'?C.amber:C.text }}>
                    {r.name}
                    {r.is_manual && <span style={{ fontSize:"10px", color:C.textSub, fontWeight:"400", marginLeft:"5px" }}>（手打ち）</span>}
                  </div>
                  <div style={{
                    display:"inline-block", background:"#FFF7ED", color:C.amber,
                    border:"1px solid #FDE68A", borderRadius:"5px", padding:"1px 6px",
                    fontSize:"10px", fontWeight:"700", marginTop:"3px",
                  }}>⚠ 指摘あり</div>
                  <div style={{ fontSize:"11px", color:C.textSub, marginTop:"3px", lineHeight:"1.5", whiteSpace:"pre-wrap" }}>{r.findings}</div>
                </div>
                <div style={{ fontSize:"11px", color:C.textSub, whiteSpace:"nowrap", textAlign:"right" }}>
                  {r.patrol_date?.slice(5)}<br/>{r.department||r.location}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 編集フォーム（モーダル） */}
      {showForm && editTarget && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}>
          <div style={{ background:C.white, borderRadius:"20px", padding:"20px", width:"100%", maxWidth:"480px", maxHeight:"80dvh", overflowY:"auto" }}>
            {renderForm()}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// 安全サイクル
// ================================
function SafetyCyclePage({ user, token }) {
  const isAdmin = user.role === "safety_admin";
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sent, setSent] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [popupPhoto, setPopupPhoto] = useState(null); // {url, name}

  // カテゴリ読み込み
  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/safety_cycle.php?type=categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const cats = Array.isArray(data) ? data : [];
      setCategories(cats);
      if (cats.length > 0 && !selectedCat) setSelectedCat(cats[0]);
    } catch { setError("読み込みに失敗しました"); }
    setLoading(false);
  };

  // 書類・ユーザー読み込み
  const loadDocs = async (catId) => {
    setDocsLoading(true);
    try {
      const [docsRes, usersRes] = await Promise.all([
        fetch(`${API}/safety_cycle.php?type=docs&category_id=${catId}`, { headers: { Authorization: `Bearer ${token}` } }),
        isAdmin ? fetch(`${API}/safety_cycle.php?type=users`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
      ]);
      const docsData = await docsRes.json();
      setDocs(Array.isArray(docsData) ? docsData : []);
      if (isAdmin && usersRes) {
        const usersData = await usersRes.json();
        setUsers(Array.isArray(usersData) ? usersData : []);
      }
    } catch {}
    setDocsLoading(false);
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { if (selectedCat) { setSent(false); loadDocs(selectedCat.id); } }, [selectedCat]);

  // カテゴリ追加
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setAddingCat(true);
    try {
      const res = await fetch(`${API}/safety_cycle.php?type=categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newCatName.trim() }),
      });
      if (res.ok) { setNewCatName(""); setShowCatForm(false); await loadCategories(); }
    } catch {}
    setAddingCat(false);
  };

  // 写真アップロード
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCat) return;
    setUploading(true); setError("");
    try {
      const fileData = await fileToBase64(file);
      const res = await fetch(`${API}/safety_cycle.php?type=docs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category_id: selectedCat.id, fileName: file.name, fileData }),
      });
      if (!res.ok) { const d = await res.json().catch(()=>({})); setError(d.error || "送信に失敗しました"); setUploading(false); return; }
      await loadDocs(selectedCat.id);
      setSent(true);
    } catch { setError("送信に失敗しました"); }
    setUploading(false);
  };

  // 自分の提出状況
  const myDoc = docs.find(d => String(d.uploaded_by) === String(user.userId));

  return (
    <div style={{ maxWidth:"720px" }}>
      <div style={{ fontSize:"16px", fontWeight:"700", color: C.navy, marginBottom:"16px" }}>🔄 安全サイクル</div>

      {/* カテゴリタブ */}
      {loading ? (
        <div style={{ textAlign:"center", padding:"20px", color: C.gray3, fontSize:"12px" }}>読み込み中...</div>
      ) : (
        <>
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"16px", alignItems:"center" }}>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => { setSelectedCat(cat); setSent(false); }}
                style={{
                  padding:"7px 16px", borderRadius:"20px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"600",
                  background: selectedCat?.id === cat.id ? C.navy : C.gray1,
                  color: selectedCat?.id === cat.id ? C.white : C.text,
                }}>
                {cat.name}
              </button>
            ))}
            {isAdmin && (
              showCatForm ? (
                <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                  <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                    placeholder="カテゴリ名" style={{ ...inputStyle, width:"130px", margin:0 }} />
                  <button onClick={handleAddCategory} disabled={addingCat} style={{ ...addBtnStyle, whiteSpace:"nowrap" }}>追加</button>
                  <button onClick={() => { setShowCatForm(false); setNewCatName(""); }} style={deleteBtnStyle}>×</button>
                </div>
              ) : (
                <button onClick={() => setShowCatForm(true)} style={addBtnStyle}>＋ カテゴリ追加</button>
              )
            )}
          </div>

          {selectedCat && (
            <>
              {/* 社員・パートナー：アップロードフォーム */}
              {!isAdmin && (
                <div style={{ background: C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"16px" }}>
                  <div style={{ fontSize:"13px", fontWeight:"600", color: C.navy, marginBottom:"4px" }}>{selectedCat.name}</div>
                  <div style={{ fontSize:"11px", color: C.gray3, marginBottom:"14px" }}>
                    {myDoc ? `提出済み（${new Date(myDoc.updated_at || myDoc.created_at).toLocaleString("ja-JP")}）` : "未提出"}
                  </div>
                  {sent ? (
                    <div style={{ textAlign:"center", padding:"12px 0" }}>
                      <div style={{ fontSize:"28px", marginBottom:"6px" }}>✅</div>
                      <div style={{ fontSize:"13px", color: C.green, fontWeight:"600", marginBottom:"10px" }}>提出しました！</div>
                      <button onClick={() => setSent(false)} style={{ ...primaryBtnStyle(false), width:"auto", padding:"8px 20px" }}>再提出する</button>
                    </div>
                  ) : (
                    <>
                      {error && <div style={{ color: C.red, fontSize:"12px", marginBottom:"10px", padding:"8px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
                      <label style={{ ...primaryBtnStyle(uploading), display:"block", textAlign:"center", cursor: uploading ? "not-allowed":"pointer", boxSizing:"border-box" }}>
                        {uploading ? "送信中..." : myDoc ? "📷 再提出する" : "📷 写真を提出する"}
                        <input type="file" accept="image/*" capture="environment" onChange={handleUpload} disabled={uploading} style={{ display:"none" }} />
                      </label>
                    </>
                  )}
                </div>
              )}

              {/* 安全管理室：提出状況一覧 */}
              {isAdmin && (
                <div>
                  <div style={{ fontSize:"13px", fontWeight:"600", color: C.navy, marginBottom:"10px" }}>提出状況</div>
                  {docsLoading ? (
                    <div style={{ textAlign:"center", padding:"20px", color: C.gray3, fontSize:"12px" }}>読み込み中...</div>
                  ) : users.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"20px", color: C.gray3, fontSize:"12px" }}>ユーザーがいません</div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                      {users.map(u => {
                        const doc = docs.find(d => String(d.uploaded_by) === String(u.id));
                        return (
                          <div key={u.id} style={{
                            background: C.white, borderRadius:"14px", padding:"12px 16px",
                            boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
                            display:"flex", alignItems:"center", gap:"12px",
                          }}>
                            {doc ? (
                              <img src={`${API}/uploads/safety_cycle/${doc.file_name}`} alt=""
                                style={{ width:"56px", height:"56px", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.gray2}`, cursor:"pointer", flexShrink:0 }}
                                onClick={() => setPopupPhoto({ url:`${API}/uploads/safety_cycle/${doc.file_name}`, name: doc.file_name })} />
                            ) : (
                              <div style={{ width:"56px", height:"56px", borderRadius:"8px", background: C.gray1, border:`1px solid ${C.gray2}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px", flexShrink:0 }}>📄</div>
                            )}
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:"13px", fontWeight:"600", color: C.text }}>{u.name}</div>
                              <div style={{ fontSize:"11px", color: C.gray3, marginTop:"2px" }}>{u.role === "partner" ? "パートナー" : "社員"}</div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              {doc ? (
                                <>
                                  <div style={{ fontSize:"11px", fontWeight:"600", color: C.green }}>✅ 提出済み</div>
                                  <div style={{ fontSize:"10px", color: C.gray3, marginTop:"2px" }}>{new Date(doc.updated_at || doc.created_at).toLocaleString("ja-JP")}</div>
                                  <button onClick={() => setPopupPhoto({ url:`${API}/uploads/safety_cycle/${doc.file_name}`, name: doc.file_name })}
                                    style={{ fontSize:"10px", color: C.blue, background:"none", border:"none", cursor:"pointer", marginTop:"4px", padding:0 }}>
                                    🔍 表示
                                  </button>
                                </>
                              ) : (
                                <div style={{ fontSize:"11px", fontWeight:"600", color: C.red }}>⬜ 未提出</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* 写真ポップアップ（安全サイクル） */}
      {popupPhoto && (
        <div onClick={() => setPopupPhoto(null)} style={{
          position:"fixed", inset:0, zIndex:900, background:"rgba(0,0,0,0.85)",
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px",
        }}>
          <img src={popupPhoto.url} alt={popupPhoto.name}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth:"100%", maxHeight:"75dvh", borderRadius:"12px", objectFit:"contain" }} />
          <div style={{ display:"flex", gap:"12px", marginTop:"16px" }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { const a=document.createElement('a'); a.href=popupPhoto.url; a.download=popupPhoto.name||'photo'; a.target='_blank'; a.click(); }}
              style={{ padding:"10px 24px", background:C.blue, color:C.white, border:"none", borderRadius:"12px", fontSize:"14px", fontWeight:"700", cursor:"pointer" }}>💾 保存</button>
            <button onClick={() => setPopupPhoto(null)}
              style={{ padding:"10px 24px", background:"rgba(255,255,255,0.15)", color:C.white, border:"1px solid rgba(255,255,255,0.3)", borderRadius:"12px", fontSize:"14px", fontWeight:"700", cursor:"pointer" }}>✕ 閉じる</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// 安全教育資料
// ================================
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// 画像を長辺1600px・JPEG品質0.8に縮小して data URL で返す
function resizeImage(file, maxSide = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });
}

function formatBytes(bytes) {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function EducationPage({ user, token }) {
  const isMobile = useIsMobile();
  const isAdmin = user.role === "safety_admin";
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [sel, setSel] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/education_materials.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMaterials(Array.isArray(data) ? data : []);
    } catch { setError("読み込みに失敗しました"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) { setError("タイトルとファイルを入力してください"); return; }
    setUploading(true); setError("");
    try {
      const fileData = await fileToBase64(file);
      const res = await fetch(`${API}/education_materials.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: title.trim(), fileName: file.name, fileData }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "アップロードに失敗しました"); setUploading(false); return; }
      setTitle(""); setFile(null); setShowForm(false);
      await load();
    } catch { setError("アップロードに失敗しました"); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("この資料を削除しますか？")) return;
    try {
      const res = await fetch(`${API}/education_materials.php?id=${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const data = await res.json().catch(()=>({})); setError(data.error || "削除に失敗しました"); return; }
      await load();
    } catch { setError("削除に失敗しました"); }
  };

  const extOf = (n) => (String(n||"").split(".").pop() || "").toLowerCase();
  const typeOf = (n) => {
    const e = extOf(n);
    if (e === "pdf") return { key:"pdf", label:"PDF", color:"#E5484D", bg:"#FFF0F0", preview:"frame" };
    if (["ppt","pptx"].includes(e)) return { key:"ppt", label:"PowerPoint", color:"#D9822B", bg:"#FFF5E8", preview:null };
    if (["doc","docx"].includes(e)) return { key:"doc", label:"Word", color:"#2B6CD9", bg:"#EAF1FF", preview:null };
    if (["png","jpg","jpeg","gif","webp"].includes(e)) return { key:"img", label:"画像", color:"#2E9E6B", bg:"#E8F7EF", preview:"img" };
    return { key:"other", label:e.toUpperCase() || "FILE", color:"#6B7A99", bg:"#EEF1F6", preview:null };
  };
  const fileUrl = (m) => `${API}/uploads/education/${m.file_name}`;
  const shown = materials.filter(m => {
    if (typeF !== "all" && typeOf(m.file_name).key !== typeF) return false;
    const k = q.trim().toLowerCase();
    return !k || String(m.title||"").toLowerCase().includes(k) || String(m.file_name||"").toLowerCase().includes(k);
  });
  const cur = materials.find(m => m.id === sel) || (!isMobile ? shown[0] : null);
  const curT = cur ? typeOf(cur.file_name) : null;
  const typeKeys = ["all", ...Array.from(new Set(materials.map(m => typeOf(m.file_name).key)))];
  const typeLabel = (k) => k === "all" ? "すべて" : ({ pdf:"PDF", ppt:"PowerPoint", doc:"Word", img:"画像", other:"その他" })[k];

  const Preview = ({ m, t }) => (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", minHeight:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"14px 16px", borderBottom:`1px solid ${C.gray1 || "#E6EAF2"}` }}>
        <span style={{ fontSize:"11px", fontWeight:"700", color:t.color, background:t.bg, borderRadius:"6px", padding:"3px 8px" }}>{t.label}</span>
        <div style={{ flex:1, minWidth:0, fontSize:"14px", fontWeight:"700", color:C.navy, wordBreak:"break-word" }}>{m.title}</div>
        <a href={fileUrl(m)} target="_blank" rel="noreferrer" style={{ ...addBtnStyle, textDecoration:"none", color:C.blue, borderColor:C.blue }}>別タブで開く</a>
        <a href={fileUrl(m)} download style={{ ...addBtnStyle, textDecoration:"none", color:C.blue, borderColor:C.blue }}>⬇ DL</a>
      </div>
      <div style={{ flex:1, minHeight:0, background:"#F3F5FA", display:"flex", alignItems:"center", justifyContent:"center" }}>
        {t.preview === "frame" ? (
          <iframe title={m.title} src={`${fileUrl(m)}#toolbar=0&navpanes=0`} style={{ width:"100%", height:"100%", border:"none", background:"#fff" }} />
        ) : t.preview === "img" ? (
          <img src={fileUrl(m)} alt={m.title} style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
        ) : (
          <div style={{ textAlign:"center", color:C.gray3, padding:"24px" }}>
            <div style={{ fontSize:"54px", marginBottom:"10px" }}>📄</div>
            <div style={{ fontSize:"13px", marginBottom:"4px" }}>{t.label}ファイルはプレビューできません</div>
            <div style={{ fontSize:"12px" }}>「DL」からダウンロードしてください</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:"1200px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
        <div style={{ fontSize:"16px", fontWeight:"700", color: C.navy }}>📚 安全教育資料
          <span style={{ fontSize:"12px", fontWeight:"600", color:C.gray3, marginLeft:"10px" }}>{materials.length}件</span>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowForm(v => !v); setError(""); }} style={addBtnStyle}>
            {showForm ? "閉じる" : "＋ 資料を追加"}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <div style={{ background: C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"16px", maxWidth:"720px" }}>
          <form onSubmit={handleUpload}>
            <Row label="タイトル *">
              <input value={title} onChange={e => setTitle(e.target.value)} required style={inputStyle} placeholder="例：感電災害防止教育資料" />
            </Row>
            <Row label="ファイル（PDF等） *">
              <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,image/*"
                onChange={e => setFile(e.target.files?.[0] || null)} style={inputStyle} />
            </Row>
            {error && <div style={{ color: C.red, fontSize:"12px", marginBottom:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
            <button type="submit" disabled={uploading} style={primaryBtnStyle(uploading)}>
              {uploading ? "アップロード中..." : "アップロードする"}
            </button>
          </form>
        </div>
      )}

      {!showForm && error && <div style={{ color: C.red, fontSize:"12px", marginBottom:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign:"center", padding:"32px", color: C.gray3, fontSize:"12px" }}>読み込み中...</div>
      ) : materials.length === 0 ? (
        <div style={{ background: C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color: C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:"36px", marginBottom:"10px" }}>📂</div>
          <div style={{ fontSize:"13px" }}>まだ資料がありません</div>
        </div>
      ) : (
        <div style={{ display:"flex", gap:"16px", alignItems:"stretch", height: isMobile ? "auto" : "calc(100vh - 210px)", minHeight: isMobile ? 0 : "420px" }}>
          <div style={{ width: isMobile ? "100%" : "380px", flexShrink:0, display:"flex", flexDirection:"column", minHeight:0 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 資料名で検索"
              style={{ ...inputStyle, marginBottom:"10px", background:C.white }} />
            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"12px" }}>
              {typeKeys.map(k => (
                <button key={k} onClick={() => setTypeF(k)} style={{
                  border:"none", cursor:"pointer", borderRadius:"999px", padding:"5px 12px", fontSize:"12px", fontWeight:"600",
                  background: typeF === k ? C.navy : C.white, color: typeF === k ? "#fff" : C.navy,
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
                }}>{typeLabel(k)}</button>
              ))}
            </div>
            <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:"8px", minHeight:0 }}>
              {shown.length === 0 && (
                <div style={{ textAlign:"center", color:C.gray3, fontSize:"12px", padding:"24px" }}>該当する資料がありません</div>
              )}
              {shown.map(m => {
                const t = typeOf(m.file_name);
                const active = cur && cur.id === m.id;
                return (
                  <div key={m.id} onClick={() => setSel(m.id)} style={{
                    background: C.white, borderRadius:"14px", padding:"12px 14px", cursor:"pointer",
                    boxShadow: active ? `0 0 0 2px ${C.blue}, 0 2px 8px rgba(0,0,0,0.08)` : "0 2px 8px rgba(0,0,0,0.06)",
                    display:"flex", alignItems:"center", gap:"12px",
                  }}>
                    <div style={{ width:"42px", height:"42px", borderRadius:"10px", background:t.bg, color:t.color, fontSize:"11px", fontWeight:"800",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{t.label === "PowerPoint" ? "PPT" : t.label}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:"13px", fontWeight:"700", color: C.text, marginBottom:"3px", wordBreak:"break-word" }}>{m.title}</div>
                      <div style={{ fontSize:"11px", color: C.gray3 }}>
                        {m.created_at ? new Date(String(m.created_at).replace(" ","T")).toLocaleDateString("ja-JP") : ""}{m.file_size ? ` ・ ${formatBytes(m.file_size)}` : ""}
                      </div>
                    </div>
                    {isAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} style={deleteBtnStyle}>🗑️</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!isMobile && (
            <div style={{ flex:1, minWidth:0, background:C.white, borderRadius:"16px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", overflow:"hidden" }}>
              {cur ? Preview({ m: cur, t: curT }) : (
                <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", color:C.gray3, fontSize:"13px" }}>資料を選ぶとプレビューが表示されます</div>
              )}
            </div>
          )}
        </div>
      )}

      {isMobile && cur && (
        <div style={{ position:"fixed", inset:0, zIndex:3000, background:C.white, display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"10px 12px" }}>
            <button onClick={() => setSel(null)} style={addBtnStyle}>← 一覧に戻る</button>
          </div>
          <div style={{ flex:1, minHeight:0 }}>{Preview({ m: cur, t: curT })}</div>
        </div>
      )}
    </div>
  );
}

// ================================
// スケジュール（準備中）
// ================================
function SchedulePage({ user, token }) {
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [search, setSearch]       = useState("");
  const [weekOffset, setWeekOffset] = useState(0);

  const emptyForm = {
    workDate:   new Date().toISOString().slice(0, 10),
    location:   "",
    assignee:   "",
    memo:       "",
    department: user.departments?.[0] || "",
  };
  const [form, setForm] = useState(emptyForm);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // 週の開始日（月曜）を計算
  const getWeekStart = (offset) => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff + offset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const weekStart = getWeekStart(weekOffset);
  const weekDays  = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const load = () => {
    setLoading(true);
    fetch(`${API}/schedules.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.schedules) setSchedules(data.schedules);
        if (data.users)     setUsers(data.users);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    setError(""); setSaving(true);
    if (!form.workDate || !form.location || !form.assignee) {
      setError("日付・作業場所・社内担当者は必須です"); setSaving(false); return;
    }
    try {
      const method = editTarget ? "PATCH" : "POST";
      const url    = editTarget ? `${API}/schedules.php?id=${editTarget.id}` : `${API}/schedules.php`;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "保存に失敗しました"); setSaving(false); return; }
      setShowForm(false); setEditTarget(null); setForm(emptyForm);
      load();
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このスケジュールを削除しますか？")) return;
    await fetch(`${API}/schedules.php?id=${id}`, {
      method: "DELETE", headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };

  const DAY_LABELS = ["月", "火", "水", "木", "金", "土", "日"];

  // ユーザーフィルタ
  const filteredUsers = users.filter(u => {
    if (filterDept !== "all") {
      const depts = Array.isArray(u.departments) ? u.departments : [];
      if (!depts.includes(filterDept)) return false;
    }
    if (search) {
      if (!u.name?.includes(search)) return false;
    }
    return true;
  });

  // 日付→スケジュールのマップ
  const scheduleMap = {};
  schedules.forEach(s => {
    const key = `${s.user_id}_${s.work_date}`;
    if (!scheduleMap[key]) scheduleMap[key] = [];
    scheduleMap[key].push(s);
  });

  const weekLabel = `${weekStart.getMonth()+1}/${weekStart.getDate()} 〜 ${weekDays[6].getMonth()+1}/${weekDays[6].getDate()}`;

  if (loading) return <div style={{ textAlign:"center", padding:"40px", color: C.gray3 }}>読み込み中...</div>;

  return (
    <div>
      {/* ヘッダー */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
        <span style={{ fontWeight:"700", fontSize:"15px", color: C.text }}>スケジュール</span>
        <button onClick={() => { setEditTarget(null); setForm(emptyForm); setError(""); setShowForm(true); }}
          style={{ ...primaryBtnStyle(false), padding:"7px 16px", width:"auto" }}>＋ 追加</button>
      </div>

      {/* 週ナビ */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"12px", background: C.white, borderRadius:"12px", padding:"10px 16px", boxShadow:"0 2px 6px rgba(0,0,0,0.05)" }}>
        <button onClick={() => setWeekOffset(o => o-1)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"18px", color: C.textSub }}>‹</button>
        <span style={{ flex:1, textAlign:"center", fontWeight:"700", fontSize:"13px", color: C.text }}>{weekLabel}</span>
        <button onClick={() => setWeekOffset(0)} style={{ padding:"3px 10px", background: C.gray1, border:`1px solid ${C.gray2}`, borderRadius:"8px", cursor:"pointer", fontSize:"11px", color: C.textSub }}>今週</button>
        <button onClick={() => setWeekOffset(o => o+1)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"18px", color: C.textSub }}>›</button>
      </div>

      {/* 課フィルタ */}
      <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"10px" }}>
        {["all", ...DEPARTMENTS].map(d => (
          <button key={d} onClick={() => setFilterDept(d)} style={{
            padding:"4px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:"600",
            cursor:"pointer", border:"none", transition:"all .15s",
            background: filterDept === d ? C.navy : C.white,
            color:      filterDept === d ? C.white : C.textSub,
            boxShadow:  filterDept === d ? "0 2px 6px rgba(26,42,74,0.2)" : "none",
          }}>{d === "all" ? "すべて" : d}</button>
        ))}
      </div>

      {/* 名前検索 */}
      <div style={{ marginBottom:"12px", position:"relative" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 名前で検索..."
          style={{ ...inputStyle, paddingLeft:"14px", fontSize:"12px" }}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = C.gray2} />
        {search && (
          <button onClick={() => setSearch("")} style={{ position:"absolute", right:"10px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color: C.gray3, fontSize:"16px" }}>×</button>
        )}
      </div>

      {/* 週グリッドヘッダー */}
      <div style={{ display:"grid", gridTemplateColumns:"80px repeat(7, 1fr)", gap:"2px", marginBottom:"4px" }}>
        <div />
        {weekDays.map((d, i) => {
          const isToday = d.toDateString() === new Date().toDateString();
          const isSat   = i === 5;
          const isSun   = i === 6;
          return (
            <div key={i} style={{
              textAlign:"center", fontSize:"11px", fontWeight:"700", padding:"4px 2px",
              borderRadius:"6px",
              background: isToday ? C.navy : "transparent",
              color: isToday ? C.white : isSat ? C.blue : isSun ? C.red : C.textSub,
            }}>
              {d.getMonth()+1}/{d.getDate()}<br/>
              <span style={{ fontSize:"10px" }}>{DAY_LABELS[i]}</span>
            </div>
          );
        })}
      </div>

      {/* ユーザー行 */}
      {filteredUsers.length === 0 ? (
        <div style={{ background: C.white, borderRadius:"16px", padding:"32px", textAlign:"center", color: C.gray3, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          該当するユーザーがいません
        </div>
      ) : filteredUsers.map(u => {
        const roleColor = u.role === "safety_admin" ? C.blue : u.role === "employee" ? "#2a5a3a" : "#5a3a1a";
        return (
          <div key={u.id} style={{ display:"grid", gridTemplateColumns:"80px repeat(7, 1fr)", gap:"2px", marginBottom:"4px" }}>
            {/* ユーザー名 */}
            <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"4px 6px", background: C.white, borderRadius:"8px", border:`1px solid ${C.gray2}` }}>
              <div style={{ width:"22px", height:"22px", borderRadius:"50%", flexShrink:0, background: roleColor+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", fontWeight:"700", color: roleColor }}>
                {u.name?.charAt(0)}
              </div>
              <span style={{ fontSize:"10px", fontWeight:"600", color: C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.name}</span>
            </div>
            {/* 各日のセル */}
            {weekDays.map((d, i) => {
              const dateStr = d.toISOString().slice(0, 10);
              const key     = `${u.id}_${dateStr}`;
              const dayScheds = scheduleMap[key] || [];
              const isToday  = d.toDateString() === new Date().toDateString();
              const isSat    = i === 5;
              const isSun    = i === 6;
              return (
                <div key={i} style={{
                  minHeight:"48px", borderRadius:"6px", padding:"2px",
                  background: isToday ? "#EBF3FC" : isSat ? "#EBF0FB" : isSun ? "#FCEBEB" : C.white,
                  border:`1px solid ${C.gray2}`,
                  position:"relative",
                }}>
                  {dayScheds.map((s, si) => (
                    <div key={si} onClick={() => {
                      if (user.role === "partner" && s.user_id !== user.userId) return;
                      setEditTarget(s);
                      setForm({ workDate: s.work_date, location: s.location, assignee: s.assignee, memo: s.memo || "", department: s.department || "" });
                      setError(""); setShowForm(true);
                    }} style={{
                      fontSize:"9px", lineHeight:"1.3", padding:"2px 4px",
                      background: C.navy, color: C.white, borderRadius:"4px",
                      marginBottom:"2px", cursor:"pointer",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                    }} title={`${s.location} / ${s.assignee}`}>
                      {s.location}
                    </div>
                  ))}
                  {/* 自分か管理者か社員は＋ボタン */}
                  {(user.role === "safety_admin" || user.role === "employee" || u.id == user.userId) && dayScheds.length === 0 && (
                    <div onClick={() => {
                      setEditTarget(null);
                      setForm({ ...emptyForm, workDate: dateStr });
                      setError(""); setShowForm(true);
                    }} style={{
                      position:"absolute", bottom:"2px", right:"2px",
                      fontSize:"14px", color: C.gray3, cursor:"pointer", lineHeight:1,
                      opacity:0.5,
                    }}>＋</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* 登録・編集モーダル */}
      {showForm && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"400px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)", maxHeight:"90dvh", overflowY:"auto",
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" }}>
              <div style={{ fontSize:"16px", fontWeight:"700", color: C.text }}>
                {editTarget ? "✏️ スケジュール編集" : "📅 スケジュール追加"}
              </div>
              {editTarget && (
                <button onClick={() => handleDelete(editTarget.id)} style={{ background:"#FFF0F0", border:"none", borderRadius:"8px", padding:"6px 10px", cursor:"pointer", fontSize:"12px", color: C.red, fontWeight:"600" }}>
                  🗑️ 削除
                </button>
              )}
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>日付 ※</label>
              <input type="date" value={form.workDate} onChange={e => set("workDate", e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>作業場所 ※</label>
              <input value={form.location} onChange={e => set("location", e.target.value)}
                placeholder="例：○○局、△△現場" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>社内担当者 ※</label>
              <input value={form.assignee} onChange={e => set("assignee", e.target.value)}
                placeholder="例：山田 太郎" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>課</label>
              <select value={form.department} onChange={e => set("department", e.target.value)}
                style={{ ...inputStyle, cursor:"pointer" }}>
                <option value="">---</option>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:"20px" }}>
              <label style={labelStyle}>メモ</label>
              <textarea value={form.memo} onChange={e => set("memo", e.target.value)}
                placeholder="作業内容、注意事項など" rows={3}
                style={{ ...inputStyle, resize:"vertical", lineHeight:"1.6" }}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            {error && (
              <div style={{ background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px", padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px" }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleSubmit} disabled={saving} style={primaryBtnStyle(saving)}>
                {saving ? "保存中..." : editTarget ? "更新する" : "追加する"}
              </button>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }} style={cancelBtnStyle}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// 設定（権限設定 + ユーザー管理）
// ================================
function Settings({ token, user }) {
  const isAdmin   = user.role === "safety_admin" || user.role === "manager";
  const canManageUsers = isAdmin;
  const [tabRaw, setTab] = useStoredState("tfe_settings_tab", "password");
  const tab = (tabRaw === "password" || (isAdmin && ["permissions","users","organizations"].includes(tabRaw))) ? tabRaw : "password";

  return (
    <div>
      <div style={{ display:"flex", gap:"8px", marginBottom:"16px", flexWrap:"wrap" }}>
        {[
          { key:"password", label:"🔑 パスワード変更" },
          ...(isAdmin ? [{ key:"permissions", label:"⚙️ 権限設定" }] : []),
          ...(canManageUsers ? [{ key:"users", label:"👥 ユーザー管理" }] : []),
          ...(isAdmin ? [{ key:"organizations", label:"🤝 組織管理" }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:"8px 16px", borderRadius:"10px", fontSize:"13px", fontWeight:"600",
            cursor:"pointer", border:"none", transition:"all .15s",
            background: tab === t.key ? C.navy : C.white,
            color:      tab === t.key ? C.white : C.textSub,
            boxShadow:  tab === t.key ? "0 2px 8px rgba(26,42,74,0.25)" : "none",
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "password"      && <PasswordChangePanel token={token} user={user} />}
      {tab === "permissions"   && isAdmin && <PermissionsPanel token={token} />}
      {tab === "users"         && canManageUsers && <UsersPanel token={token} canSearch={user.role === "safety_admin"} />}
      {tab === "organizations" && isAdmin && <OrganizationsPanel token={token} />}
    </div>
  );
}

// ================================
// パスワード変更パネル
// ================================
function PasswordChangePanel({ token, user }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = async () => {
    if (!currentPw) { setError("現在のパスワードを入力してください"); return; }
    if (newPw.length < 4) { setError("新しいパスワードは4文字以上で入力してください"); return; }
    if (newPw !== confirmPw) { setError("新しいパスワードが一致しません"); return; }
    setSaving(true); setError(""); setSuccess(false);
    try {
      const res = await fetch(`${API}/users.php?action=change_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "変更に失敗しました"); setSaving(false); return; }
      setSuccess(true);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch { setError("変更に失敗しました"); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth:"400px" }}>
      <div style={{ background:C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:"13px", fontWeight:"600", color:C.navy, marginBottom:"16px" }}>パスワード変更</div>
        <Row label="現在のパスワード">
          <input type="password" value={currentPw} onChange={e=>setCurrentPw(e.target.value)} style={inputStyle} placeholder="現在のパスワード" />
        </Row>
        <Row label="新しいパスワード">
          <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} style={inputStyle} placeholder="4文字以上" />
        </Row>
        <Row label="新しいパスワード（確認）">
          <input type="password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} style={inputStyle} placeholder="もう一度入力" />
        </Row>
        {error && <div style={{ color:C.red, fontSize:"12px", marginBottom:"12px", padding:"10px", background:"#FFF0F0", borderRadius:"8px" }}>{error}</div>}
        {success && <div style={{ color:C.green, fontSize:"12px", marginBottom:"12px", padding:"10px", background:"#EAF3DE", borderRadius:"8px" }}>パスワードを変更しました！</div>}
        <button onClick={handleChange} disabled={saving} style={primaryBtnStyle(saving)}>
          {saving ? "変更中..." : "変更する"}
        </button>
      </div>
    </div>
  );
}

// ================================
// 組織管理パネル
// ================================
function OrganizationsPanel({ token }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/organizations.php`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (Array.isArray(data)) setOrgs(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) { setError("会社名を入力してください"); return; }
    setSaving(true); setError(""); setSuccess("");
    try {
      const res = await fetch(`${API}/organizations.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newName.trim(), orgType: "partner" }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "追加に失敗しました"); setSaving(false); return; }
      setNewName("");
      setSuccess(`「${newName.trim()}」を追加しました`);
      load();
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`「${org.name}」を削除しますか？\n※所属ユーザーがいる場合は削除できません`)) return;
    setDeletingId(org.id); setError(""); setSuccess("");
    try {
      const res = await fetch(`${API}/organizations.php?id=${org.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "削除に失敗しました"); setDeletingId(null); return; }
      setSuccess(`「${org.name}」を削除しました`);
      load();
    } catch { setError("サーバーに接続できません"); }
    setDeletingId(null);
  };

  const tfeOrg = orgs.find(o => o.org_type === 'main');
  const partnerOrgList = orgs.filter(o => o.org_type !== 'main');

  return (
    <div style={{ maxWidth:"480px" }}>
      <div style={{ background:C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"16px" }}>
        <div style={{ fontSize:"13px", fontWeight:"700", color:C.navy, marginBottom:"16px" }}>🏢 自社</div>
        {tfeOrg && (
          <div style={{ padding:"12px 16px", background:C.gray1, borderRadius:"10px", fontSize:"13px", color:C.text, fontWeight:"600" }}>
            {tfeOrg.name}
            <span style={{ marginLeft:"8px", fontSize:"11px", color:C.textSub, fontWeight:"400" }}>（変更不可）</span>
          </div>
        )}
      </div>

      <div style={{ background:C.white, borderRadius:"16px", padding:"20px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:"13px", fontWeight:"700", color:C.navy, marginBottom:"16px" }}>🤝 パートナー会社</div>

        {/* 新規追加 */}
        <div style={{ display:"flex", gap:"8px", marginBottom:"16px" }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="会社名を入力"
            style={{ ...inputStyle, flex:1, marginBottom:0 }}
            onFocus={e => e.target.style.borderColor = C.blue}
            onBlur={e => e.target.style.borderColor = C.gray2}
          />
          <button onClick={handleAdd} disabled={saving} style={{
            padding:"10px 16px", borderRadius:"10px", fontSize:"12px", fontWeight:"700",
            background: saving ? C.gray2 : C.navy, color: saving ? C.textSub : C.white,
            border:"none", cursor: saving ? "default" : "pointer", whiteSpace:"nowrap",
          }}>
            {saving ? "追加中..." : "+ 追加"}
          </button>
        </div>

        {error && <div style={{ background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"8px", padding:"10px 14px", fontSize:"12px", color:C.red, marginBottom:"12px" }}>⚠️ {error}</div>}
        {success && <div style={{ background:"#EAF3DE", border:`1px solid #C3E6A0`, borderRadius:"8px", padding:"10px 14px", fontSize:"12px", color:C.green, marginBottom:"12px" }}>✓ {success}</div>}

        {loading ? (
          <div style={{ textAlign:"center", color:C.textSub, fontSize:"12px", padding:"20px" }}>読み込み中...</div>
        ) : partnerOrgList.length === 0 ? (
          <div style={{ textAlign:"center", color:C.textSub, fontSize:"12px", padding:"20px" }}>パートナー会社はまだ登録されていません</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            {partnerOrgList.map(org => (
              <div key={org.id} style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"12px 16px", background:C.gray1, borderRadius:"10px",
              }}>
                <span style={{ fontSize:"13px", fontWeight:"600", color:C.text }}>🤝 {org.name}</span>
                <button
                  onClick={() => handleDelete(org)}
                  disabled={deletingId === org.id}
                  style={{
                    padding:"5px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"600",
                    background:"#FFF0F0", color:C.red, border:`1px solid #FFCCCC`,
                    cursor: deletingId === org.id ? "default" : "pointer", opacity: deletingId === org.id ? 0.5 : 1,
                  }}>
                  {deletingId === org.id ? "削除中..." : "削除"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ================================
// 権限設定パネル
// ================================
function PermissionsPanel({ token }) {
  const [perms, setPerms] = useState({
    employee: { reports:true, trouble_reports:true,  process:true, schedule:true },
    partner:  { reports:true, trouble_reports:false, process:true, schedule:true },
  });
  const [loading, setLoading] = useState(true);

  const screens = [
    { key:"reports",         label:"報告一覧" },
    { key:"trouble_reports", label:"トラブル報告" },
    { key:"process",         label:"工程×リスク" },
    { key:"schedule",        label:"スケジュール" },
  ];
  const roles = [
    { key:"employee", label:"社員" },
    { key:"partner",  label:"パートナー" },
  ];

  useEffect(() => {
    fetch(`${API}/permissions.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const built = { employee:{}, partner:{} };
        data.forEach(p => { if (built[p.role]) built[p.role][p.screenKey] = p.isVisible; });
        setPerms(prev => ({
          employee: { ...prev.employee, ...built.employee },
          partner:  { ...prev.partner,  ...built.partner  },
        }));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleToggle = async (role, screenKey) => {
    const newVal = !perms[role][screenKey];
    setPerms(prev => ({ ...prev, [role]: { ...prev[role], [screenKey]: newVal } }));
    await fetch(`${API}/permissions.php`, {
      method:"PATCH",
      headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body: JSON.stringify({ role, screenKey, isVisible: newVal }),
    }).catch(() => {});
  };

  if (loading) return <div style={{ textAlign:"center", padding:"40px", color: C.gray3 }}>読み込み中...</div>;

  return (
    <div>
      <div style={{ background: C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.gray2}`, fontSize:"12px", color: C.textSub }}>
          画面の表示・非表示を設定します（安全管理室は常に全画面表示）
        </div>
        {screens.map((s,i) => (
          <div key={s.key} style={{ padding:"14px 16px", borderBottom: i < screens.length-1 ? `1px solid ${C.gray2}` : "none" }}>
            <div style={{ fontSize:"13px", fontWeight:"600", color: C.text, marginBottom:"10px" }}>{s.label}</div>
            <div style={{ display:"flex", gap:"16px" }}>
              {roles.map(r => {
                const isOn = perms[r.key][s.key] !== false;
                return (
                  <div key={r.key} style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                    <div onClick={() => handleToggle(r.key, s.key)} style={{
                      width:"40px", height:"22px", borderRadius:"11px",
                      background: isOn ? C.green : C.gray2,
                      cursor:"pointer", position:"relative", transition:"background .2s",
                      flexShrink:0,
                    }}>
                      <div style={{
                        position:"absolute", top:"3px",
                        left: isOn ? "20px" : "3px",
                        width:"16px", height:"16px", borderRadius:"50%",
                        background: C.white, transition:"left .2s",
                        boxShadow:"0 1px 3px rgba(0,0,0,0.2)",
                      }} />
                    </div>
                    <span style={{ fontSize:"12px", color: C.textSub }}>{r.label}</span>
                    <span style={{ fontSize:"10px", color: isOn ? C.green : C.gray3, fontWeight:"600" }}>
                      {isOn ? "表示" : "非表示"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================================
// ユーザー管理パネル
// ================================
const DEPARTMENTS = ["本社(総務経理課)", "各部", "NE課", "東京営業所", "ホームソリューション課", "ICTエンジニアリング課", "第一エンジニアリング課", "第二エンジニアリング課", "安全管理室"];

const ROLE_LABELS = {
  safety_admin:      "安全管理室",
  employee_manager:  "社員（管理職）",
  employee:          "社員（一般）",
  employee_dispatch: "社員（派遣・業務委託）",
  partner:           "パートナー",
};
const ROLE_COLORS = {
  safety_admin:      C.blue,
  employee_manager:  "#1a5c3a",
  employee:          "#2a5a3a",
  employee_dispatch: "#4a5a2a",
  partner:           "#5a3a1a",
};

function UsersPanel({ token, canSearch = false }) {
  const [users, setUsers]     = useState([]);
  const [orgs, setOrgs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");

  // タブ: "tfe" | "partner"
  const [userTabRaw, setUserTab] = useStoredState("tfe_users_tab", "tfe");
  const userTab = userTabRaw === "partner" ? "partner" : "tfe";
  // パートナータブ用: 選択中の会社
  const [partnerOrgId, setPartnerOrgId] = useState(null);
  // TFEタブ用: 課フィルタ
  const [filterDept, setFilterDept] = useState("all");
  // 名前検索（社員・パートナー横断）
  const [userSearch, setUserSearch] = useState("");

  // モーダル種別: null | "add" | "edit" | "password" | "delete"
  const [modal, setModal] = useState(null);
  const [targetUser, setTargetUser] = useState(null);

  const emptyForm = { name:"", email:"", password:"", role:"employee", organizationId:"", departments:[] };
  const [form, setForm] = useState(emptyForm);
  const [newPassword, setNewPassword] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/users.php`,         { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/organizations.php`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([u, o]) => {
      if (Array.isArray(u)) setUsers(u);
      if (Array.isArray(o)) {
        setOrgs(o);
        // TFE自社の組織IDをデフォルトに
        const tfeOrg = o.find(org => org.org_type === 'main') || o[0];
        if (tfeOrg) setForm(prev => ({ ...prev, organizationId: tfeOrg.id }));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // TFE自社組織とパートナー会社組織を分ける（org_type: 'main' が自社）
  const tfeOrg    = orgs.find(o => o.org_type === 'main') || orgs[0];
  const partnerOrgs = orgs.filter(o => o.id !== tfeOrg?.id);

  useEffect(() => { load(); }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  // 課チェックボックストグル
  const toggleDept = (dept) => {
    setForm(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept],
    }));
  };

  // ユーザー登録
  const handleAdd = async () => {
    setError(""); setSaving(true);
    if (!form.name || !form.email || !form.password || !form.organizationId) {
      setError("すべての項目を入力してください"); setSaving(false); return;
    }
    if (users.some(u => String(u.email || "").toLowerCase() === String(form.email).trim().toLowerCase())) {
      setError("このメールアドレスは、すでに登録されています"); setSaving(false); return;
    }
    try {
      const res = await fetch(`${API}/users.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({ error: `サーバーエラーが発生しました（${res.status}）` }));
      if (!res.ok) { setError(data.error || "登録に失敗しました"); setSaving(false); return; }
      setModal(null);
      setForm({ ...emptyForm, organizationId: orgs[0]?.id || "" });
      showSuccess("ユーザーを登録しました");
      load();
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  // ユーザー編集
  const handleEdit = async () => {
    setError(""); setSaving(true);
    if (!form.name || !form.role || !form.organizationId) {
      setError("氏名・ロール・所属組織は必須です"); setSaving(false); return;
    }
    try {
      const res = await fetch(`${API}/users.php`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id:             targetUser.id,
          name:           form.name,
          role:           form.role,
          departments:    form.departments,
          organizationId: form.organizationId,
        }),
      });
      const data = await res.json().catch(() => ({ error: `サーバーエラーが発生しました（${res.status}）` }));
      if (!res.ok) { setError(data.error || "更新に失敗しました"); setSaving(false); return; }
      setModal(null);
      showSuccess(`${form.name} の情報を更新しました`);
      load();
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  // パスワード変更
  const handlePassword = async () => {
    setError(""); setSaving(true);
    if (!newPassword || newPassword.length < 4) {
      setError("4文字以上のパスワードを入力してください"); setSaving(false); return;
    }
    try {
      const res = await fetch(`${API}/users.php`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: targetUser.id, newPassword }),
      });
      const data = await res.json().catch(() => ({ error: `サーバーエラーが発生しました（${res.status}）` }));
      if (!res.ok) { setError(data.error || "変更に失敗しました"); setSaving(false); return; }
      setModal(null); setNewPassword("");
      showSuccess(`${targetUser.name} のパスワードを変更しました`);
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  // ユーザー削除
  const handleDelete = async () => {
    setError(""); setSaving(true);
    try {
      const res = await fetch(`${API}/users.php?id=${targetUser.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({ error: `サーバーエラーが発生しました（${res.status}）` }));
      if (!res.ok) { setError(data.error || "削除に失敗しました"); setSaving(false); return; }
      setModal(null);
      showSuccess(`${targetUser.name} を削除しました`);
      load();
    } catch { setError("サーバーに接続できません"); }
    setSaving(false);
  };

  // ===== CSV エクスポート =====
  const handleCsvExport = () => {
    const header = ["ID", "氏名", "メールアドレス", "ロール", "課・所属", "有効"];
    const rows = users.map(u => [
      u.id,
      u.name,
      u.email,
      u.role,
      Array.isArray(u.departments) ? u.departments.join("|") : (u.departments || ""),
      u.is_active ? "1" : "0",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const bom = "﻿";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `users_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ===== CSV インポート =====
  const [csvModal, setCsvModal] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvError, setCsvError]   = useState("");
  const [csvImporting, setCsvImporting] = useState(false);

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let text = ev.target.result;
        // BOM除去
        if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) { setCsvError("データ行がありません"); return; }
        const parsed = lines.slice(1).map(line => {
          // シンプルCSVパース（ダブルクォート対応）
          const cols = [];
          let cur = "", inQ = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQ = !inQ; }
            else if (ch === ',' && !inQ) { cols.push(cur); cur = ""; }
            else { cur += ch; }
          }
          cols.push(cur);
          return {
            id:          cols[0]?.replace(/^"|"$/g,"").trim(),
            name:        cols[1]?.replace(/^"|"$/g,"").trim(),
            email:       cols[2]?.replace(/^"|"$/g,"").trim(),
            role:        cols[3]?.replace(/^"|"$/g,"").trim(),
            departments: (cols[4]?.replace(/^"|"$/g,"").trim() || "").split("|").filter(Boolean),
            is_active:   cols[5]?.replace(/^"|"$/g,"").trim() !== "0",
          };
        }).filter(r => r.id && r.name);
        setCsvPreview(parsed);
        setCsvModal(true);
      } catch(err) { setCsvError("CSVの読み込みに失敗しました: " + err.message); }
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const handleCsvImport = async () => {
    if (!csvPreview.length) return;
    setCsvImporting(true); setCsvError("");
    try {
      const res = await fetch(`${API}/users.php`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bulkUpdate: csvPreview }),
      });
      const data = await res.json();
      if (!res.ok) { setCsvError(data.error || "インポートに失敗しました"); setCsvImporting(false); return; }
      setCsvModal(false); setCsvPreview([]);
      showSuccess(`${csvPreview.length}件を更新しました`);
      load();
    } catch { setCsvError("サーバーに接続できません"); }
    setCsvImporting(false);
  };

  // TFE社員: tfeOrgに所属するユーザー
  const tfeUsers = tfeOrg ? users.filter(u => u.organization_id === tfeOrg.id) : [];
  // パートナー: それ以外
  const partnerUsers = users.filter(u => tfeOrg ? u.organization_id !== tfeOrg.id : false);

  // TFEタブ用フィルタ
  const filteredTfeUsers = filterDept === "all"
    ? tfeUsers
    : tfeUsers.filter(u => Array.isArray(u.departments) && u.departments.includes(filterDept));

  // パートナー会社別グループ
  const partnerGroups = partnerOrgs.map(org => ({
    org,
    members: partnerUsers.filter(u => u.organization_id === org.id),
  })).filter(g => g.members.length > 0);

  // 所属（TFE自社 / パートナーのタブ＋パートナーは会社をプルダウンで選ぶ）：登録・編集モーダル共通
  const renderAffiliation = () => {
      const onPartner = form.organizationId !== tfeOrg?.id;
      const goTfe = () => { if (!onPartner) return; setForm(p => ({
        ...p, organizationId: tfeOrg?.id || "", role: p.role === "partner" ? "employee" : p.role, departments: [],
      })); };
      const goPartner = () => { if (onPartner) return; setForm(p => ({
        ...p, organizationId: partnerOrgs[0]?.id || "", role: "partner", departments: [],
      })); };
      const tabBtn = (on, label, onClick) => (
        <button type="button" onClick={onClick} style={{
          flex:1, padding:"9px 8px", fontSize:"12px", fontWeight:"700", border:"none", borderRadius:"10px", cursor:"pointer",
          background: on ? C.white : "transparent", color: on ? C.navy : C.textSub,
          boxShadow: on ? "0 2px 8px rgba(0,0,0,0.08)" : "none", transition:"all .15s",
        }}>{label}</button>
      );
      return (
        <div style={{ marginBottom:"14px" }}>
          <label style={labelStyle}>所属</label>
          <div style={{ display:"flex", background:C.gray1, borderRadius:"12px", padding:"4px", marginBottom: onPartner ? "10px" : 0 }}>
            {tabBtn(!onPartner, "🏢 TFE（自社）", goTfe)}
            {tabBtn(onPartner, "🤝 パートナー", goPartner)}
          </div>
          {onPartner && (
            partnerOrgs.length === 0 ? (
              <div style={{ fontSize:"12px", color:C.gray3 }}>パートナー会社が登録されていません（設定の「組織管理」から追加できます）</div>
            ) : (
              <select value={form.organizationId} onChange={e => setForm(p => ({ ...p, organizationId: e.target.value, role:"partner" }))}
                style={{ ...inputStyle, cursor:"pointer", marginBottom:0 }}>
                {partnerOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )
          )}
        </div>
      );
  };

  // 検索：名前・メール・会社名・課のどれかに含まれるユーザー（社員・パートナー両方）
  const searchQ = canSearch ? userSearch.trim().toLowerCase() : "";
  const searchResults = searchQ
    ? users.filter(u => {
        const orgName = orgs.find(o => o.id === u.organization_id)?.name || "";
        const depts = Array.isArray(u.departments) ? u.departments.join(" ") : "";
        return [u.name, u.email, orgName, depts].some(v => String(v || "").toLowerCase().includes(searchQ));
      })
    : [];

  // ユーザー行レンダリング（共通）
  const renderUserRow = (u, i, arr) => {
    const orgName   = orgs.find(o => o.id === u.organization_id)?.name || "-";
    const roleColor = ROLE_COLORS[u.role] || C.gray3;
    const roleLabel = ROLE_LABELS[u.role] || u.role;
    const depts     = Array.isArray(u.departments) ? u.departments : [];
    return (
      <div key={u.id} style={{
        padding:"14px 16px",
        borderBottom: i < arr.length-1 ? `1px solid ${C.gray2}` : "none",
        display:"flex", alignItems:"center", gap:"12px",
      }}>
        <div style={{
          width:"38px", height:"38px", borderRadius:"50%", flexShrink:0,
          background: roleColor + "22",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:"15px", fontWeight:"700", color: roleColor,
        }}>{u.name?.charAt(0) || "?"}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:"13px", fontWeight:"600", color: C.text, marginBottom:"2px" }}>{u.name}</div>
          <div style={{ fontSize:"11px", color: C.textSub, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</div>
          <div style={{ display:"flex", gap:"4px", flexWrap:"wrap", marginTop:"4px" }}>
            {depts.map(d => (
              <span key={d} style={{
                fontSize:"9px", padding:"2px 6px", borderRadius:"10px",
                background: C.navy + "15", color: C.navy, fontWeight:"600",
              }}>{d}</span>
            ))}
            {depts.length === 0 && <span style={{ fontSize:"10px", color: C.gray3 }}>{orgName}</span>}
          </div>
        </div>
        <div style={{
          fontSize:"10px", padding:"3px 8px", borderRadius:"20px",
          background: roleColor + "18", color: roleColor,
          border:`1px solid ${roleColor}33`, fontWeight:"600", flexShrink:0,
        }}>{roleLabel}</div>
        <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
          <button onClick={() => {
            setTargetUser(u); setError("");
            setForm({ name:u.name, email:u.email, password:"", role:u.role, organizationId:u.organization_id, departments:Array.isArray(u.departments)?u.departments:[] });
            setModal("edit");
          }} title="編集" style={{ padding:"6px 8px", background:C.gray1, border:`1.5px solid ${C.gray2}`, borderRadius:"8px", cursor:"pointer", fontSize:"14px", lineHeight:1 }}>✏️</button>
          <button onClick={() => { setTargetUser(u); setNewPassword(""); setError(""); setModal("password"); }} title="パスワード変更"
            style={{ padding:"6px 8px", background:C.gray1, border:`1.5px solid ${C.gray2}`, borderRadius:"8px", cursor:"pointer", fontSize:"14px", lineHeight:1 }}>🔑</button>
          <button onClick={() => { setTargetUser(u); setError(""); setModal("delete"); }} title="削除"
            style={{ padding:"6px 8px", background:"#FFF0F0", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"14px", lineHeight:1 }}>🗑️</button>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ textAlign:"center", padding:"40px", color: C.gray3 }}>読み込み中...</div>;

  return (
    <div>
      {/* 成功メッセージ */}
      {success && (
        <div style={{
          background:"#E6F9F2", border:`1px solid ${C.green}`, borderRadius:"10px",
          padding:"10px 14px", fontSize:"12px", color: C.green,
          marginBottom:"12px", display:"flex", alignItems:"center", gap:"6px",
        }}>✅ {success}</div>
      )}

      {/* ヘッダー（CSV操作 + ユーザー追加） */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"14px", flexWrap:"wrap", gap:"8px" }}>
        <div style={{ fontSize:"13px", color: C.textSub }}>{users.length}名登録</div>
        <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
          <button onClick={handleCsvExport} title="CSVダウンロード" style={{
            padding:"7px 13px", background: C.green, color: C.white,
            border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"700",
            boxShadow:"0 2px 8px rgba(29,158,117,0.25)",
          }}>📥 CSV出力</button>
          <label title="CSVアップロード" style={{
            padding:"7px 13px", background: C.amber, color: C.white,
            border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"700",
            boxShadow:"0 2px 8px rgba(186,117,23,0.25)", display:"inline-block",
          }}>
            📤 CSV取込
            <input type="file" accept=".csv" onChange={handleCsvFile} style={{ display:"none" }} />
          </label>
          {csvError && <span style={{ fontSize:"11px", color: C.red }}>{csvError}</span>}
          <button onClick={() => {
            setModal("add"); setError("");
            setForm({ ...emptyForm, organizationId: tfeOrg?.id || "" });
          }} style={{
            padding:"8px 16px", background: C.navy, color: C.white,
            border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"12px", fontWeight:"700",
            boxShadow:"0 2px 8px rgba(26,42,74,0.25)",
          }}>＋ ユーザー追加</button>
        </div>
      </div>

      {/* 検索（安管室のみ） */}
      {canSearch && <div style={{ position:"relative", marginBottom:"12px" }}>
        <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
          placeholder="🔍 名前で検索（社員・パートナー共通）"
          style={{ ...inputStyle, marginBottom:0, paddingRight:"36px" }} />
        {userSearch && (
          <button onClick={() => setUserSearch("")} aria-label="検索をクリア"
            style={{ position:"absolute", right:"8px", top:"50%", transform:"translateY(-50%)", border:"none", background:C.gray2, color:C.textSub,
              width:"22px", height:"22px", borderRadius:"50%", cursor:"pointer", fontSize:"12px", lineHeight:1 }}>×</button>
        )}
      </div>}

      {searchQ && (
        <div style={{ background:C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:"14px" }}>
          <div style={{ padding:"10px 16px", fontSize:"11px", fontWeight:"700", color:C.textSub, borderBottom:`1px solid ${C.gray2}` }}>
            検索結果 {searchResults.length}名
          </div>
          {searchResults.length === 0
            ? <div style={{ textAlign:"center", padding:"28px", color:C.gray3, fontSize:"13px" }}>該当するユーザーがいません</div>
            : searchResults.map((u, i) => renderUserRow(u, i, searchResults))}
        </div>
      )}

      {/* TFE / パートナー タブ */}
      {!searchQ && (<>
      <div style={{ display:"flex", gap:"0", marginBottom:"16px", background:C.gray1, borderRadius:"12px", padding:"4px" }}>
        {[
          { key:"tfe", label:`🏢 TFE社員（${tfeUsers.length}名）` },
          { key:"partner", label:`🤝 パートナー（${partnerUsers.length}名）` },
        ].map(t => (
          <button key={t.key} onClick={() => setUserTab(t.key)} style={{
            flex:1, padding:"9px 12px", fontSize:"12px", fontWeight:"700",
            border:"none", borderRadius:"10px", cursor:"pointer", transition:"all .15s",
            background: userTab === t.key ? C.white : "transparent",
            color:      userTab === t.key ? C.navy  : C.textSub,
            boxShadow:  userTab === t.key ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ===== TFE社員タブ ===== */}
      {userTab === "tfe" && (
        <div>
          {/* 課フィルタ */}
          <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"12px" }}>
            {["all", ...DEPARTMENTS].map(d => (
              <button key={d} onClick={() => setFilterDept(d)} style={{
                padding:"5px 12px", borderRadius:"20px", fontSize:"11px", fontWeight:"600",
                cursor:"pointer", border:"none", transition:"all .15s",
                background: filterDept === d ? C.navy : C.white,
                color:      filterDept === d ? C.white : C.textSub,
                boxShadow:  filterDept === d ? "0 2px 6px rgba(26,42,74,0.2)" : "none",
              }}>{d === "all" ? "すべて" : d}</button>
            ))}
          </div>
          <div style={{ background:C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            {filteredTfeUsers.length === 0 ? (
              <div style={{ textAlign:"center", padding:"32px", color:C.gray3, fontSize:"13px" }}>該当するユーザーがいません</div>
            ) : filteredTfeUsers.map((u, i) => renderUserRow(u, i, filteredTfeUsers))}
          </div>
        </div>
      )}

      {/* ===== パートナータブ ===== */}
      {userTab === "partner" && (
        <div>
          {partnerOrgs.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px", color:C.gray3, fontSize:"13px" }}>
              パートナー会社が登録されていません
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
              {/* 会社一覧がなければ全パートナーを表示、あれば会社グループ */}
              {partnerGroups.length === 0 && partnerUsers.length === 0 ? (
                <div style={{ textAlign:"center", padding:"40px", color:C.gray3, fontSize:"13px" }}>パートナーユーザーがいません</div>
              ) : partnerOrgs.map(org => {
                const members = partnerUsers.filter(u => u.organization_id === org.id);
                const isOpen  = partnerOrgId === org.id;
                return (
                  <div key={org.id} style={{ background:C.white, borderRadius:"16px", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                    {/* 会社ヘッダー（クリックで展開） */}
                    <div onClick={() => setPartnerOrgId(isOpen ? null : org.id)} style={{
                      padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between",
                      cursor:"pointer", borderBottom: isOpen && members.length > 0 ? `1px solid ${C.gray2}` : "none",
                      background: isOpen ? C.navy + "08" : C.white,
                    }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                        <span style={{ fontSize:"20px" }}>🏭</span>
                        <div>
                          <div style={{ fontSize:"13px", fontWeight:"700", color:C.text }}>{org.name}</div>
                          <div style={{ fontSize:"11px", color:C.textSub }}>{members.length}名</div>
                        </div>
                      </div>
                      <span style={{ fontSize:"16px", color:C.textSub, transform: isOpen ? "rotate(180deg)" : "none", transition:"transform .2s" }}>▼</span>
                    </div>
                    {/* メンバー一覧（展開時） */}
                    {isOpen && (
                      <div>
                        {members.length === 0 ? (
                          <div style={{ textAlign:"center", padding:"20px", color:C.gray3, fontSize:"12px" }}>メンバーがいません</div>
                        ) : members.map((u, i) => renderUserRow(u, i, members))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </>)}

      {/* ===== ユーザー登録モーダル ===== */}
      {modal === "add" && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"400px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)", maxHeight:"90dvh", overflowY:"auto",
          }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color: C.text, marginBottom:"20px" }}>👤 ユーザー登録</div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>氏名</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="山田 太郎" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>メールアドレス</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="yamada@example.com" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>初期パスワード</label>
              <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="••••••••" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            {renderAffiliation()}

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>ロール</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value, departments:[] }))}
                style={{ ...inputStyle, cursor:"pointer" }}>
                {/* パートナー組織選択時はpartnerロールのみ */}
                {form.organizationId !== tfeOrg?.id ? (
                  <option value="partner">パートナー</option>
                ) : (
                  <>
                    <option value="employee">社員（一般）</option>
                    <option value="employee_manager">社員（管理職）</option>
                    <option value="employee_dispatch">社員（派遣・業務委託）</option>
                    <option value="manager">管理職（安パト権限あり）</option>
                    <option value="safety_admin">安全管理室</option>
                  </>
                )}
              </select>
            </div>

            {/* TFE選択時のみ課を表示 */}
            {form.organizationId === tfeOrg?.id && (
              <div style={{ marginBottom:"20px" }}>
                <label style={labelStyle}>所属課（複数選択可）</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                  {DEPARTMENTS.map(d => {
                    const checked = form.departments.includes(d);
                    return (
                      <div key={d} onClick={() => toggleDept(d)} style={{
                        padding:"6px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                        cursor:"pointer", border:`1.5px solid ${checked ? C.navy : C.gray2}`,
                        background: checked ? C.navy : C.white,
                        color: checked ? C.white : C.textSub,
                        transition:"all .15s",
                      }}>{d}</div>
                    );
                  })}
                </div>
              </div>
            )}

            {error && (
              <div style={{
                background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px",
                padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px",
              }}>⚠️ {error}</div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleAdd} disabled={saving} style={primaryBtnStyle(saving)}>
                {saving ? "登録中..." : "登録する"}
              </button>
              <button onClick={() => setModal(null)} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 編集モーダル ===== */}
      {modal === "edit" && targetUser && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"400px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)", maxHeight:"90dvh", overflowY:"auto",
          }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color: C.text, marginBottom:"6px" }}>✏️ ユーザー編集</div>
            <div style={{ fontSize:"12px", color: C.textSub, marginBottom:"20px" }}>{targetUser.email}</div>

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>氏名</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            {renderAffiliation()}

            <div style={{ marginBottom:"14px" }}>
              <label style={labelStyle}>ロール</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                style={{ ...inputStyle, cursor:"pointer" }}>
                {form.organizationId !== tfeOrg?.id ? (
                  <option value="partner">パートナー</option>
                ) : (
                  <>
                    <option value="employee">社員（一般）</option>
                    <option value="employee_manager">社員（管理職）</option>
                    <option value="employee_dispatch">社員（派遣・業務委託）</option>
                    <option value="manager">管理職（安パト権限あり）</option>
                    <option value="safety_admin">安全管理室</option>
                  </>
                )}
              </select>
            </div>

            {form.organizationId === tfeOrg?.id && (
              <div style={{ marginBottom:"20px" }}>
                <label style={labelStyle}>所属課（複数選択可）</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                  {DEPARTMENTS.map(d => {
                    const checked = form.departments.includes(d);
                    return (
                      <div key={d} onClick={() => toggleDept(d)} style={{
                        padding:"6px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:"600",
                        cursor:"pointer", border:`1.5px solid ${checked ? C.navy : C.gray2}`,
                        background: checked ? C.navy : C.white,
                        color: checked ? C.white : C.textSub,
                        transition:"all .15s",
                      }}>{d}</div>
                    );
                  })}
                </div>
              </div>
            )}

            {error && (
              <div style={{
                background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px",
                padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px",
              }}>⚠️ {error}</div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleEdit} disabled={saving} style={primaryBtnStyle(saving)}>
                {saving ? "更新中..." : "更新する"}
              </button>
              <button onClick={() => setModal(null)} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== パスワード変更モーダル ===== */}
      {modal === "password" && targetUser && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"360px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color: C.text, marginBottom:"6px" }}>🔑 パスワード変更</div>
            <div style={{ fontSize:"12px", color: C.textSub, marginBottom:"20px" }}>{targetUser.name}</div>

            <div style={{ marginBottom:"20px" }}>
              <label style={labelStyle}>新しいパスワード</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="新しいパスワード" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.gray2} />
            </div>

            {error && (
              <div style={{
                background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px",
                padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px",
              }}>⚠️ {error}</div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handlePassword} disabled={saving} style={primaryBtnStyle(saving)}>
                {saving ? "変更中..." : "変更する"}
              </button>
              <button onClick={() => setModal(null)} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 削除確認モーダル ===== */}
      {modal === "delete" && targetUser && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"340px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color: C.red, marginBottom:"8px" }}>🗑️ ユーザー削除</div>
            <div style={{ fontSize:"13px", color: C.text, marginBottom:"6px" }}>
              <strong>{targetUser.name}</strong> を削除しますか？
            </div>
            <div style={{ fontSize:"12px", color: C.textSub, marginBottom:"24px" }}>
              この操作は取り消せません。
            </div>

            {error && (
              <div style={{
                background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px",
                padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px",
              }}>⚠️ {error}</div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleDelete} disabled={saving} style={{
                ...primaryBtnStyle(saving),
                background: saving ? C.gray3 : C.red,
                boxShadow: saving ? "none" : "0 3px 10px rgba(226,75,74,0.3)",
              }}>
                {saving ? "削除中..." : "削除する"}
              </button>
              <button onClick={() => setModal(null)} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
      {/* ===== CSVインポート確認モーダル ===== */}
      {csvModal && (
        <div style={{
          position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:"20px",
        }} onClick={e => { if (e.target === e.currentTarget) { setCsvModal(false); setCsvPreview([]); } }}>
          <div style={{
            background: C.white, borderRadius:"20px", padding:"24px",
            width:"100%", maxWidth:"560px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.3)", maxHeight:"85dvh", overflowY:"auto",
          }}>
            <div style={{ fontSize:"16px", fontWeight:"700", color: C.text, marginBottom:"4px" }}>📤 CSVインポート確認</div>
            <div style={{ fontSize:"12px", color: C.textSub, marginBottom:"16px" }}>
              以下 <strong>{csvPreview.length}件</strong> を更新します（名前・課・ロールを反映）
            </div>

            {/* プレビュー表 */}
            <div style={{ overflowX:"auto", marginBottom:"16px" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"11px" }}>
                <thead>
                  <tr style={{ background: C.gray2, color: C.textSub }}>
                    {["氏名","メール","ロール","課・所属","有効"].map(h => (
                      <th key={h} style={{ padding:"6px 10px", textAlign:"left", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.slice(0, 20).map((u, i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.gray2}` }}>
                      <td style={{ padding:"5px 10px", fontWeight:"600", whiteSpace:"nowrap" }}>{u.name}</td>
                      <td style={{ padding:"5px 10px", color: C.textSub }}>{u.email}</td>
                      <td style={{ padding:"5px 10px" }}>{ROLE_LABELS[u.role] || u.role}</td>
                      <td style={{ padding:"5px 10px" }}>{u.departments.join("、")}</td>
                      <td style={{ padding:"5px 10px", color: u.is_active ? C.green : C.red }}>
                        {u.is_active ? "有効" : "無効"}
                      </td>
                    </tr>
                  ))}
                  {csvPreview.length > 20 && (
                    <tr><td colSpan={5} style={{ padding:"8px 10px", color: C.gray3, fontSize:"11px", textAlign:"center" }}>
                      …他 {csvPreview.length - 20} 件
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {csvError && (
              <div style={{
                background:"#FFF0F0", border:`1px solid #FFCCCC`, borderRadius:"10px",
                padding:"10px 14px", fontSize:"12px", color: C.red, marginBottom:"14px",
              }}>⚠️ {csvError}</div>
            )}

            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleCsvImport} disabled={csvImporting} style={primaryBtnStyle(csvImporting)}>
                {csvImporting ? "インポート中..." : "この内容でインポート"}
              </button>
              <button onClick={() => { setCsvModal(false); setCsvPreview([]); setCsvError(""); }} style={cancelBtnStyle}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================
// 権限なし
// ================================
function PermDenied() {
  return (
    <div style={{ textAlign:"center", padding:"48px 24px", color: C.gray3 }}>
      <div style={{ fontSize:"48px", marginBottom:"12px" }}>🔒</div>
      <div style={{ fontSize:"14px", fontWeight:"600", color: C.text, marginBottom:"6px" }}>閲覧権限がありません</div>
      <div style={{ fontSize:"12px" }}>この画面を表示する権限がありません</div>
    </div>
  );
}

// ================================
// 共通パーツ
// ================================
function Row({ label, children }) {
  return (
    <div style={{ marginBottom:"14px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const labelStyle = {
  display:"block", fontSize:"11px", color: C.textSub, marginBottom:"6px",
  fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.05em",
};

const inputStyle = {
  width:"100%", padding:"11px 14px", fontSize:"13px",
  border:`1.5px solid ${C.gray2}`, borderRadius:"10px",
  boxSizing:"border-box", outline:"none", background: C.gray1,
  transition:"border-color .15s", color: C.text,
};

const primaryBtnStyle = (disabled) => ({
  width:"100%", padding:"11px 16px",
  background: disabled ? C.gray3 : C.navy,
  color: C.white, border:"none", borderRadius:"10px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize:"13px", fontWeight:"700",
  transition:"background .2s",
  boxShadow: disabled ? "none" : "0 3px 10px rgba(26,42,74,0.25)",
});

const cancelBtnStyle = {
  padding:"11px 16px", background: C.gray1, color: C.textSub,
  border:`1.5px solid ${C.gray2}`, borderRadius:"10px",
  cursor:"pointer", fontSize:"13px", fontWeight:"600",
};

const addBtnStyle = {
  padding:"6px 14px", background: C.gray1, border:`1.5px solid ${C.gray2}`,
  borderRadius:"8px", cursor:"pointer", fontSize:"12px", color: C.textSub, fontWeight:"600",
};

const deleteBtnStyle = {
  padding:"6px 10px", background:"#FFF0F0", border:"none",
  borderRadius:"8px", cursor:"pointer", color: C.red, fontSize:"16px", lineHeight:1,
};

// Service Worker登録（無効化）
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
}

export default App;